import Phaser from 'phaser';
import { GAME_CONFIG, PLAYER_CONFIG, PHYSICS_CONFIG, PROJECTILE_CONFIG, BALL_SIZES, CAMERA_CONFIG, DEBUG_CONFIG, LIVES_CONFIG, ENERGY_CONFIG, debugState, designModeState, characterOnlyModeState, screenShakeState, SCENE_KEYS, ASSET_KEYS, EFFECTS_CONFIG, difficultyState, graphicsState } from '@/config/constants';
import { AIPlayer } from '@/utils/AIPlayer';
import { DemoController } from '@/utils/DemoController';
import { AchievementManager } from '@/utils/AchievementManager';
import { BackgroundManager } from '@/utils/BackgroundManager';
import { StageLoader } from '@/utils/StageLoader';
import { StageConfig } from '@/types/StageConfig';
import { getBubbleConfig, getExplosionColors, getBubbleTint, BubbleTypeName, BUBBLE_TEXTURE_RADIUS, BUBBLE_GROW_MULTIPLIER, BUBBLE_POP_MULTIPLIER, getBubbleSound, getBubbleSizeFromRadius } from '@/types/BubbleTypes';
import { getPowerupConfig, PowerupTypeName } from '@/types/PowerupTypes';
import { getObstacleConfig, ObstacleTypeName } from '@/types/ObstacleTypes';
import { AudioManager } from '@/utils/AudioManager';
import { ParticlePools } from '@/utils/ParticlePools';
import { AudioChoreographer } from '@/utils/AudioChoreographer';
import { ImpactSystem } from '@/utils/ImpactSystem';
import { AmbientWorldSetup } from '@/utils/AmbientWorldSetup';
import { PowerupTracker } from '@/utils/PowerupTracker';
import { getGlobalLeaderboard } from '@/utils/SupabaseClient';
import { ANIMATION_KEYS, FRAME_KEYS } from '@/utils/AnimationManager';
import { PostFXManager } from '@/utils/PostFXManager';
import { ScorePopup } from '@/utils/ScorePopup';
import { AmbientBubbleColumns } from '@/utils/AmbientBubbleColumns';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { EducationIntroModal } from '@/ui/EducationIntroModal';
import { MechanicsTutorialModal } from '@/ui/MechanicsTutorialModal';
import { GamepadNav } from '@/utils/GamepadNav';
import { KioskWatchdog } from '@/utils/KioskWatchdog';
import { StormSkyLayer } from '@/utils/StormSkyLayer';

/**
 * Game Scene - Main Gameplay
 * Handles the core game loop, player, projectiles, and balls
 */
export class GameScene extends Phaser.Scene {
  // Game entities (will be initialized in create)
  private player?: Phaser.GameObjects.Sprite;
  private projectile?: Phaser.GameObjects.Rectangle; // Legacy - kept for compatibility
  private projectileGlow?: Phaser.GameObjects.Rectangle; // Legacy - kept for compatibility
  private projectileSprites: Phaser.GameObjects.Image[] = []; // Array of stacked energy projectile sprites
  private balls: Phaser.GameObjects.Arc[] = [];
  private lastBallHitX: number = 0;
  private lastBallHitY: number = 0;
  private ballBehaviorCounter: number = 0; // round-robin index for per-ball movement personalities
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private spaceKey?: Phaser.Input.Keyboard.Key;
  private gamepad?: Phaser.Input.Gamepad.Gamepad;
  private prevGamepadShoot: boolean = false;
  private prevGamepadPause: boolean = false;
  private particleEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private bubbleTrailEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private waterSplashEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  // Glow effect tweens
  private glowFadeIn?: Phaser.Tweens.Tween;
  private glowFadeOut?: Phaser.Tweens.Tween;
  private tridentGlow?: Phaser.GameObjects.Arc; // Power point glow at trident
  // The player's "shooting aura" glow filter — stored at creation time so
  // we can mutate outerStrength/color over the player's lifetime. v4 no
  // longer exposes a `.preFX.list[0]` style accessor; we keep the ref here.
  private playerGlow?: Phaser.Filters.Glow;

  // Game state
  private isProjectileActive = false;
  private projectileAtMaxHeight = false;
  private projectileTargetHeight = 0;
  private projectileStartX = 0; // Store X position where projectile was fired
  private projectileSafetyTimer?: Phaser.Time.TimerEvent; // Safety timeout to prevent infinite projectile
  private currentStage = 1;
  private startingScore = 0; // Score carried over from previous stages
  private isGameOver = false;
  private isPaused = false;
  private pauseText?: Phaser.GameObjects.Text;
  private isCountdownActive = false; // Track countdown state
  // Last bubble count we pushed to the HUD; used by update() to keep the
  // upper-right "BUBBLES" pill in lockstep with this.balls.length across
  // every spawn path (initial stage spawn, splits, powerup pops, endless
  // wave respawns) without having to remember to call updateBallCount()
  // at each one.
  private hudBubbleCount = -1;
  private playerState: 'idle' | 'walking' | 'shooting' = 'idle';
  private ballsBeingSplit = new Set<Phaser.GameObjects.Arc>(); // Track balls in split animation
  private winCheckTimer?: Phaser.Time.TimerEvent; // Track pending win check
  private createRunToken = 0;
  private isSceneReady = false;

  // ── Educational layer (stage.educational) ─────────────────────────────────
  // bagsCleared = total pops on bubbles that carried an item this stage.
  // Children inherit the `hasItem` flag from their parent on split, so a
  // LARGE bag-bubble that splits into 2 MEDIUMs → 4 SMALLs yields up to 7
  // increments (1 parent + 2 + 4) through the full chain of pops.
  private bagsCleared: number = 0;

  // ── Storm-sky overlay (World 3 only) ──────────────────────────────────
  // World 3 ship stages (5+6) get a 3-layer dark-cloud overlay that fades as
  // the player pops bubbles. Progress = pops / spawns (monotonic — children
  // inflate the denominator; pops grow the numerator until cleared = 1.0).
  private stormSky?: StormSkyLayer;
  private bubblePopsThisStage = 0;
  private bubbleSpawnsThisStage = 0;
  // Transient hint set by handleBallHit right before spawning a split-child
  // so maybeAttachEducationalItem can honor parent inheritance instead of
  // rolling a fresh random frequency check.
  private pendingForceHasItem?: boolean;

  // Powerup system
  private droppedPowerups: Phaser.GameObjects.Image[] = []; // Active powerup items on screen
  private isBallsFrozen = false; // Track if balls are frozen
  private frozenBallVelocities = new Map<Phaser.GameObjects.Arc, { vx: number; vy: number }>(); // Store frozen velocities
  private activePowerupEffect?: Phaser.Time.TimerEvent; // Track active powerup effect timer

  // Shield system
  private hasShield = false;
  private shieldGraphic?: Phaser.GameObjects.Arc;

  // Multi-shot system
  private isMultiShotActive = false;
  private multiShotTimer?: Phaser.Time.TimerEvent;

  // Obstacle system
  private obstacles: Phaser.GameObjects.GameObject[] = []; // Static obstacles (platforms, walls, etc.)

  // Stage system
  private stageConfig?: StageConfig;
  private backgroundManager?: BackgroundManager;

  // Lives system
  private lives: number = LIVES_CONFIG.MAX_LIVES;
  private isInvincible: boolean = false;
  // invincibilityTimer managed inline in startInvincibility()

  // Demo mode
  private isDemoMode: boolean = false;
  private aiPlayer?: AIPlayer;
  private demoLabel?: Phaser.GameObjects.Text;
  private demoPrompt?: Phaser.GameObjects.Text;
  // demoInputListener stored via closure in setupDemoMode()
  private demoLeaderboardElements: Phaser.GameObjects.GameObject[] = [];

  // Touch controls
  private touchLeft: boolean = false;
  private touchRight: boolean = false;
  private touchShoot: boolean = false;
  private isTouchDevice: boolean = false;

  // Player ground shadow
  private playerShadow?: Phaser.GameObjects.Ellipse;

  // === AAA: Player momentum system ===
  private playerVelocityX: number = 0; // Current horizontal velocity

  // === AAA: Dash system ===
  private isDashing: boolean = false;
  private dashDirection: number = 0; // -1 left, 1 right
  private dashTimer: number = 0;
  private dashCooldownTimer: number = 0;
  private lastLeftTapTime: number = 0;
  private lastRightTapTime: number = 0;
  private prevInputLeft: boolean = false;
  private prevInputRight: boolean = false;

  // === AAA: Charged shot system ===
  private isCharging: boolean = false;
  private chargeStartTime: number = 0;
  private chargeIndicator?: Phaser.GameObjects.Arc;
  private chargeGlow?: Phaser.GameObjects.Arc;

  // === AAA: Water caustics overlay ===
  private causticsGraphics?: Phaser.GameObjects.Graphics;
  private causticsTime: number = 0;

  // === AAA: Low health effects ===
  private lowHealthVignette?: Phaser.GameObjects.Graphics;
  private lowHealthPulseTween?: Phaser.Tweens.Tween;

  // === AAA: Screen edge urgency glow ===
  private urgencyOverlay?: Phaser.GameObjects.Graphics;

  // === AAA: Ambient life handled by AmbientLife + AmbientWorldSetup ===

  // === AAA: Character dialog quips ===
  private lastQuipTime: number = 0;
  private quipCooldown: number = 8000; // Minimum ms between quips

  // === AAA: Combo tracking for finisher ===
  // (combo count tracked via UIScene streakCount)

  // === AAA: Pollution pool hazards ===
  private pollutionPools: Phaser.GameObjects.Image[] = [];

  // === AAA: Endless mode ===
  private isEndlessMode: boolean = false;
  private endlessWave: number = 0;
  private endlessBallsPerWave: number = 2;

  // Debug state
  private debugGraphics?: Phaser.GameObjects.Graphics;
  private debugTexts: Phaser.GameObjects.Text[] = [];
  private readonly onTimerUrgency = (timeLeft: number): void => {
    if (this.urgencyOverlay) {
      // Pulse the urgency overlay
      const intensity = Math.max(0, (6 - timeLeft) / 5); // 0 at 5s, 1 at 0s
      this.tweens.add({
        targets: this.urgencyOverlay,
        alpha: { from: 0, to: intensity * EFFECTS_CONFIG.URGENCY_GLOW_ALPHA },
        duration: 300,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    }
  };

  constructor() {
    super({ key: SCENE_KEYS.GAME });
  }

  // === AUTO-PLAY: AI plays through all stages for testing ===
  public isAutoPlay: boolean = false;

  init(data?: { stage?: number; score?: number; demoMode?: boolean; endlessMode?: boolean; autoPlay?: boolean }): void {
    // Set stage number from data, default to 1
    this.currentStage = data?.stage || 1;
    // Store starting score to pass to UI scene
    this.startingScore = data?.score || 0;
    // Demo mode
    this.isDemoMode = data?.demoMode || false;
    // Auto-play mode (AI plays through all stages)
    this.isAutoPlay = data?.autoPlay || false;
    if (this.isAutoPlay) this.isDemoMode = true; // Use AI player
    // AAA: Endless mode
    this.isEndlessMode = data?.endlessMode || false;
    this.endlessWave = 0;
    this.endlessBallsPerWave = 2;
    // Reset cached HUD count so the next stage forces a fresh sync on
    // the first update() tick — even if it happens to spawn the same
    // ball count as the previous stage's final state.
    this.hudBubbleCount = -1;
    // Reset lives (respect difficulty)
    const diffConfig = difficultyState.getConfig();
    this.lives = diffConfig.lives;
    this.isInvincible = false;
    // Reset momentum
    this.playerVelocityX = 0;
    this.isDashing = false;
    this.dashCooldownTimer = 0;
    console.log(`🎮 GameScene initialized for stage ${this.currentStage}, starting score: ${this.startingScore}${this.isDemoMode ? ' [DEMO]' : ''}${this.isEndlessMode ? ' [ENDLESS]' : ''} difficulty: ${difficultyState.getDifficulty()}`);
  }

  create(): void {
    // Note: width and height available via this.cameras.main if needed

    console.log('🎮 Game scene created');
    const runToken = ++this.createRunToken;
    this.isSceneReady = false;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.handleShutdown, this);

    // ── DEFENSIVE RESET ON EVERY CREATE ──────────────────────────────────
    // Previous scenes can leave camera/time state lingering (e.g. stuck
    // fade, residual zoom, non-1.0 timeScale from last-ball bullet-time),
    // which manifests as a "black screen" on the next autoplay stage.
    const cam = this.cameras.main;
    cam.resetFX?.();                  // clear any residual fade/flash
    cam.setAlpha(1);                  // ensure visible
    cam.setZoom(1);                   // reset zoom
    cam.setScroll(0, 0);              // reset scroll
    cam.fadeIn(200, 0, 0, 0);         // fresh fade-in so we never stay black
    this.time.timeScale = 1;          // reset any bullet-time
    // v4 cameras carry their FilterList across scene restart. setupCamera()
    // and PostFXManager.attach() add filters unconditionally, so without an
    // explicit clear they accumulate every autoplay-driven restart and the
    // stacked filter passes blow out the render to a near-black wash.
    cam.filters?.internal.clear();
    cam.filters?.external.clear();

    // ── TRIPLE-AAA CINEMATIC POST-FX STACK ──────────────────────────────
    // Adds bloom, vignette, chromatic aberration, film grain, color grade in
    // a single pass. Pipeline is gated by graphicsState.postFX.
    PostFXManager.attach(this, 'gameplay');

    // Rising bubble columns — underwater only (World 1 / stages 1+2).
    if (this.currentStage <= 2) {
      AmbientBubbleColumns.install(this);
    }

    // Create pixel texture for particles if it doesn't exist
    if (!this.textures.exists('pixel')) {
      const graphics = this.add.graphics();
      graphics.fillStyle(0xFFFFFF, 1);
      graphics.fillCircle(8, 8, 8); // Make it a circle for better particle look
      graphics.generateTexture('pixel', 16, 16);
      graphics.destroy();
      console.log('✅ Created pixel texture for particles');
    }

    // Reset game state
    this.isGameOver = false;
    this.isProjectileActive = false;
    // currentStage is set in init() method, don't reset it here
    this.player = undefined;
    this.playerGlow = undefined;
    this.projectileGlow = undefined;
    this.tridentGlow = undefined;
    this.particleEmitter = undefined;
    this.bubbleTrailEmitter = undefined;
    this.waterSplashEmitter = undefined;
    this.projectileSprites = [];
    this.balls = [];
    this.obstacles = [];
    this.debugTexts = [];
    this.playerState = 'idle';
    this.ballsBeingSplit.clear();
    
    // Reset powerup state
    this.droppedPowerups = [];
    this.isBallsFrozen = false;
    this.frozenBallVelocities.clear();
    if (this.activePowerupEffect) {
      this.activePowerupEffect.remove();
      this.activePowerupEffect = undefined;
    }
    
    // Stop ALL tweens from previous game
    this.tweens.killAll();
    
    // Clear win check timer
    if (this.winCheckTimer) {
      this.winCheckTimer.remove();
    }
    this.winCheckTimer = undefined;

    // Clear any existing projectile
    if (this.projectile) {
      this.projectile.destroy();
      this.projectile = undefined;
    }

    // Initialize background manager
    this.backgroundManager = new BackgroundManager(this);

    // Initialize AudioManager and play stage music
    const audioManager = AudioManager.getInstance();
    audioManager.initialize(this);
    // Pick music by world: stages 1-2 = stage1, stages 3-4 = coastal, stages 5-6 = boat
    const stageMusic = this.currentStage <= 2 ? 'stage1' : this.currentStage <= 4 ? 'coastal' : 'boat';
    audioManager.playMusic(stageMusic);

    // Load stage configuration and initialize scene
    this.loadStageAndInitialize(runToken);

    // Start/Restart UI scene (ensures fresh state on restart)
    const uiScene = this.scene.get(SCENE_KEYS.UI);
    if (uiScene && this.scene.isActive(SCENE_KEYS.UI)) {
      this.scene.stop(SCENE_KEYS.UI);
      this.scene.launch(SCENE_KEYS.UI, { startingScore: this.startingScore, lives: this.lives, demoMode: this.isDemoMode && !this.isAutoPlay });
    } else {
      this.scene.launch(SCENE_KEYS.UI, { startingScore: this.startingScore, lives: this.lives, demoMode: this.isDemoMode && !this.isAutoPlay });
    }

    // Setup demo mode
    if (this.isDemoMode) {
      this.setupDemoMode();
    }

    // Setup touch controls for mobile
    this.setupTouchControls();

    // Setup debug graphics (no checkbox - moved to menu)
    this.setupDebugGraphics();

    // Listen for score display event from UIScene
    this.events.off('show-score-text', this.showFloatingScoreText, this);
    this.events.on('show-score-text', this.showFloatingScoreText, this);

    // AAA: Listen for timer urgency event
    this.events.off('timer-urgency', this.onTimerUrgency);
    this.events.on('timer-urgency', this.onTimerUrgency);

    // Display stage info
    console.log(`🌊 Stage ${this.currentStage}: Aegean Shores`);
  }

  update(_time: number, delta: number): void {
    // Don't update if game is over, paused, or countdown is active
    if (this.isGameOver || this.isPaused || this.isCountdownActive) return;
    if (!this.isSceneReady) return;

    // === BUBBLES-REMAINING HUD SYNC ===========================================
    // Push this.balls.length to the upper-right HUD pill whenever it changes.
    // Single source of truth so initial spawns, splits, powerup-ball pops, and
    // endless-mode wave respawns all keep the counter accurate without each
    // spawn site having to remember to call UIScene.updateBallCount().
    if (this.balls.length !== this.hudBubbleCount) {
      this.hudBubbleCount = this.balls.length;
      const ui = this.scene.get(SCENE_KEYS.UI) as any;
      ui?.updateBallCount?.(this.hudBubbleCount);
    }

    // === LAST BALL BULLET-TIME (event-driven via ImpactSystem baseline) ===
    const impact = ImpactSystem.get(this);
    if (this.balls.length === 1 && this.ballsBeingSplit.size === 0 && !this.isDemoMode) {
      if (impact.getBaseTimeScale() !== 0.55) {
        impact.setBaseTimeScale(0.55);
        this.cameras.main.zoomTo(1.06, 600, 'Sine.easeOut');
      }
    } else if (this.balls.length > 1 && impact.getBaseTimeScale() !== 1) {
      impact.setBaseTimeScale(1);
      this.cameras.main.zoomTo(1, 400, 'Sine.easeInOut');
    }

    // Update player
    this.updatePlayer(delta);

    // Update projectile
    this.updateProjectile(delta);

    // Update balls
    this.updateBalls(delta);

    // Update powerups
    this.updatePowerups(delta);

    // Check collisions
    this.checkCollisions();

    // === AAA: Check pollution pool collisions ===
    this.checkPollutionPoolCollisions();

    // === AAA: Water caustics DISABLED for performance ===
    // this.updateCaustics(delta);

    // === AAA: Update low health effects ===
    this.updateLowHealthEffects();

    // === AAA: Update charge indicator ===
    this.updateChargeIndicator();

    // Update debug visualization
    this.updateDebugInfo();
  }

  /**
   * Load stage configuration and initialize the scene.
   *
   * Splits "load" and "init" so init runs exactly once per create() — the
   * previous try+catch-retry shape ran initializeScene twice when a scene
   * transition raced with the fetch, producing the "stage 2 is empty"
   * symptom (partial init left player+background, second init hit the
   * duplicate-init guard and skipped ball spawn + HUD + modal).
   */
  private async loadStageAndInitialize(runToken: number): Promise<void> {
    try {
      this.stageConfig = await StageLoader.loadStage(this.currentStage);
    } catch (error) {
      // StageLoader.loadStage already returns a default on network error,
      // so reaching here means something unusual (e.g., validation throw).
      // Leave stageConfig unset — initializeScene's spawnTestBalls fallback
      // will handle it.
      console.error('❌ Failed to load stage, falling back to defaults:', error);
      this.stageConfig = undefined;
    }

    if (runToken !== this.createRunToken) {
      console.warn(`⚠️ Ignoring stale stage load for stage ${this.currentStage} (run ${runToken})`);
      return;
    }

    console.log(`📋 Stage ${this.currentStage} loaded: ${this.stageConfig?.stageName ?? 'DEFAULT'}`);
    this.initializeScene();
  }

  /**
   * Initialize the scene (called after stage is loaded).
   *
   * loadStageAndInitialize guarantees this runs exactly once per create()
   * run, so no duplicate-init guard is needed. The stale-load check in
   * loadStageAndInitialize protects against overlapping scene transitions.
   */
  private initializeScene(): void {
    // Wrap each step so a throw in one doesn't skip the rest — critical for
    // the "empty stage 2" bug where a silent exception mid-init left the
    // scene with only Poseidon + background and no balls/ambient/modal.
    const step = (name: string, fn: () => void) => {
      try { fn(); } catch (e) { console.error(`❌ initializeScene step "${name}" failed:`, e); }
    };

    step('setupCamera', () => this.setupCamera());
    step('createParticleEmitter', () => this.createParticleEmitter());
    step('createBackground', () => this.createBackground());
    step('createAtmosphericEffects', () => this.createAtmosphericEffects());
    step('createGround', () => this.createGround());
    step('createPlayer', () => this.createPlayer());
    step('setupInput', () => this.setupInput());

    // ── EDUCATIONAL SETUP (must run BEFORE ball spawn) ───────────────
    // ensureEducationalItemAnimations() registers Phaser anims like
    // 'bag_drift' so that maybeAttachEducationalItem() in spawnBall()
    // can find and play them. The HUD counter is wired in parallel.
    step('stormSkyInit', () => {
      // Tear down any pre-existing overlay (e.g. on restart between stages).
      this.stormSky?.destroy();
      this.stormSky = undefined;
      this.bubblePopsThisStage = 0;
      this.bubbleSpawnsThisStage = 0;
      // Storm sky only exists on World 3 ship stages (stages 5 + 6).
      if (this.currentStage === 5 || this.currentStage === 6) {
        this.stormSky = new StormSkyLayer(this);
        this.stormSky.create();
        this.stormSky.setProgressImmediate(0); // full storm at start
      }
    });

    step('educationalSetup', () => {
      this.bagsCleared = 0;
      const itemTheme = this.stageConfig?.educational?.itemTheme;
      if (itemTheme?.itemLabel) {
        this.ensureEducationalItemAnimations();
        const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
        const install = () => {
          if (uiScene?.setEducationalTheme) uiScene.setEducationalTheme(itemTheme.itemLabel);
        };
        if (uiScene?.sys?.isActive?.()) install();
        else this.time.delayedCall(60, install);
      }
    });

    // Spawn balls from stage configuration or test balls (unless Character Only mode)
    step('spawnBalls', () => {
      if (!characterOnlyModeState.isCharacterOnlyMode()) {
        if (this.stageConfig) {
          this.spawnBallsFromStage();
        } else {
          this.spawnTestBalls();
        }
      } else {
        console.log('🎭 CHARACTER ONLY MODE - No balls spawned');
      }
    });

    // Show design mode indicator if enabled
    step('designModeIndicator', () => {
      if (designModeState.isDesignMode()) {
        const designModeText = this.add.text(
          20,
          GAME_CONFIG.HEIGHT - 60,
          '🎨 DESIGN MODE',
          {
            fontSize: '32px',
            fontFamily: 'Arial',
            color: '#FFD700',
            backgroundColor: 'rgba(0,0,0,0.7)',
            padding: { x: 20, y: 10 },
            stroke: '#000000',
            strokeThickness: 4,
          }
        );
        designModeText.setOrigin(0, 1);
        designModeText.setDepth(1000);
        designModeText.setScrollFactor(0);
        console.log('🎨 Design Mode ACTIVE - No timer, No damage');
      }
    });

    step('characterOnlyIndicator', () => {
      if (characterOnlyModeState.isCharacterOnlyMode()) {
        const characterOnlyModeText = this.add.text(
          20,
          GAME_CONFIG.HEIGHT - 100,
          '🎭 CHARACTER ONLY MODE',
          {
            fontSize: '32px',
            fontFamily: 'Arial',
            color: '#00FF88',
            backgroundColor: 'rgba(0,0,0,0.7)',
            padding: { x: 20, y: 10 },
            stroke: '#000000',
            strokeThickness: 4,
          }
        );
        characterOnlyModeText.setOrigin(0, 1);
        characterOnlyModeText.setDepth(1000);
        characterOnlyModeText.setScrollFactor(0);
        console.log('🎭 Character Only Mode ACTIVE - No balls, Test animations');
      }
    });

    // AAA: Install parallax layers for all stages, but ambient creatures only for endless mode
    const worldNum = this.currentStage <= 2 ? 1 : this.currentStage <= 4 ? 2 : 3;
    step('installParallaxLayers', () => this.installParallaxLayers(worldNum));

    // Install ambient creatures if we're in endless mode OR the stage has an
    // educational ambient creature configured. Educational stages rely on the
    // ambient layer to spawn the themed creature (turtle, etc.).
    step('ambientWorldSetup', () => {
      const hasEducationalAmbient = !!this.stageConfig?.educational?.ambientCreature;
      if (this.isEndlessMode || hasEducationalAmbient) {
        AmbientWorldSetup.install(this, worldNum);
      }
    });

    console.log(`🎮 Scene initialized for stage ${this.currentStage}`);
    this.isSceneReady = true;

    // First-play mechanics tutorial → educational intro → cinematic stage
    // intro + countdown. The mechanics card fires once per new game (the
    // flag is set by NameEntryScene when a fresh name is entered) and
    // chains into the per-stage education modal if the stage has one.
    // The HUD/UI scene is hidden while either modal is up so the cards
    // sit cleanly over the stage background.
    step('educationalIntroOrCountdown', () => {
      const mechanicsPending = !!this.registry.get('mechanicsTutorialPending');
      const intro = this.stageConfig?.educational?.intro;
      // Skip educational/mechanics modals entirely in demo + autoplay flows.
      // They have no dismisser there and used to leave the loop wedged on
      // stage transitions in v4 (autoplay would freeze on stage 2).
      const willShowModal = !this.isDemoMode && (mechanicsPending || !!intro);

      if (!willShowModal) {
        this.showStageIntro();
        return;
      }

      this.isCountdownActive = true; // freeze gameplay while any modal is up

      // Hide the HUD by toggling UIScene's main camera. setVisible on the
      // scene wrapper races with Phaser's scene-boot lifecycle (boot
      // restores default visibility after scene.launch); the camera flag
      // is read every frame and survives any boot ordering. Defer one
      // tick so even a freshly-launched UIScene has a camera to read.
      const hideHud = () => {
        const ui = this.scene.get(SCENE_KEYS.UI);
        if (ui?.cameras?.main) ui.cameras.main.setVisible(false);
      };
      const showHud = () => {
        const ui = this.scene.get(SCENE_KEYS.UI);
        if (ui?.cameras?.main) ui.cameras.main.setVisible(true);
      };
      hideHud();
      this.time.delayedCall(50, hideHud);   // catch the post-boot reset

      const finishIntros = () => {
        showHud();
        this.isCountdownActive = false;
        this.showStageIntro();
      };

      const showEducation = () => {
        if (intro) {
          const modal = new EducationIntroModal(this, intro, finishIntros);
          modal.show();
        } else {
          finishIntros();
        }
      };

      if (mechanicsPending) {
        this.registry.set('mechanicsTutorialPending', false);
        const mechanics = new MechanicsTutorialModal(this, showEducation);
        mechanics.show();
      } else {
        showEducation();
      }
    });
  }

  /**
   * Show world/stage name intro before countdown
   */
  private showStageIntro(): void {
    this.isCountdownActive = true;
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    const worldName = this.stageConfig?.worldName || 'World';
    const stageName = this.stageConfig?.stageName || `Stage ${this.currentStage}`;

    // === AAA: CINEMATIC LETTERBOX BARS ===
    const barHeight = 100;
    const topBar = this.add.rectangle(WIDTH / 2, -barHeight / 2, WIDTH, barHeight, 0x000000, 1);
    topBar.setDepth(10001);
    const bottomBar = this.add.rectangle(WIDTH / 2, HEIGHT + barHeight / 2, WIDTH, barHeight, 0x000000, 1);
    bottomBar.setDepth(10001);

    // Slide bars in
    this.tweens.add({ targets: topBar, y: barHeight / 2, duration: 400, ease: 'Power2' });
    this.tweens.add({ targets: bottomBar, y: HEIGHT - barHeight / 2, duration: 400, ease: 'Power2' });

    // === AAA: CAMERA PAN across background ===
    const cam = this.cameras.main;
    cam.zoomTo(1.15, 800, 'Power2');
    this.tweens.add({
      targets: cam, scrollX: { from: -40, to: 40 },
      duration: 2500, ease: 'Sine.easeInOut',
    });

    // === MISSION eyebrow — small white kicker above the big title. ===
    // The dark abyss stroke keeps the white text legible on the bright shore
    // backgrounds and the darker World 1/3 backgrounds alike.
    const worldText = this.add.text(WIDTH / 2, HEIGHT / 2 - 150, worldName.toUpperCase(), {
      fontFamily: FONTS.display,
      fontSize: '44px',
      color: COLORS_HEX.pearlWhite,
      stroke: COLORS_HEX.abyssBlue,
      strokeThickness: 4,
      letterSpacing: 8,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 8, fill: true },
    });
    worldText.setOrigin(0.5);
    worldText.setDepth(10002);
    worldText.setAlpha(0);
    worldText.setScale(0.3);

    // === STAGE NAME — big yellow Skranji hero title (matches win screens) ===
    const stageText = this.add.text(WIDTH / 2, HEIGHT / 2 - 30, stageName.toUpperCase(), {
      fontFamily: FONTS.fancyRegular,
      fontSize: '116px',
      color: '#ffe000',
      stroke: '#000000',
      strokeThickness: 10,
      shadow: { offsetX: 0, offsetY: 6, color: '#000000', blur: 18, fill: true },
    });
    stageText.setOrigin(0.5);
    stageText.setDepth(10002);
    stageText.setAlpha(0);
    stageText.setScale(0.5);

    // === AAA: OCEAN LORE/FACT (environmental message) ===
    const oceanFacts = [
      'Over 8 million tons of plastic enter our oceans yearly.',
      'Every bag you pop is a turtle saved.',
      'The ocean produces over 50% of the world\'s oxygen.',
      'By 2050, there could be more plastic than fish in the sea.',
      'Marine pollution affects over 800 animal species worldwide.',
      'One person can save 170 marine animals per year by reducing plastic.',
    ];
    // The stage's mission objective takes the lore slot when present; otherwise
    // fall back to the rotating ocean fact.
    const objective = this.stageConfig?.educational?.intro?.objective;
    const fact = objective
      ? objective
      : this.isEndlessMode
        ? 'Survive the endless tides!'
        : oceanFacts[(this.currentStage - 1) % oceanFacts.length];

    const loreText = this.add.text(WIDTH / 2, HEIGHT / 2 + 95, fact, {
      fontFamily: FONTS.display,
      fontSize: objective ? '38px' : '28px',
      color: '#FFFFFF',
      stroke: objective ? '#003366' : '#001122',
      strokeThickness: objective ? 5 : 3,
      wordWrap: { width: 900 },
      align: 'center',
    });
    loreText.setOrigin(0.5);
    loreText.setDepth(10002);
    loreText.setAlpha(0);

    // Animate in - staggered
    this.time.delayedCall(300, () => {
      this.tweens.add({
        targets: worldText,
        alpha: 1, scale: 1,
        duration: 600,
        ease: 'Back.easeOut',
      });
    });

    this.time.delayedCall(600, () => {
      this.tweens.add({
        targets: stageText,
        alpha: 1, scale: 1,
        duration: 500,
        ease: 'Back.easeOut',
      });
    });

    this.time.delayedCall(900, () => {
      this.tweens.add({
        targets: loreText,
        alpha: 0.8,
        duration: 600,
        ease: 'Power2',
      });
    });

    // Hold for 2.5s then cinematic exit
    this.time.delayedCall(3000, () => {
      // Zoom back to normal
      cam.zoomTo(1, 500, 'Power2');
      cam.setScroll(0, 0);

      // Slide letterbox bars out
      this.tweens.add({ targets: topBar, y: -barHeight / 2, duration: 400, ease: 'Power2', onComplete: () => topBar.destroy() });
      this.tweens.add({ targets: bottomBar, y: HEIGHT + barHeight / 2, duration: 400, ease: 'Power2', onComplete: () => bottomBar.destroy() });

      // Fade all text out
      this.tweens.add({
        targets: [worldText, stageText, loreText],
        alpha: 0, scale: 1.2,
        duration: 400,
        ease: 'Power2',
        onComplete: () => {
          worldText.destroy();
          stageText.destroy();
          loreText.destroy();
          this.startCountdown();
        },
      });
    });
  }

  /**
   * Start countdown sequence (3, 2, 1, GO!)
   */
  private startCountdown(): void {
    this.isCountdownActive = true;
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // === AAA COUNTDOWN ===
    // Each number has unique visual treatment
    const countdownConfig = [
      { text: '3', color: '#4DD0E1', stroke: '#003366', startY: -100, startScale: 3, shake: 0, particles: 5 },
      { text: '2', color: '#FF8800', stroke: '#663300', startY: HEIGHT / 2, startScale: 0.2, shake: 0.001, particles: 8 },
      { text: '1', color: '#FF2222', stroke: '#660000', startY: HEIGHT / 2, startScale: 5, shake: 0.002, particles: 12 },
      { text: 'GO!', color: '#FFD700', stroke: '#885500', startY: HEIGHT / 2, startScale: 0.1, shake: 0.004, particles: 30 },
    ];

    let currentIndex = 0;

    // Cinematic zoom during countdown
    this.cameras.main.zoomTo(1.04, 2400, 'Sine.easeInOut');

    const showNumber = () => {
      if (currentIndex >= countdownConfig.length) {
        this.isCountdownActive = false;
        this.cameras.main.zoomTo(1, 300, 'Power2');
        console.log('🎮 Countdown complete - Game start!');
        return;
      }

      const cfg = countdownConfig[currentIndex];
      const isGo = cfg.text === 'GO!';

      const countdownText = this.add.text(WIDTH / 2, cfg.startY, cfg.text, {
        fontFamily: FONTS.fancy,
        fontSize: isGo ? '240px' : '220px',
        color: cfg.color,
        stroke: cfg.stroke,
        strokeThickness: isGo ? 14 : 12,
        shadow: { offsetX: 0, offsetY: 0, color: cfg.color, blur: isGo ? 40 : 25, fill: false },
      });
      countdownText.setOrigin(0.5);
      countdownText.setDepth(10000);
      countdownText.setAlpha(0);
      countdownText.setScale(cfg.startScale);

      // Screen shake per number
      if (cfg.shake > 0 && screenShakeState.isEnabled()) {
        this.cameras.main.shake(80, cfg.shake);
      }

      // Particle burst
      if (this.particleEmitter) {
        const tint = parseInt(cfg.color.replace('#', '0x'));
        this.particleEmitter.setParticleTint(tint);
        this.particleEmitter.emitParticleAt(WIDTH / 2, HEIGHT / 2, cfg.particles);
      }

      // AAA: Countdown SFX (bell toll for numbers, horn blast for GO!)
      const audioManager = AudioManager.getInstance();
      try {
        if (isGo) {
          audioManager.playSFX(this, 'sfx_go', { volume: 0.8 });
        } else {
          audioManager.playSFX(this, 'sfx_countdown', { volume: 0.6, detune: currentIndex * 200 } as any);
        }
      } catch {}

      // ENTRANCE — each number has unique entry
      this.tweens.add({
        targets: countdownText,
        alpha: 1,
        y: HEIGHT / 2,
        scale: isGo ? 1.3 : 1.1,
        duration: isGo ? 300 : 200,
        ease: 'Back.easeOut',
        easeParams: isGo ? [3] : [2],
        onComplete: () => {
          // Brief wobble for numbers, explosive hold for GO!
          if (!isGo) {
            this.tweens.add({
              targets: countdownText,
              angle: { from: -3, to: 3 },
              duration: 100, yoyo: true, repeat: 1,
            });
          }

          // Hold then exit
          this.time.delayedCall(isGo ? 300 : 350, () => {
            this.tweens.add({
              targets: countdownText,
              alpha: 0,
              scale: isGo ? 3 : 1.8,
              duration: isGo ? 400 : 200,
              ease: 'Power2',
              onComplete: () => {
                countdownText.destroy();
                currentIndex++;
                showNumber();
              },
            });

            // GO! gets special effects
            if (isGo) {
              ImpactSystem.get(this).fire(WIDTH / 2, HEIGHT / 2, 'HUGE', 0xFFD700);
              // Confetti burst
              if (this.particleEmitter) {
                const colors = [0xFFD700, 0xFF6600, 0x00CED1, 0xFF2222, 0x32CD32];
                for (const c of colors) {
                  this.particleEmitter.setParticleTint(c);
                  this.particleEmitter.emitParticleAt(
                    WIDTH / 2 + (Math.random() - 0.5) * 300,
                    HEIGHT / 2 + (Math.random() - 0.5) * 200,
                    6
                  );
                }
              }
            }
          });
        },
      });
    };

    // Start the countdown
    showNumber();
  }

  /**
   * Toggle pause state
   */
  private pauseOverlay?: Phaser.GameObjects.Rectangle;
  private pauseElements: Phaser.GameObjects.GameObject[] = [];
  private pauseMusicBaseVolume?: number;
  private pauseNav?: GamepadNav;
  private pauseNavTick?: () => void;

  private togglePause(): void {
    this.isPaused = !this.isPaused;

    if (this.isPaused) {
      this.physics.pause();
      this.tweens.pauseAll();
      this.time.paused = true;

      const { WIDTH, HEIGHT } = GAME_CONFIG;
      const cx = WIDTH / 2;
      const cy = HEIGHT / 2;

      // Dark overlay
      this.pauseOverlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x000000, 0.76);
      this.pauseOverlay.setOrigin(0, 0);
      this.pauseOverlay.setDepth(9990);

      // ── Suspended crystal pause shell ───────────────────────────────
      const panelW = 560;
      const panelH = 560;
      const panelX = cx - panelW / 2;
      const panelY = cy - panelH / 2;
      const modal = this.add.graphics().setDepth(9991);
      modal.fillStyle(COLORS.aqua, 0.09);
      modal.fillRoundedRect(panelX - 14, panelY - 12, panelW + 28, panelH + 28, 28);
      modal.fillStyle(COLORS.midnightDepth, 0.58);
      modal.fillRoundedRect(panelX, panelY + 10, panelW, panelH, 22);
      modal.fillGradientStyle(COLORS.tidewater, COLORS.tidewater, COLORS.abyssBlue, COLORS.abyssBlue, 0.95, 0.95, 0.92, 0.92);
      modal.fillRoundedRect(panelX, panelY, panelW, panelH, 22);
      modal.fillStyle(COLORS.pearlWhite, 0.05);
      modal.fillRoundedRect(panelX + 10, panelY + 10, panelW - 20, 132, 16);
      modal.lineStyle(2, COLORS.aqua, 0.7);
      modal.strokeRoundedRect(panelX, panelY, panelW, panelH, 22);
      modal.lineStyle(1, COLORS.pearlWhite, 0.24);
      modal.strokeRoundedRect(panelX + 5, panelY + 5, panelW - 10, panelH - 10, 18);
      modal.fillStyle(COLORS.aquaLight, 0.32);
      modal.fillRoundedRect(panelX + 64, panelY + 4, panelW - 128, 3, 2);

      const gemRadius = 9;
      const gemInset = 18;
      const gems: Phaser.GameObjects.Graphics[] = [];
      const drawGem = (gx: number, gy: number) => {
        const g = this.add.graphics().setDepth(9993);
        g.fillStyle(0x050912, 1);
        g.fillCircle(gx, gy, gemRadius + 2);
        g.fillStyle(COLORS.sapphireDeep, 1);
        g.fillCircle(gx, gy, gemRadius);
        g.fillStyle(COLORS.sapphire, 1);
        g.fillCircle(gx, gy, gemRadius - 2);
        g.fillStyle(COLORS.sapphireLight, 0.9);
        g.fillCircle(gx - gemRadius * 0.35, gy - gemRadius * 0.35, gemRadius * 0.4);
        g.fillStyle(0xFFFFFF, 0.7);
        g.fillCircle(gx - gemRadius * 0.45, gy - gemRadius * 0.45, gemRadius * 0.18);
        gems.push(g);
      };
      drawGem(panelX + gemInset, panelY + gemInset);
      drawGem(panelX + panelW - gemInset, panelY + gemInset);
      drawGem(panelX + gemInset, panelY + panelH - gemInset);
      drawGem(panelX + panelW - gemInset, panelY + panelH - gemInset);

      // Decorative divider under title
      const divider = this.add.graphics().setDepth(9992);
      divider.lineStyle(2, COLORS.aquaLight, 0.5);
      divider.lineBetween(cx - 180, cy - 210, cx + 180, cy - 210);
      divider.fillStyle(COLORS.pearlWhite, 0.8);
      divider.fillCircle(cx, cy - 210, 4);

      const pauseEyebrow = this.add.text(cx, cy - 286, 'RUN INTERRUPTED', {
        fontFamily: FONTS.display,
        fontSize: '20px',
        color: COLORS_HEX.aquaLight,
        stroke: COLORS_HEX.abyssBlue,
        strokeThickness: 3,
        letterSpacing: 6,
      } as any).setOrigin(0.5).setDepth(9994);

      const pauseCopy = this.add.text(cx, cy - 166, 'The arena is frozen. Resume the dive, restart this stage, or break back to the menu.', {
        fontFamily: FONTS.body,
        fontSize: '24px',
        color: COLORS_HEX.textSecondary,
        stroke: COLORS_HEX.midnightDepth,
        strokeThickness: 1,
        align: 'center',
        wordWrap: { width: 430, useAdvancedWrap: true },
      } as any).setOrigin(0.5).setDepth(9994);

      // PAUSED title — pearl display with colder energy
      this.pauseText = this.add.text(cx, cy - 250, 'PAUSED', {
        fontFamily: FONTS.display,
        fontSize: '88px',
        color: COLORS_HEX.pearlWhite,
        stroke: COLORS_HEX.abyssBlue,
        strokeThickness: 7,
        fontStyle: 'italic',
        letterSpacing: 6,
        shadow: { offsetX: 0, offsetY: 5, color: COLORS_HEX.midnightDepth, blur: 10, fill: true },
      } as any);
      this.pauseText.setOrigin(0.5);
      this.pauseText.setDepth(9994);

      // ── 3 NAV BUTTONS (code-drawn, match main-menu / victory-menu) ──
      // Spacing: 90px between button centers, starting at cy - 70
      const btnW = 420;
      const resumeBtn = createPillButton(this, {
        x: cx, y: cy - 70,
        label: 'RESUME',
        variant: 'primary',
        size: 'medium',
        width: btnW,
        onClick: () => this.togglePause(),
      });
      resumeBtn.setDepth(9995);

      const restartBtn = createPillButton(this, {
        x: cx, y: cy + 30,
        label: 'RESTART STAGE',
        variant: 'secondary',
        size: 'medium',
        width: btnW,
        onClick: () => {
          this.scene.stop(SCENE_KEYS.UI);
          this.scene.restart({ stage: this.currentStage, score: this.startingScore });
        },
      });
      restartBtn.setDepth(9995);

      const menuBtn = createPillButton(this, {
        x: cx, y: cy + 130,
        label: 'MAIN MENU',
        variant: 'secondary',
        size: 'medium',
        width: btnW,
        onClick: () => {
          this.scene.stop(SCENE_KEYS.UI);
          this.scene.start(SCENE_KEYS.MENU);
        },
      });
      menuBtn.setDepth(9995);

      // Entrance — panel scales up, buttons fade in staggered
      modal.setAlpha(0);
      this.pauseText.setAlpha(0).setScale(0.6);
      pauseEyebrow.setAlpha(0);
      pauseCopy.setAlpha(0);
      [resumeBtn, restartBtn, menuBtn].forEach((b) => b.setAlpha(0));
      gems.forEach((g) => g.setAlpha(0));
      this.tweens.add({ targets: modal, alpha: 1, duration: 220, ease: 'Sine.easeOut' });
      this.tweens.add({ targets: gems, alpha: 1, duration: 260, delay: 60, ease: 'Sine.easeOut' });
      this.tweens.add({ targets: pauseEyebrow, alpha: 0.86, duration: 220, delay: 70, ease: 'Sine.easeOut' });
      this.tweens.add({ targets: this.pauseText, alpha: 1, scale: 1, duration: 260, delay: 80, ease: 'Back.easeOut', easeParams: [2] });
      this.tweens.add({ targets: pauseCopy, alpha: 1, duration: 240, delay: 150, ease: 'Sine.easeOut' });
      this.tweens.add({ targets: resumeBtn, alpha: 1, duration: 220, delay: 180 });
      this.tweens.add({ targets: restartBtn, alpha: 1, duration: 220, delay: 280 });
      this.tweens.add({ targets: menuBtn, alpha: 1, duration: 220, delay: 380 });

      // Music duck + curtain SFX on pause modal entry
      const audio = AudioManager.getInstance();
      if (this.cache.audio.exists('sfx_curtain_heavy_down')) {
        audio.playSFX(this, 'sfx_curtain_heavy_down', { volume: 0.45 });
      }
      const music: any = (audio as any).currentMusic;
      if (music && typeof music.volume === 'number') {
        this.pauseMusicBaseVolume = music.volume;
        this.tweens.add({ targets: music, volume: music.volume * 0.3, duration: 250 });
      }

      // Focus halo — nothing pre-selected (pauseFocusIdx = -1). The player
      // must move onto a CTA with the D-pad/stick/arrows/mouse before A/ENTER
      // can activate it, so no frame is visible until the joystick selects.
      const pauseButtons = [resumeBtn, restartBtn, menuBtn];
      let pauseFocusIdx = -1;
      const pauseHalo = this.add.graphics().setDepth(9996);
      const drawPauseHalo = () => {
        if (pauseFocusIdx < 0) { pauseHalo.clear(); return; }
        const b = pauseButtons[pauseFocusIdx];
        drawSelectionHalo(pauseHalo, {
          cx: b.x,
          cy: b.y,
          w: (b as any).pillWidth ?? btnW,
          h: (b as any).pillHeight ?? 72,
          rx: (b as any).pillCapRx ?? 36,
          ry: (b as any).pillCapRy ?? 36,
        }, { gap: 14 });
      };
      pauseButtons.forEach((b, i) => b.on('pointerover', () => {
        pauseFocusIdx = i; drawPauseHalo();
      }));

      const stepPause = (dir: number) => {
        pauseFocusIdx = pauseFocusIdx < 0
          ? 0
          : Phaser.Math.Clamp(pauseFocusIdx + dir, 0, pauseButtons.length - 1);
        drawPauseHalo();
      };

      // Pause-mode gamepad listener, runs only while paused since the main
      // getInputState() is gated off during pause. A and B both press the
      // focused pause button (no B as resume shortcut, so B cannot fire a
      // button that does not have focus). Start still toggles pause.
      this.pauseNav = new GamepadNav(this, {
        onUp:    () => stepPause(-1),
        onDown:  () => stepPause(1),
        onConfirm: () => { if (pauseFocusIdx >= 0) pauseButtons[pauseFocusIdx].emit('pointerdown'); },
        onPause:   () => this.togglePause(), // Start toggles pause
      });
      this.pauseNavTick = () => this.pauseNav?.update();
      this.events.on(Phaser.Scenes.Events.UPDATE, this.pauseNavTick);

      this.pauseElements = [
        this.pauseOverlay, modal, divider, ...gems,
        pauseEyebrow, this.pauseText, pauseCopy,
        resumeBtn, restartBtn, menuBtn,
        pauseHalo,
      ];

      // The pause menu is a blocking dialog: arm the kiosk idle watchdog so an
      // abandoned pause resets after 40s. Balanced on resume and on shutdown.
      KioskWatchdog.instance.pushModal();
      this.events.emit('game-paused');
      console.log('⏸️ Game PAUSED');
    } else {
      this.physics.resume();
      this.tweens.resumeAll();
      this.time.paused = false;

      // Curtain-up SFX + restore music volume
      const audio = AudioManager.getInstance();
      if (this.cache.audio.exists('sfx_curtain_heavy_up')) {
        audio.playSFX(this, 'sfx_curtain_heavy_up', { volume: 0.45 });
      }
      const music: any = (audio as any).currentMusic;
      if (music && typeof music.volume === 'number' && this.pauseMusicBaseVolume !== undefined) {
        this.tweens.add({ targets: music, volume: this.pauseMusicBaseVolume, duration: 350 });
        this.pauseMusicBaseVolume = undefined;
      }

      // Tear down pause-mode gamepad listener.
      if (this.pauseNavTick) {
        this.events.off(Phaser.Scenes.Events.UPDATE, this.pauseNavTick);
        this.pauseNavTick = undefined;
      }
      this.pauseNav?.destroy();
      this.pauseNav = undefined;

      // Destroy all pause modal elements
      this.pauseElements.forEach(el => el.destroy());
      this.pauseElements = [];
      this.pauseText = undefined;
      this.pauseOverlay = undefined;

      // Resume UI scene timer
      KioskWatchdog.instance.popModal();
      this.events.emit('game-resumed');

      console.log('▶️ Game RESUMED');
    }
  }

  private createBackground(): void {
    if (!this.backgroundManager) return;

    // Use stage configuration if available, otherwise use default gradient
    if (this.stageConfig && this.stageConfig.background) {
      this.backgroundManager.createBackground(this.stageConfig.background);
    } else {
      // Default gradient background
      this.backgroundManager.createBackground({
        type: 'gradient',
        topColor: '#87CEEB',
        bottomColor: '#1E90FF'
      });
    }
  }

  /**
   * Add atmospheric depth effects (code-based, no image assets)
   */
  private createAtmosphericEffects(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const worldNum = this.currentStage <= 2 ? 1 : this.currentStage <= 4 ? 2 : 3;

    // 1. Vignette — darkened edges for cinematic depth
    const vignette = this.add.graphics();
    vignette.setDepth(400);
    vignette.setAlpha(0.4);
    // Top edge
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.6, 0.6, 0, 0);
    vignette.fillRect(0, 0, WIDTH, 120);
    // Bottom edge
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.5, 0.5);
    vignette.fillRect(0, HEIGHT - 100, WIDTH, 100);
    // Left edge
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.4, 0, 0.4, 0);
    vignette.fillRect(0, 0, 80, HEIGHT);
    // Right edge
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0.4, 0, 0.4);
    vignette.fillRect(WIDTH - 80, 0, 80, HEIGHT);

    // Floating ambient particles — underwater rising bubbles, World 1 only.
    if (worldNum === 1) {
      if (!this.textures.exists('ambient_particle')) {
        const g = this.add.graphics();
        g.fillStyle(0xFFFFFF, 1);
        g.fillCircle(3, 3, 3);
        g.generateTexture('ambient_particle', 6, 6);
        g.destroy();
      }
      this.add.particles(0, 0, 'ambient_particle', {
        x: { min: 0, max: WIDTH },
        y: HEIGHT + 10,
        lifespan: { min: 3000, max: 6000 },
        speedY: { min: -60, max: -30 },
        speedX: { min: -8, max: 8 },
        scale: { start: 0.2, end: 0.6 },
        alpha: { start: 0.12, end: 0 },
        tint: [0x88DDFF, 0xAAEEFF, 0x66CCFF],
        frequency: 300,
        blendMode: 'ADD',
      }).setDepth(5);
    }

    // 3. AAA: Water caustics overlay (DISABLED - performance impact)
    // this.causticsGraphics = this.add.graphics();
    // this.causticsGraphics.setDepth(4);
    // this.causticsGraphics.setAlpha(EFFECTS_CONFIG.CAUSTICS_ALPHA);
    // this.causticsGraphics.setBlendMode(Phaser.BlendModes.ADD);

    // 4. AAA: Low health vignette (red pulse when 1 life)
    this.lowHealthVignette = this.add.graphics();
    this.lowHealthVignette.setDepth(9000);
    this.lowHealthVignette.setAlpha(0);
    // Draw red vignette edges
    this.lowHealthVignette.fillGradientStyle(0xFF0000, 0xFF0000, 0xFF0000, 0xFF0000, 0.6, 0.6, 0, 0);
    this.lowHealthVignette.fillRect(0, 0, WIDTH, 150);
    this.lowHealthVignette.fillGradientStyle(0xFF0000, 0xFF0000, 0xFF0000, 0xFF0000, 0, 0, 0.6, 0.6);
    this.lowHealthVignette.fillRect(0, HEIGHT - 150, WIDTH, 150);
    this.lowHealthVignette.fillGradientStyle(0xFF0000, 0xFF0000, 0xFF0000, 0xFF0000, 0.5, 0, 0.5, 0);
    this.lowHealthVignette.fillRect(0, 0, 100, HEIGHT);
    this.lowHealthVignette.fillGradientStyle(0xFF0000, 0xFF0000, 0xFF0000, 0xFF0000, 0, 0.5, 0, 0.5);
    this.lowHealthVignette.fillRect(WIDTH - 100, 0, 100, HEIGHT);

    // 5. AAA: Timer urgency screen edge glow
    this.urgencyOverlay = this.add.graphics();
    this.urgencyOverlay.setDepth(9001);
    this.urgencyOverlay.setAlpha(0);
    this.urgencyOverlay.fillGradientStyle(0xFF4400, 0xFF4400, 0xFF4400, 0xFF4400, 0.5, 0.5, 0, 0);
    this.urgencyOverlay.fillRect(0, 0, WIDTH, 80);
    this.urgencyOverlay.fillGradientStyle(0xFF4400, 0xFF4400, 0xFF4400, 0xFF4400, 0, 0, 0.5, 0.5);
    this.urgencyOverlay.fillRect(0, HEIGHT - 80, WIDTH, 80);

    // Floating pollution debris drift — underwater storytelling, World 1 only.
    if (worldNum === 1) {
      const pollutionItems = ['●', '■', '▲', '◆'];
      for (let i = 0; i < 4; i++) {
        const px = Math.random() * WIDTH;
        const py = 150 + Math.random() * (HEIGHT - 350);
        const item = this.add.text(px, py, pollutionItems[i % pollutionItems.length], {
          fontSize: `${10 + Math.random() * 8}px`,
          color: '#888888',
        });
        item.setAlpha(0.12 + Math.random() * 0.08);
        item.setDepth(2);
        item.setAngle(Math.random() * 360);
        this.tweens.add({
          targets: item,
          x: item.x + (Math.random() - 0.5) * 200,
          y: item.y + (Math.random() - 0.5) * 100,
          angle: item.angle + (Math.random() - 0.5) * 90,
          alpha: { from: item.alpha, to: item.alpha * 0.5 },
          duration: 8000 + Math.random() * 6000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }

    // 7. AAA: DEEPER PARALLAX LAYERS - foreground seaweed sway
    for (let i = 0; i < 5; i++) {
      const seaweedX = WIDTH * 0.1 + (WIDTH * 0.8 / 5) * i + Math.random() * 40;
      const seaweedY = HEIGHT - 30;
      const seaweed = this.add.rectangle(seaweedX, seaweedY, 4, 40 + Math.random() * 30, 0x2D8B4E, 0.15);
      seaweed.setOrigin(0.5, 1);
      seaweed.setDepth(500); // In front of gameplay
      // Sway animation
      this.tweens.add({
        targets: seaweed,
        angle: { from: -8, to: 8 },
        duration: 2000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Math.random() * 2000,
      });
    }

    // 8. Subtle animated light rays (underwater worlds only)
    if (worldNum === 1) {
      for (let i = 0; i < 3; i++) {
        const rayX = WIDTH * 0.2 + i * WIDTH * 0.3;
        const ray = this.add.graphics();
        ray.setDepth(3);
        ray.setAlpha(0.06);
        ray.fillStyle(0xFFFFFF, 1);
        // Tapered beam shape
        ray.fillTriangle(
          rayX - 30, 0,
          rayX + 30, 0,
          rayX + 80 + i * 20, HEIGHT * 0.7
        );

        // Gentle sway
        this.tweens.add({
          targets: ray,
          x: { from: -20, to: 20 },
          alpha: { from: 0.04, to: 0.08 },
          duration: 4000 + i * 1000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  private createGround(): void {
    const graphics = this.add.graphics();
    graphics.fillStyle(0x8B4513, 1);
    graphics.fillRect(0, PHYSICS_CONFIG.GROUND_Y, this.cameras.main.width, 80);
    graphics.setAlpha(0);
  }

  private setupCamera(): void {
    const camera = this.cameras.main;
    camera.setBounds(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
    camera.fadeIn(CAMERA_CONFIG.FADE_IN_DURATION, 0, 0, 0);

    // AAA: Camera post-processing effects
    // v4: Bloom moved to Phaser.Actions.AddEffectBloom; Vignette stays on the FilterList.
    if (camera.filters) {
      camera.filters.internal.addVignette(0.5, 0.5, 0.9, 0.2);
      Phaser.Actions.AddEffectBloom([camera], {
        threshold: 0.3,
        blurRadius: 0.3,
        blurSteps: 0.8,
        blendAmount: 0.6,
      });
    }

    console.log('📷 Camera system initialized with AAA post-processing');
  }

  private createPlayer(): void {
    // Try to create Poseidon sprite with animation, fallback to rectangle if sprites not loaded
    try {
      // Check if the first idle frame exists
      const firstFrameKey = `${FRAME_KEYS.PLAYER.IDLE}_1`;
      if (!this.textures.exists(firstFrameKey)) {
        throw new Error('Idle animation frames not loaded');
      }

      // Create sprite using the first frame
      const player = this.add.sprite(
        PLAYER_CONFIG.START_X,
        PLAYER_CONFIG.START_Y,
        firstFrameKey
      );
      this.player = player;

      // Lock on-screen display size. Idle frames are 600×760 (2× crisp);
      // walk/shoot/flinch/etc are 300×380. setDisplaySize keeps the sprite
      // at a fixed visual size regardless of which pose's frames are active.
      const dispW = PLAYER_CONFIG.WIDTH * PLAYER_CONFIG.SCALE * 1.2;
      const dispH = PLAYER_CONFIG.HEIGHT * PLAYER_CONFIG.SCALE * 1.2;
      const lockDisplaySize = () => player.setDisplaySize(dispW, dispH);
      lockDisplaySize();
      player.on('animationstart', lockDisplaySize);
      
      this.player.enableFilters();

      // Add shooting aura glow effect (starts invisible).
      // Uses lighter cyan color to match trident energy. We keep the filter
      // reference on `this.playerGlow` so later code can mutate it.
      // v4 addGlow inserts a `scale` arg before `knockout`.
      this.playerGlow = this.player.filters!.internal.addGlow(
        0x71D2FF,  // Light cyan color matching trident (#71D2FF)
        5,         // outerStrength
        0,         // innerStrength
        1,         // scale (v4 NEW)
        false,     // knockout
        0.1,       // quality
        16,        // distance
      );
      this.playerGlow.outerStrength = 0; // start invisible until shooting
      console.log('✨ Shooting aura glow effect added (light cyan, invisible until shooting)');
      
      // Play idle animation
      if (this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) {
        this.player.play(ANIMATION_KEYS.PLAYER.IDLE);
        console.log('✅ Playing idle animation on player sprite');
      }
      
      this.playerState = 'idle';

      // Create ground shadow (ellipse beneath player)
      this.createPlayerGroundShadow();

      console.log('✅ Player sprite created with animations');
    } catch (error) {
      console.warn('⚠️ Sprite frames not loaded, using placeholder rectangle');
      // Fallback to rectangle if sprite doesn't exist (doubled size: 0.5 -> 1.0)
      this.player = this.add.rectangle(
        PLAYER_CONFIG.START_X,
        PLAYER_CONFIG.START_Y,
        PLAYER_CONFIG.WIDTH * PLAYER_CONFIG.SCALE * 1.0,
        PLAYER_CONFIG.HEIGHT * PLAYER_CONFIG.SCALE * 1.0,
        0x0066FF
      ) as any;
      this.playerState = 'idle';
    }
  }

  private setupInput(): void {
    // Arrow keys
    this.cursors = this.input.keyboard?.createCursorKeys();

    // Space bar for shooting
    this.spaceKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // P key for pause
    const pKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.P);
    pKey?.on('down', () => {
      this.togglePause();
    });

    // === USB / Bluetooth gamepad ===
    // Browsers only surface a gamepad after the user presses a button on it,
    // so we bind whichever pad fires the 'connected' event.
    if (this.input.gamepad) {
      const adopt = (pad: Phaser.Input.Gamepad.Gamepad) => {
        this.gamepad = pad;
        console.log(`🎮 Gamepad connected: ${pad.id} (${pad.buttons.length} buttons, ${pad.axes.length} axes)`);
      };
      // Already-connected pads (e.g. browser remembered a previous session)
      const existing = this.input.gamepad.getPad(0);
      if (existing) adopt(existing);
      this.input.gamepad.on('connected', adopt);
      this.input.gamepad.on('disconnected', (pad: Phaser.Input.Gamepad.Gamepad) => {
        if (this.gamepad === pad) {
          this.gamepad = undefined;
          console.log('🎮 Gamepad disconnected');
        }
      });
    }
  }

  private createParticleEmitter(): void {
    console.log('🎨 Creating particle emitters...');
    
    // Create a particle emitter for ball pop explosions
    // Default to REGULAR bubble explosion colors
    this.particleEmitter = this.add.particles(0, 0, 'pixel', {
      speed: { min: 200, max: 400 },
      angle: { min: 0, max: 360 },
      scale: { start: 2, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 400,
      blendMode: 'SCREEN',
      tint: getExplosionColors('REGULAR') as any, // Default bubble explosion colors
      gravityY: 400,
      frequency: -1, // Don't emit automatically
    });
    
    // Set depth to be above everything
    this.particleEmitter.setDepth(1000);
    
    // Stop emitting by default (we'll emit on demand)
    this.particleEmitter.stop();
    
    // Create bubble trail emitter for trident projectile - blue energy particles
    this.bubbleTrailEmitter = this.add.particles(0, 0, 'pixel', {
      speed: { min: 30, max: 70 },
      angle: { min: 150, max: 210 }, // Mostly upward with variation
      scale: { start: 2, end: 0 },
      alpha: { start: 0.8, end: 0 },
      lifespan: 600,
      blendMode: 'ADD', // Additive for glow effect
      tint: [0x4488FF, 0x5599FF, 0x6699FF], // Blue energy colors
      gravityY: -80, // Float upward
      frequency: 30, // Emit frequently for dense trail
    });
    
    this.bubbleTrailEmitter.setDepth(-3); // Behind player sprite
    this.bubbleTrailEmitter.stop(); // Don't emit until projectile fires
    
    // Create water splash emitter - blue energy sparks
    this.waterSplashEmitter = this.add.particles(0, 0, 'pixel', {
      speed: { min: 60, max: 120 },
      angle: { min: -80, max: 80 }, // Wide spray for energy effect
      scale: { start: 1.5, end: 0 },
      alpha: { start: 0.9, end: 0 },
      lifespan: 500,
      blendMode: 'ADD', // Additive for energy glow
      tint: [0xAADDFF, 0x88CCFF, 0xCCEEFF], // Bright blue/white energy
      gravityY: 50, // Slight downward drift
      frequency: 35, // Frequent emission for dense effect
    });
    
    this.waterSplashEmitter.setDepth(-4); // Behind bubble trail and player
    this.waterSplashEmitter.stop(); // Don't emit until projectile fires
    
    console.log('✅ Particle emitters created (explosion + bubble trail + water splash)');
  }

  /**
   * AAA: Start a dash in the given direction
   */
  private startDash(direction: number): void {
    this.isDashing = true;
    this.dashDirection = direction;
    this.dashTimer = PLAYER_CONFIG.DASH_DURATION;
    this.dashCooldownTimer = PLAYER_CONFIG.DASH_COOLDOWN;
    this.isInvincible = true; // Invincible during dash

    // Dash burst particles
    if (this.particleEmitter && this.player) {
      this.particleEmitter.setParticleTint(0x00CCFF);
      this.particleEmitter.emitParticleAt(this.player.x, this.player.y, 8);
    }

    // Camera micro-shake for dash feel
    if (screenShakeState.isEnabled()) {
      this.cameras.main.shake(60, 0.001);
    }

    // Slight squash on player during dash
    if (this.player) {
      const origScaleX = PLAYER_CONFIG.SCALE * 1.2;
      const origScaleY = PLAYER_CONFIG.SCALE * 1.2;
      this.player.setScale(origScaleX * 1.15, origScaleY * 0.9);
      this.tweens.add({
        targets: this.player,
        scaleX: origScaleX, scaleY: origScaleY,
        duration: 200, ease: 'Sine.easeOut',
      });
    }

    console.log(`💨 DASH ${direction > 0 ? 'RIGHT' : 'LEFT'}!`);
  }

  private emitBallPopParticles(x: number, y: number, radius: number, colors?: number[]): void {
    if (!this.particleEmitter) return;

    const explosionColors = colors || getExplosionColors('REGULAR');
    const colorArray = Array.from(explosionColors);
    (this.particleEmitter as any).config.tint = { values: colorArray };

    // 3x more particles for juicy pops
    const particleCount = Math.floor(radius * 1.5);
    this.particleEmitter.emitParticleAt(x, y, particleCount);

    // === UNIFIED IMPACT — hit-stop, camera kick, flash, shockwave, barrel ===
    const flashColor = colorArray[0] || 0xFFFFFF;
    const impact = ImpactSystem.get(this);
    const presetName = ImpactSystem.presetForRadius(radius);
    impact.fire(x, y, presetName, flashColor);

    // Last-ball bullet-time: slow the scene dramatically on final pop of stage
    if (this.balls.length === 1 && this.ballsBeingSplit.size === 0 && !this.isGameOver) {
      impact.timeSlice(700, 0.35);
    }

    // === AAA: DEBRIS PARTICLES (small chunks flying outward) ===
    for (let i = 0; i < Math.min(radius * 0.08, 6); i++) {
      const angle = (Math.PI * 2 / 6) * i + Math.random() * 0.5;
      const speed = 200 + Math.random() * 300;
      const debris = this.add.circle(x, y, 3 + Math.random() * 4, flashColor, 0.8);
      debris.setDepth(998);
      this.tweens.add({
        targets: debris,
        x: x + Math.cos(angle) * speed * 0.5,
        y: y + Math.sin(angle) * speed * 0.5 + 100, // gravity pull
        alpha: 0,
        scale: 0,
        duration: 400 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => debris.destroy(),
      });
    }
  }

  /**
   * Drop a powerup item at the specified location
   */
  private dropPowerup(x: number, y: number, powerupType: PowerupTypeName): void {
    const config = getPowerupConfig(powerupType);
    
    // Create powerup sprite - size like level 3 ball (SMALL size / 2)
    const powerup = this.add.image(x, y, config.sprite);
    const smallBallScale = BALL_SIZES.SMALL.radius / BUBBLE_TEXTURE_RADIUS; // Scale to match small ball
    const powerupScale = smallBallScale * 0.5; // Half of small ball = level 3 size
    powerup.setScale(powerupScale * 0.8); // Start at 80% of target size
    powerup.setData('powerupType', powerupType);
    powerup.setData('velocityY', 0); // Start with no downward velocity
    
    // Set depth to render in front of everything
    powerup.setDepth(200);
    
    // Add awesome Phaser FX Glow effect (v4: addGlow has new `scale` arg)
    const glowColor = parseInt(config.glowColor, 16);
    powerup.enableFilters();
    const glowFX = powerup.filters!.internal.addGlow(glowColor, 4, 0, 1, false, 0.1, 10);
    powerup.setData('glowFX', glowFX); // Store reference to glow FX
    
    // Add to tracked powerups
    this.droppedPowerups.push(powerup);
    
    // Fast blinking glow effect - animate the outerStrength
    if (glowFX) {
      this.tweens.add({
        targets: glowFX,
        outerStrength: 8, // Pulse between 4 and 8
        duration: config.glowBlinkSpeed,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
    
    // Pulse animation to slightly larger size
    this.tweens.add({
      targets: powerup,
      scale: powerupScale * 1.0, // Pulse to full target size
      duration: 400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    console.log(`⚡ Dropped ${config.name} powerup at (${x}, ${y})`);
  }

  /**
   * Update dropped powerups (make them fall and check for collection)
   */
  private updatePowerups(delta: number): void {
    const deltaSeconds = delta / 1000;
    
    if (this.droppedPowerups.length > 0 && debugState.getDebugMode()) {
      console.log(`🔄 Updating ${this.droppedPowerups.length} powerups, Player exists: ${!!this.player}`);
    }
    
    for (let i = this.droppedPowerups.length - 1; i >= 0; i--) {
      const powerup = this.droppedPowerups[i];
      const powerupType = powerup.getData('powerupType');
      const config = getPowerupConfig(powerupType);
      
      // Check for collection by player FIRST (before moving)
      // This allows immediate collection if powerup drops on player
      if (!this.player) {
        console.warn('⚠️ Player not found in updatePowerups!');
        continue;
      }
      
      if (this.player) {
        // Use generous collection zone: horizontal proximity + vertical overlap
        // Player is at y~876, ground powerups at y~970 — pure circle distance fails
        const dx = Math.abs(this.player.x - powerup.x);
        const dy = Math.abs(this.player.y - powerup.y);
        const horizontalReach = config.collectRadius + 40; // Wider horizontal pickup
        const verticalReach = 150; // Generous vertical (player height covers this)

        if (dx < horizontalReach && dy < verticalReach) {
          console.log(`🎯 Collecting powerup! dx: ${dx.toFixed(0)}, dy: ${dy.toFixed(0)}`);
          this.collectPowerup(powerup, powerupType);
          this.droppedPowerups.splice(i, 1);
          continue;
        }
      }
      
      // Float and bob in the water instead of sinking to the floor. The drop
      // eases to a hover line that still sits inside the player's collection
      // reach, bobs gently so it reads as floating, and drifts toward the
      // player so it stays reachable wherever they are.
      const hoverY = PHYSICS_CONFIG.GROUND_Y - 200; // ~800: within the player's vertical reach
      let baseY = powerup.getData('floatBaseY');
      if (baseY === undefined) baseY = powerup.y;
      baseY += (hoverY - baseY) * Math.min(1, deltaSeconds * 1.5); // ease toward hover height
      powerup.setData('floatBaseY', baseY);

      let bobPhase = (powerup.getData('bobPhase') || 0) + deltaSeconds * 2.2;
      powerup.setData('bobPhase', bobPhase);
      powerup.y = baseY + Math.sin(bobPhase) * 16; // gentle vertical bob

      // gentle horizontal drift toward the player so the reward floats over
      const driftDx = this.player.x - powerup.x;
      powerup.x += Math.sign(driftDx) * Math.min(Math.abs(driftDx), 90 * deltaSeconds);
    }
  }

  // ============================================
  // === AAA SYSTEMS ===
  // ============================================

  /**
   * AAA: Animated water caustics overlay
   */
  // @ts-ignore - disabled but kept for future re-enable
  private updateCaustics(delta: number): void {
    if (!this.causticsGraphics) return;
    // Skip caustics on low/medium quality
    if (!graphicsState.getConfig().caustics) {
      this.causticsGraphics.setAlpha(0);
      return;
    }
    this.causticsGraphics.setAlpha(EFFECTS_CONFIG.CAUSTICS_ALPHA);
    this.causticsTime += delta * EFFECTS_CONFIG.CAUSTICS_SPEED;

    this.causticsGraphics.clear();
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cellSize = 120;

    for (let x = 0; x < WIDTH; x += cellSize) {
      for (let y = 0; y < HEIGHT; y += cellSize) {
        // Simple animated caustic pattern using sin waves
        const brightness = Math.sin(x * 0.01 + this.causticsTime * 2) *
                          Math.cos(y * 0.012 + this.causticsTime * 1.5) *
                          Math.sin((x + y) * 0.008 + this.causticsTime);
        if (brightness > 0.2) {
          const alpha = (brightness - 0.2) * 0.5;
          this.causticsGraphics.fillStyle(0xAADDFF, alpha);
          this.causticsGraphics.fillCircle(
            x + Math.sin(this.causticsTime + x * 0.02) * 20,
            y + Math.cos(this.causticsTime * 0.7 + y * 0.02) * 20,
            cellSize * 0.4 * brightness
          );
        }
      }
    }
  }

  /**
   * AAA: Low health vignette pulse
   */
  private updateLowHealthEffects(): void {
    if (!this.lowHealthVignette) return;

    if (this.lives === 1 && !this.isGameOver) {
      // Activate pulsing red vignette
      if (!this.lowHealthPulseTween) {
        this.lowHealthPulseTween = this.tweens.add({
          targets: this.lowHealthVignette,
          alpha: { from: 0, to: EFFECTS_CONFIG.LOW_HEALTH_VIGNETTE_ALPHA },
          duration: EFFECTS_CONFIG.LOW_HEALTH_PULSE_SPEED,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    } else {
      // Deactivate
      if (this.lowHealthPulseTween) {
        this.lowHealthPulseTween.stop();
        this.lowHealthPulseTween = undefined;
        this.lowHealthVignette.setAlpha(0);
      }
    }
  }

  /**
   * AAA: Update charge shot indicator
   */
  private updateChargeIndicator(): void {
    if (!this.isCharging || !this.player || !this.chargeIndicator) return;

    const elapsed = this.time.now - this.chargeStartTime;
    const chargePercent = Math.min(elapsed / PLAYER_CONFIG.CHARGE_TIME, 1);

    // Update charge ring size
    this.chargeIndicator.setScale(0.5 + chargePercent * 1.0);
    this.chargeIndicator.setAlpha(0.3 + chargePercent * 0.5);
    this.chargeIndicator.setPosition(this.player.x, this.player.y - this.player.height * this.player.scaleY * 0.3);

    if (this.chargeGlow) {
      this.chargeGlow.setPosition(this.player.x, this.player.y - this.player.height * this.player.scaleY * 0.3);
      this.chargeGlow.setScale(0.3 + chargePercent * 1.5);
      this.chargeGlow.setAlpha(chargePercent * 0.6);
    }

    // Fully charged feedback
    if (chargePercent >= 1 && this.chargeIndicator.getData('notifiedFull') !== true) {
      this.chargeIndicator.setData('notifiedFull', true);
      // Camera micro-pulse
      if (screenShakeState.isEnabled()) {
        this.cameras.main.shake(40, 0.001);
      }
      // Color shift to gold
      this.chargeIndicator.setStrokeStyle(3, 0xFFD700, 0.8);
      if (this.chargeGlow) this.chargeGlow.setFillStyle(0xFFD700, 0.4);
    }
  }

  // ============================================
  // === AAA: AMBIENT FISH SYSTEM ===
  // ============================================

  /**
   * Install 3-layer parallax backgrounds on top of the stage background.
   * Each layer tiles horizontally and scrolls at a different speed when
   * the camera shakes/zooms.
   */
  private installParallaxLayers(worldNum: number): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const worldKey = `world${worldNum}`;

    const layers = [
      { suffix: 'far',  y: HEIGHT * 0.35, depth: 1, alpha: 0.55, scrollFactor: 0.3 },
      { suffix: 'mid',  y: HEIGHT * 0.6,  depth: 2, alpha: 0.75, scrollFactor: 0.6 },
      { suffix: 'near', y: HEIGHT * 0.9,  depth: 3, alpha: 0.9,  scrollFactor: 0.85 },
    ];

    for (const layer of layers) {
      const key = `parallax_${worldKey}_${layer.suffix}`;
      if (!this.textures.exists(key)) continue;

      const img = this.add.image(WIDTH / 2, layer.y, key);
      img.setDepth(layer.depth);
      img.setAlpha(layer.alpha);
      img.setScrollFactor(layer.scrollFactor);
      img.setOrigin(0.5, 0.5);
    }
  }

  // ============================================
  // === AAA: POLLUTION POOL HAZARD SYSTEM ===
  // ============================================

  /**
   * Spawn a toxic pollution pool on the ground
   */
  private spawnPollutionPool(x: number): void {
    if (!this.textures.exists('pollution_pool')) return;

    const groundY = PHYSICS_CONFIG.GROUND_Y - 15;
    const pool = this.add.image(x, groundY, 'pollution_pool');
    pool.setScale(0);
    pool.setAlpha(0);
    pool.setDepth(8); // Above background, below gameplay
    pool.setOrigin(0.5, 0.5);

    // Scale to ~160px wide
    const targetScale = 160 / pool.width;

    // Fade in with dramatic splat
    this.tweens.add({
      targets: pool,
      scale: targetScale,
      alpha: 0.8,
      duration: 400,
      ease: 'Back.easeOut',
    });

    // Add toxic glow (v4: addGlow has new `scale` arg)
    pool.enableFilters();
    const glow = pool.filters!.internal.addGlow(0x44FF00, 4, 0, 1, false, 0.1, 8);
    this.tweens.add({
      targets: glow,
      outerStrength: 8,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Store collision data
    pool.setData('radius', 70);
    pool.setData('active', true);

    this.pollutionPools.push(pool);

    // Auto-expire after 5 seconds
    this.time.delayedCall(5000, () => {
      pool.setData('active', false);
      this.tweens.add({
        targets: pool,
        alpha: 0,
        scale: targetScale * 0.5,
        duration: 600,
        ease: 'Power2',
        onComplete: () => {
          pool.destroy();
          this.pollutionPools = this.pollutionPools.filter(p => p !== pool);
        },
      });
    });

    // Character quip warning
    this.showCharacterQuip('Watch the sludge!', '#44FF00');
    console.log(`☠️ Pollution pool spawned at x=${Math.round(x)}`);
  }

  /**
   * Check player collision with pollution pools
   */
  private checkPollutionPoolCollisions(): void {
    if (!this.player || this.isInvincible || this.isGameOver || designModeState.isDesignMode()) return;

    for (const pool of this.pollutionPools) {
      if (!pool.getData('active')) continue;
      const poolRadius = pool.getData('radius') || 70;
      const dx = Math.abs(this.player.x - pool.x);
      const dy = Math.abs(this.player.y - pool.y);
      // Use rectangular zone (player near ground, pool on ground)
      if (dx < poolRadius && dy < 150) {
        // Player hit by pollution!
        if (this.hasShield) {
          this.popShield();
          this.startInvincibility();
          return;
        }
        // Pollution drains extra energy for its severity (one more than a normal hit)
        const lifeLost = this.handlePlayerHit(ENERGY_CONFIG.DAMAGE_PER_HIT + 1);
        ImpactSystem.get(this).fire(this.player!.x, this.player!.y, 'MEDIUM', 0x44FF00);
        if (!lifeLost) this.showCharacterQuip('The pollution!', '#FF4444');
        return;
      }
    }
  }

  /**
   * ── TRIPLE-AAA: Unified damage handler ──
   * Energy drains per hit. When energy hits 0 we consume 1 "continue" (life)
   * and refill. When lives hit 0 → Game Over.
   *
   * Returns true if this hit caused a life loss (for callers that want to
   * trigger death-specific effects).
   */
  private handlePlayerHit(damage: number = 1): boolean {
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    const depleted: boolean = uiScene?.drainEnergy ? !!uiScene.drainEnergy(damage) : true;

    if (!depleted) {
      // Took damage but still alive — brief invincibility + hit-stop.
      this.startInvincibility();
      ImpactSystem.get(this).fire(this.player!.x, this.player!.y, 'MEDIUM', 0xFF3355);
      return false;
    }

    // Energy depleted — consume a continue
    this.lives--;
    if (uiScene?.updateLives) uiScene.updateLives(this.lives);

    if (this.lives <= 0) {
      this.playerDeathCameraEffect();
      this.gameOver('Energy depleted');
      return true;
    }

    // Still have continues — refill energy + longer invincibility + big kick
    if (uiScene?.refillEnergy) uiScene.refillEnergy();
    this.startInvincibility();
    ImpactSystem.get(this).fire(this.player!.x, this.player!.y, 'LARGE', 0xFF3355);
    ImpactSystem.get(this).timeSlice(220, 0.45);
    this.showCharacterQuip('I will rise again!', '#FFD700');
    return true;
  }

  /**
   * AAA: Show a character dialog quip above Poseidon
   */
  private showCharacterQuip(text: string, color: string = '#FFFFFF'): void {
    if (!this.player) return;
    const now = this.time.now;
    if (now - this.lastQuipTime < this.quipCooldown) return;
    this.lastQuipTime = now;

    const quip = this.add.text(this.player.x, this.player.y - 200, text, {
      fontFamily: 'CF Asty Std',
      fontSize: '36px',
      color: color,
      stroke: '#000000',
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 4, fill: true },
    });
    quip.setOrigin(0.5);
    quip.setDepth(2000);
    quip.setAlpha(0);
    quip.setScale(0.5);

    // Speech bubble feel - pop in
    this.tweens.add({
      targets: quip,
      alpha: 1,
      scale: 1.0,
      y: this.player.y - 240,
      duration: 300,
      ease: 'Back.easeOut',
    });

    // Float up and fade
    this.tweens.add({
      targets: quip,
      alpha: 0,
      y: this.player.y - 300,
      duration: 500,
      delay: 1500,
      ease: 'Power2',
      onComplete: () => quip.destroy(),
    });
  }

  /**
   * AAA: Get a random quip for a game event
   */
  private getEventQuip(event: 'combo_high' | 'hit' | 'powerup' | 'last_ball' | 'near_miss'): { text: string; color: string } {
    const quips: Record<string, Array<{ text: string; color: string }>> = {
      combo_high: [
        { text: 'Feel the ocean\'s fury!', color: '#FFD700' },
        { text: 'The tides are with me!', color: '#00CCFF' },
        { text: 'Trident of destruction!', color: '#FF6600' },
        { text: 'UNSTOPPABLE!', color: '#FF2222' },
      ],
      hit: [
        { text: 'Argh! Merely a scratch!', color: '#FF4444' },
        { text: 'The sea tests me...', color: '#FFAA00' },
        { text: 'I will not fall!', color: '#FF6666' },
      ],
      powerup: [
        { text: 'Power of the deep!', color: '#00FFFF' },
        { text: 'The ocean provides!', color: '#00FF88' },
        { text: 'Excellent!', color: '#FFD700' },
      ],
      last_ball: [
        { text: 'One remains...', color: '#AADDFF' },
        { text: 'Finish this!', color: '#FFD700' },
        { text: 'Almost there!', color: '#00FF88' },
      ],
      near_miss: [
        { text: 'Too close!', color: '#FFAA00' },
        { text: 'Watch it!', color: '#FF8800' },
      ],
    };

    const options = quips[event];
    return options[Math.floor(Math.random() * options.length)];
  }

  /**
   * Collect a powerup and activate its effect
   */
  private collectPowerup(powerup: Phaser.GameObjects.Image, powerupType: PowerupTypeName): void {
    const config = getPowerupConfig(powerupType);

    // AAA: golden pickup chime (layered whoosh + sparkle)
    const audio = AudioManager.getInstance();
    audio.playSFX(this, 'sfx_powerup_collect', { volume: 0.7 });
    // Oceanic Overwatch: Poseidon voice on powerup collect
    audio.playVoiceStinger(this, 'powerup', 2500);

    // AAA: gold dust milestone burst (unified ParticlePools)
    ParticlePools.get(this).milestone('gold', powerup.x, powerup.y);

    // Collection animation (FX glow will automatically be destroyed with the sprite)
    this.tweens.add({
      targets: powerup,
      scale: 2.0,
      alpha: 0,
      duration: 300,
      ease: 'Back.easeIn',
      onComplete: () => powerup.destroy()
    });

    // Activate powerup effect
    this.activatePowerup(powerupType);

    // AAA: Character quip on powerup
    const puQuip = this.getEventQuip('powerup');
    this.showCharacterQuip(puQuip.text, puQuip.color);

    console.log(`⚡ Collected ${config.name}!`);
  }

  /**
   * Activate a powerup effect
   */
  private activatePowerup(powerupType: PowerupTypeName): void {
    const config = getPowerupConfig(powerupType);
    
    // Cancel any existing powerup effect
    if (this.activePowerupEffect) {
      this.activePowerupEffect.remove();
      this.unfreezeAllBalls(); // Unfreeze if switching powerups
    }
    
    // Apply effect based on type
    switch (config.effect) {
      case 'freeze_balls':
        this.freezeAllBalls(config.duration, config.name, config.displayDuration);
        break;
      case 'apply_shield':
        this.applyShield(config.name, config.displayDuration);
        break;
      case 'apply_multi_shot':
        this.applyMultiShot(config.duration, config.name, config.displayDuration);
        break;
      case 'apply_rapid_fire':
        this.applyRapidFire(config.duration, config.name, config.displayDuration);
        break;
      case 'apply_score_2x':
        this.applyScore2x(config.duration, config.name, config.displayDuration);
        break;
      case 'apply_extra_life':
        this.applyExtraLife(config.name, config.displayDuration);
        break;
      default:
        console.warn(`Unknown powerup effect: ${config.effect}`);
    }
  }

  // === RAPID FIRE: Removes cooldown, can fire continuously ===
  private isRapidFireActive = false;
  private rapidFireTimer?: Phaser.Time.TimerEvent;

  private applyRapidFire(duration: number, displayName: string, displayDuration: number): void {
    this.isRapidFireActive = true;
    this.showPowerupText(displayName, displayDuration);
    PowerupTracker.get(this).addChip('rapidfire', 'powerup_rapidfire', duration);
    if (this.rapidFireTimer) this.rapidFireTimer.remove();
    this.rapidFireTimer = this.time.delayedCall(duration, () => {
      this.isRapidFireActive = false;
    });
    // Player glows orange during rapid fire
    if (this.playerGlow) {
      this.playerGlow.outerStrength = 6;
      (this.playerGlow as unknown as { color: number }).color = 0xFF6600;
      this.time.delayedCall(duration, () => {
        if (this.playerGlow && !this.hasShield) {
          this.playerGlow.outerStrength = 0;
        }
      });
    }
    console.log(`🔥 Rapid Fire activated for ${duration}ms!`);
  }

  // === SCORE 2X: All points doubled ===
  private isScore2xActive = false;
  private score2xTimer?: Phaser.Time.TimerEvent;

  private applyScore2x(duration: number, displayName: string, displayDuration: number): void {
    this.isScore2xActive = true;
    this.showPowerupText(displayName, displayDuration);
    PowerupTracker.get(this).addChip('score2x', 'powerup_score2x', duration);
    if (this.score2xTimer) this.score2xTimer.remove();
    this.score2xTimer = this.time.delayedCall(duration, () => {
      this.isScore2xActive = false;
    });
    console.log(`💰 Score 2x activated for ${duration}ms!`);
  }

  // === EXTRA LIFE: Gain 1 life (up to max) ===
  private applyExtraLife(displayName: string, displayDuration: number): void {
    if (this.lives < LIVES_CONFIG.MAX_LIVES) {
      this.lives++;
    }
    this.showPowerupText(displayName, displayDuration);
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    if (uiScene?.updateLives) uiScene.updateLives(this.lives);
    // Green flash
    this.cameras.main.flash(200, 50, 255, 50);
    console.log(`❤️ Extra Life! Lives: ${this.lives}`);
  }

  /**
   * Apply Ocean Shield - absorbs 1 hit
   */
  private shieldExtras: Phaser.GameObjects.GameObject[] = [];

  private applyShield(displayName: string, displayDuration: number): void {
    this.hasShield = true;
    this.showPowerupText(displayName, displayDuration);
    // Shield is indefinite until popped — use a long duration, removed manually
    PowerupTracker.get(this).addChip('shield', 'powerup_shield', 60000);
    if (!this.player) return;

    // Cleanup old shield
    if (this.shieldGraphic) this.shieldGraphic.destroy();
    this.shieldExtras.forEach(e => e.destroy());
    this.shieldExtras = [];

    const px = this.player.x;
    const py = this.player.y;
    const depth = this.player.depth + 1;

    // === APPROACH: Subtle ring outline + player glow + orbiting sparks ===
    // Instead of a big glowy texture, use a thin circular outline ring
    // and apply a glow FX directly on the player sprite

    // Thin outer ring (not filled, just stroke)
    const ring = this.add.circle(px, py, 140, 0x000000, 0);
    ring.setStrokeStyle(3, 0x00FFFF, 0.6);
    ring.setDepth(depth + 1); // In front of player
    this.shieldGraphic = ring as any;

    // Ring pulse
    this.tweens.add({
      targets: ring,
      scale: { from: 0.95, to: 1.05 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Apply cyan glow directly on the PLAYER (the character glows, not a separate object)
    if (this.playerGlow) {
      // Boost existing glow to cyan
      this.playerGlow.outerStrength = 8;
      (this.playerGlow as unknown as { color: number }).color = 0x00CED1;
    }

    // Faint rotating aura texture BEHIND player at very low opacity
    const aura = this.add.image(px, py, 'shield_aura');
    aura.setScale(0.65);
    aura.setAlpha(0.12); // Barely visible — just a hint
    aura.setBlendMode(Phaser.BlendModes.SCREEN);
    aura.setDepth(depth - 3);
    this.shieldExtras.push(aura);

    this.tweens.add({
      targets: aura,
      angle: 360,
      duration: 10000,
      repeat: -1,
      ease: 'Linear',
    });
    this.tweens.add({
      targets: aura,
      alpha: { from: 0.08, to: 0.18 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // === ORBITING SPARKS ===
    for (let i = 0; i < 3; i++) {
      const spark = this.add.circle(px, py, 4, 0x00FFFF, 0.9);
      spark.setBlendMode(Phaser.BlendModes.ADD);
      spark.setDepth(depth + 1);
      spark.setData('orbitAngle', (Math.PI * 2 / 3) * i);
      spark.setData('orbitSpeed', 2.0 + i * 0.4);
      this.shieldExtras.push(spark);
    }

    // === SPAWN FLASH ===
    const flash = this.add.circle(px, py, 20, 0x00FFFF, 0.9);
    flash.setBlendMode(Phaser.BlendModes.ADD);
    flash.setDepth(depth + 2);
    this.tweens.add({
      targets: flash, scale: 10, alpha: 0, duration: 500, ease: 'Power2',
      onComplete: () => flash.destroy(),
    });

    console.log('🛡️ Ocean Shield activated!');
  }

  /**
   * Update shield elements position
   */
  private updateShieldPosition(): void {
    if (!this.hasShield || !this.player) return;
    const px = this.player.x;
    const py = this.player.y;

    // Move main aura
    if (this.shieldGraphic) {
      this.shieldGraphic.setPosition(px, py);
    }

    // Move extras (second aura layer + orbiting sparks)
    for (const extra of this.shieldExtras) {
      const orbitAngle = (extra as any).getData?.('orbitAngle');
      if (orbitAngle !== undefined) {
        const speed = (extra as any).getData('orbitSpeed');
        const newAngle = orbitAngle + speed * 0.016;
        (extra as any).setData('orbitAngle', newAngle);
        (extra as any).setPosition(
          px + Math.cos(newAngle) * 120,
          py + Math.sin(newAngle) * 120
        );
      } else {
        // It's the second aura layer — follow player
        (extra as any).setPosition(px, py);
      }
    }
  }

  /**
   * Pop the shield with dramatic shatter effect
   */
  private popShield(): void {
    this.hasShield = false;
    const px = this.player?.x || PLAYER_CONFIG.START_X;
    const py = this.player?.y || PLAYER_CONFIG.START_Y;

    // Remove shield chip from HUD tracker
    PowerupTracker.get(this).removeChip('shield');

    // Unified shield-break impact (teal, large)
    ImpactSystem.get(this).fire(px, py, 'LARGE', 0x00DDDD);

    // Shatter aura outward
    if (this.shieldGraphic) {
      this.tweens.add({
        targets: this.shieldGraphic,
        scale: 1.5, alpha: 0, angle: (this.shieldGraphic as any).angle + 180,
        duration: 400, ease: 'Power2',
        onComplete: () => { this.shieldGraphic?.destroy(); this.shieldGraphic = undefined; },
      });
    }

    // Shatter extras outward
    this.shieldExtras.forEach((el, i) => {
      const angle = (Math.PI * 2 / Math.max(this.shieldExtras.length, 1)) * i;
      this.tweens.add({
        targets: el,
        x: (el as any).x + Math.cos(angle) * 250,
        y: (el as any).y + Math.sin(angle) * 250,
        alpha: 0, scale: 0.2,
        duration: 450, ease: 'Power2',
        onComplete: () => el.destroy(),
      });
    });
    this.shieldExtras = [];

    // Reset player glow back to default
    if (this.playerGlow) {
      this.playerGlow.outerStrength = 0;
    }

    // Particle burst
    if (this.particleEmitter) {
      this.particleEmitter.setParticleTint(0x00FFFF);
      this.particleEmitter.emitParticleAt(px, py, 25);
    }

    // Expanding shockwave ring
    const shockRing = this.add.circle(px, py, 30, 0x00FFFF, 0);
    shockRing.setStrokeStyle(3, 0x00FFFF, 0.7);
    shockRing.setBlendMode(Phaser.BlendModes.ADD);
    shockRing.setDepth(999);
    this.tweens.add({
      targets: shockRing, scale: 5, alpha: 0, duration: 500, ease: 'Power2',
      onComplete: () => shockRing.destroy(),
    });

    console.log('🛡️💥 Shield shattered!');
  }

  /**
   * Apply Eco Blast - fires 3 projectiles for duration
   */
  private applyMultiShot(duration: number, displayName: string, displayDuration: number): void {
    this.isMultiShotActive = true;
    this.showPowerupText(displayName, displayDuration);
    PowerupTracker.get(this).addChip('multishot', 'powerup_multishot', duration);

    // Cancel existing timer
    if (this.multiShotTimer) this.multiShotTimer.remove();

    this.multiShotTimer = this.time.delayedCall(duration, () => {
      this.isMultiShotActive = false;
      console.log('🌊 Eco Blast expired');
    });
    console.log(`🌊 Eco Blast activated for ${duration}ms!`);
  }

  /**
   * Freeze all balls for a duration
   */
  private freezeAllBalls(duration: number, displayName: string, displayDuration: number): void {
    console.log(`❄️ Freezing all balls for ${duration}ms`);
    
    // Store current velocities
    this.balls.forEach(ball => {
      const isPowerupBall = ball.getData('isPowerupBall');
      if (!isPowerupBall) { // Don't freeze powerup balls
        const vx = ball.getData('velocityX');
        const vy = ball.getData('velocityY');
        this.frozenBallVelocities.set(ball, { vx, vy });
        
        // Set velocities to zero
        ball.setData('velocityX', 0);
        ball.setData('velocityY', 0);
        
        // Visual freeze effect (balls are sprites)
        (ball as any).setTint(0x88DDFF);
      }
    });
    
    this.isBallsFrozen = true;

    // Display powerup name in center of screen
    this.showPowerupText(displayName, displayDuration);
    PowerupTracker.get(this).addChip('timefreeze', 'powerup_timefreeze', duration);

    // Set timer to unfreeze
    this.activePowerupEffect = this.time.delayedCall(duration, () => {
      this.unfreezeAllBalls();
    });
  }

  /**
   * Unfreeze all balls
   */
  private unfreezeAllBalls(): void {
    console.log(`🔥 Unfreezing all balls`);
    
    const MIN_VELOCITY = 100; // Minimum velocity to ensure balls keep moving
    
    this.balls.forEach(ball => {
      const stored = this.frozenBallVelocities.get(ball);
      if (stored) {
        let vx = stored.vx;
        let vy = stored.vy;
        
        // Check if ball has too little velocity (stuck)
        const totalVelocity = Math.sqrt(vx * vx + vy * vy);
        if (totalVelocity < MIN_VELOCITY) {
          console.log(`⚠️ Ball had low velocity (${totalVelocity.toFixed(0)}), giving it a push`);
          
          // Give it a random horizontal velocity
          vx = (Math.random() > 0.5 ? 1 : -1) * (150 + Math.random() * 100);
          
          // Give it upward velocity if it's near the ground or has low vertical velocity
          if (Math.abs(vy) < 50 || ball.y > PHYSICS_CONFIG.GROUND_Y - 200) {
            vy = -(200 + Math.random() * 100); // Upward bounce
          }
        }
        
        ball.setData('velocityX', vx);
        ball.setData('velocityY', vy);
        
        // Clear freeze tint - restore original bubble tint
        const bubbleType = ball.getData('bubbleType') || 'REGULAR';
        const tint = getBubbleTint(bubbleType);
        (ball as any).setTint(tint);
      }
    });
    
    this.frozenBallVelocities.clear();
    this.isBallsFrozen = false;
    this.activePowerupEffect = undefined;
  }

  /**
   * Show powerup name text in center of screen
   */
  private showPowerupText(text: string, duration: number): void {
    const { width, height } = this.cameras.main;
    
    const powerupText = this.add.text(width / 2, height / 2, text, {
      fontSize: '80px',
      fontFamily: 'CF Asty Std',
      color: '#00FFFF',
      stroke: '#003366',
      strokeThickness: 8,
      align: 'center'
    });
    powerupText.setOrigin(0.5);
    powerupText.setDepth(1000);
    powerupText.setScale(0);
    
    // Smash in
    this.tweens.add({
      targets: powerupText,
      scale: 1.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Hold briefly, then fade out
        this.time.delayedCall(duration, () => {
          this.tweens.add({
            targets: powerupText,
            alpha: 0,
            scale: 0.8,
            duration: 300,
            ease: 'Back.easeIn',
            onComplete: () => powerupText.destroy()
          });
        });
      }
    });
  }

  /**
   * Get current input state (abstracts keyboard, AI, and touch)
   */
  private getInputState(delta: number): { moveLeft: boolean; moveRight: boolean; shoot: boolean } {
    // Demo mode: use AI
    if (this.isDemoMode && this.aiPlayer) {
      const ballData = this.balls.map(ball => ({
        x: ball.x,
        y: ball.y,
        radius: ball.getData('radius') as number,
        vx: ball.getData('velocityX') as number,
        vy: ball.getData('velocityY') as number,
      }));
      const ai = this.aiPlayer.getInput(
        this.player?.x || PLAYER_CONFIG.START_X,
        this.player?.y || PLAYER_CONFIG.START_Y,
        ballData,
        delta
      );
      return { moveLeft: ai.left, moveRight: ai.right, shoot: ai.shoot };
    }

    // Normal mode: keyboard + touch + gamepad
    let padLeft = false;
    let padRight = false;
    let padShoot = false; // edge-detected (rising)
    let padPause = false; // edge-detected (rising)

    if (this.gamepad) {
      // Left stick X (axis 0) + D-pad. Deadzone keeps drifting sticks quiet.
      const DEADZONE = 0.25;
      const axisX = this.gamepad.axes.length > 0 ? this.gamepad.axes[0].getValue() : 0;
      const dpadLeft = this.gamepad.left;   // D-pad left (button 14 on standard mapping)
      const dpadRight = this.gamepad.right; // D-pad right (button 15)
      padLeft = dpadLeft || axisX < -DEADZONE;
      padRight = dpadRight || axisX > DEADZONE;

      // Button 0 (A / Cross) — primary shoot button. "Button 1" in colloquial 1-indexed
      // terms is the first/main face button, which on the W3C Standard Gamepad mapping
      // is index 0. We also accept button 1 (B / Circle) as a courtesy.
      const shootHeld =
        (this.gamepad.buttons[0]?.pressed ?? false) ||
        (this.gamepad.buttons[1]?.pressed ?? false);
      padShoot = shootHeld && !this.prevGamepadShoot;
      this.prevGamepadShoot = shootHeld;

      // Start (9) or Select/Back (8) → pause toggle
      const pauseHeld =
        (this.gamepad.buttons[9]?.pressed ?? false) ||
        (this.gamepad.buttons[8]?.pressed ?? false);
      padPause = pauseHeld && !this.prevGamepadPause;
      this.prevGamepadPause = pauseHeld;
    } else {
      this.prevGamepadShoot = false;
      this.prevGamepadPause = false;
    }

    if (padPause) this.togglePause();

    const moveLeft = (this.cursors?.left.isDown || false) || this.touchLeft || padLeft;
    const moveRight = (this.cursors?.right.isDown || false) || this.touchRight || padRight;
    const shoot = (this.cursors ? Phaser.Input.Keyboard.JustDown(this.cursors.up) : false) ||
                  (this.spaceKey ? Phaser.Input.Keyboard.JustDown(this.spaceKey) : false) ||
                  this.touchShoot ||
                  padShoot;

    // Reset touch shoot (it's a one-shot)
    if (this.touchShoot) this.touchShoot = false;

    return { moveLeft, moveRight, shoot };
  }

  private updatePlayer(delta: number): void {
    if (!this.player) return;

    const input = this.getInputState(delta);
    const deltaSeconds = delta / 1000;
    let isMoving = false;

    // === AAA: DASH SYSTEM (double-tap detection) ===
    this.dashCooldownTimer = Math.max(0, this.dashCooldownTimer - delta);

    if (!this.isDashing) {
      // Detect double-tap left
      if (input.moveLeft && !this.prevInputLeft) {
        const now = this.time.now;
        if (now - this.lastLeftTapTime < PLAYER_CONFIG.DASH_DOUBLE_TAP_WINDOW && this.dashCooldownTimer <= 0) {
          this.startDash(-1);
        }
        this.lastLeftTapTime = now;
      }
      // Detect double-tap right
      if (input.moveRight && !this.prevInputRight) {
        const now = this.time.now;
        if (now - this.lastRightTapTime < PLAYER_CONFIG.DASH_DOUBLE_TAP_WINDOW && this.dashCooldownTimer <= 0) {
          this.startDash(1);
        }
        this.lastRightTapTime = now;
      }
    }
    this.prevInputLeft = input.moveLeft;
    this.prevInputRight = input.moveRight;

    // === AAA: DASH EXECUTION ===
    if (this.isDashing) {
      this.dashTimer -= delta;
      this.player.x += this.dashDirection * PLAYER_CONFIG.DASH_SPEED * deltaSeconds;
      this.playerVelocityX = this.dashDirection * PLAYER_CONFIG.DASH_SPEED;

      // Dash afterimage trail
      if (this.player.type === 'Sprite' && Math.random() < 0.6) {
        const afterimage = this.add.sprite(this.player.x, this.player.y, this.player.texture.key, this.player.frame.name);
        afterimage.setScale(this.player.scaleX, this.player.scaleY);
        afterimage.setFlipX(this.player.flipX);
        afterimage.setAlpha(EFFECTS_CONFIG.DASH_AFTERIMAGE_ALPHA);
        afterimage.setTint(0x00CCFF);
        afterimage.setBlendMode(Phaser.BlendModes.ADD);
        afterimage.setDepth(this.player.depth - 1);
        this.tweens.add({
          targets: afterimage,
          alpha: 0, scaleX: afterimage.scaleX * 1.1, scaleY: afterimage.scaleY * 0.9,
          duration: EFFECTS_CONFIG.DASH_AFTERIMAGE_FADE,
          onComplete: () => afterimage.destroy(),
        });
      }

      if (this.dashTimer <= 0) {
        this.isDashing = false;
        this.isInvincible = false; // End dash invincibility
      }
      isMoving = true;
    } else {
      // === AAA: MOMENTUM-BASED MOVEMENT ===
      const targetVelocity = input.moveLeft ? -PLAYER_CONFIG.SPEED :
                             input.moveRight ? PLAYER_CONFIG.SPEED : 0;

      if (targetVelocity !== 0) {
        // Accelerate toward target
        const accelDir = Math.sign(targetVelocity - this.playerVelocityX);
        this.playerVelocityX += accelDir * PLAYER_CONFIG.ACCELERATION * deltaSeconds;
        // Clamp to max speed
        this.playerVelocityX = Phaser.Math.Clamp(this.playerVelocityX, -PLAYER_CONFIG.SPEED, PLAYER_CONFIG.SPEED);
        isMoving = true;
      } else {
        // Decelerate to stop (slide feel)
        if (Math.abs(this.playerVelocityX) > 10) {
          const decelDir = -Math.sign(this.playerVelocityX);
          this.playerVelocityX += decelDir * PLAYER_CONFIG.DECELERATION * deltaSeconds;
          // Check if we overshot zero
          if (decelDir > 0 && this.playerVelocityX > 0) this.playerVelocityX = 0;
          if (decelDir < 0 && this.playerVelocityX < 0) this.playerVelocityX = 0;
          isMoving = Math.abs(this.playerVelocityX) > 10;
        } else {
          this.playerVelocityX = 0;
        }
      }

      // Apply velocity
      this.player.x += this.playerVelocityX * deltaSeconds;
    }

    // Flip sprite based on velocity direction
    if (this.playerVelocityX < -50) this.player.setFlipX(true);
    else if (this.playerVelocityX > 50) this.player.setFlipX(false);

    // === AAA: SKID PARTICLES (direction change at high speed) ===
    if (Math.abs(this.playerVelocityX) > PLAYER_CONFIG.SPEED * 0.5 && isMoving) {
      const isChangingDirection = (input.moveLeft && this.playerVelocityX > 100) ||
                                   (input.moveRight && this.playerVelocityX < -100);
      if (isChangingDirection && this.particleEmitter) {
        const dustY = this.player.y + this.player.height * this.player.scaleY * 0.4;
        this.particleEmitter.setParticleTint(0xDDCCAA);
        this.particleEmitter.emitParticleAt(this.player.x, dustY, 3);
      }
    }

    // Update animation based on movement (if not shooting and if using sprite)
    if (this.playerState !== 'shooting' && this.player.type === 'Sprite') {
      if (isMoving) {
        if (this.playerState !== 'walking') {
          if (this.anims.exists(ANIMATION_KEYS.PLAYER.WALK)) {
            this.player.play(ANIMATION_KEYS.PLAYER.WALK);
          }
          this.playerState = 'walking';
        }
      } else {
        if (this.playerState !== 'idle') {
          if (this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) {
            this.player.play(ANIMATION_KEYS.PLAYER.IDLE);
          }
          this.playerState = 'idle';
        }
      }
    }

    // === WALK DUST PUFFS ===
    if (isMoving && this.player && Math.random() < 0.15) {
      const dustY = this.player.y + this.player.height * this.player.scaleY * 0.4;
      if (this.particleEmitter) {
        this.particleEmitter.setParticleTint(0xBBAA88);
        this.particleEmitter.emitParticleAt(this.player.x, dustY, 1);
      }
    }

    // Constrain to game boundaries
    const halfWidth = (this.player.width * this.player.scaleX) / 2;
    this.player.x = Phaser.Math.Clamp(
      this.player.x,
      PHYSICS_CONFIG.WALL_LEFT + halfWidth,
      PHYSICS_CONFIG.WALL_RIGHT - halfWidth
    );

    // Shoot
    if (input.shoot) {
      this.shootProjectile();
    }

    // Update trident power point glow position (follows player)
    if (this.tridentGlow && this.player) {
      const tridentY = this.player.y - (this.player.height * this.player.scaleY * 0.4);
      this.tridentGlow.setPosition(this.player.x, tridentY);
    }

    // Update ground shadow position
    if (this.playerShadow && this.player) {
      this.playerShadow.setX(this.player.x);
    }

    // === DYNAMIC BACKGROUND PARALLAX (very subtle shift based on player position) ===
    if (this.backgroundManager && this.player) {
      const parallaxStrength = 5; // pixels of max shift (reduced from 15 to prevent jitter)
      const normalizedX = (this.player.x - GAME_CONFIG.WIDTH / 2) / (GAME_CONFIG.WIDTH / 2); // -1 to 1
      const targetScroll = normalizedX * parallaxStrength;
      // Lerp to target for smooth scrolling instead of instant jump
      const currentScroll = this.cameras.main.scrollX;
      this.cameras.main.setScroll(currentScroll + (targetScroll - currentScroll) * 0.1, 0);
    }
  }

  private shootProjectile(): void {
    // Rapid fire allows shooting even with active projectile (destroys old one first)
    if (this.isProjectileActive && this.isRapidFireActive) {
      this.destroyProjectile();
    }
    if (this.isProjectileActive || !this.player) return;

    console.log('🔱 Shooting stacked energy projectiles!');

    // ── TRIPLE-AAA MUZZLE FLASH: 3-layer flash burst at trident tip ──
    const tridentTipY = this.player.y - (this.player.height * this.player.scaleY * 0.42);
    const flashX = this.player.x;
    const flashY = tridentTipY;
    // Core flash (white)
    const muzzleCore = this.add.circle(flashX, flashY, 28, 0xFFFFFF, 1);
    muzzleCore.setBlendMode(Phaser.BlendModes.ADD).setDepth(150);
    this.tweens.add({ targets: muzzleCore, scale: 2.6, alpha: 0, duration: 160, ease: 'Cubic.easeOut', onComplete: () => muzzleCore.destroy() });
    // Gold ring
    const muzzleRing = this.add.circle(flashX, flashY, 18, 0x000000, 0).setStrokeStyle(5, 0xFFD700, 1);
    muzzleRing.setBlendMode(Phaser.BlendModes.ADD).setDepth(149);
    this.tweens.add({ targets: muzzleRing, scale: 4.0, alpha: 0, duration: 260, ease: 'Cubic.easeOut', onComplete: () => muzzleRing.destroy() });
    // Sapphire shockwave
    const muzzleShock = this.add.circle(flashX, flashY, 24, 0x000000, 0).setStrokeStyle(3, 0x5A8EEA, 0.9);
    muzzleShock.setBlendMode(Phaser.BlendModes.ADD).setDepth(148);
    this.tweens.add({ targets: muzzleShock, scale: 6.5, alpha: 0, duration: 380, ease: 'Cubic.easeOut', onComplete: () => muzzleShock.destroy() });
    // Cyan sparks from trident
    try { ParticlePools.get(this).burst('sapphire', flashX, flashY, 14); } catch {}

    // Fade IN the shooting aura glow (very fast power-up from trident)
    if (this.playerGlow) {
      // Stop any ongoing fade-out tween
      if (this.glowFadeOut) {
        this.glowFadeOut.stop();
        this.glowFadeOut = undefined;
      }

      // Very quick fade-in to full strength (faster build-up)
      this.glowFadeIn = this.tweens.add({
        targets: this.playerGlow,
        outerStrength: 5,  // Fade to full cyan glow (matches new strength)
        duration: 50,      // Faster fade-in (0.05 seconds - 2x faster)
        ease: 'Power2'
      });
      console.log('✨ Shooting aura glow fading IN (fast build from trident)');
    }

    // Create power point glow at trident (top of sprite)
    if (!this.tridentGlow && this.player) {
      const tridentY = this.player.y - (this.player.height * this.player.scaleY * 0.4); // Top of sprite
      this.tridentGlow = this.add.circle(this.player.x, tridentY, 20, 0x71D2FF, 0);
      this.tridentGlow.setBlendMode(Phaser.BlendModes.ADD);
      this.tridentGlow.setDepth(this.player.depth - 1); // Behind player

      // Fade in the power point glow
      this.tweens.add({
        targets: this.tridentGlow,
        alpha: 0.8,
        scale: 1.5,
        duration: 50,
        ease: 'Power2'
      });
      console.log('⚡ Trident power point glow created');
    }

    // Play shooting animation (if using sprite)
    if (this.player.type === 'Sprite') {
      this.playerState = 'shooting';
      
      // Play shoot animation if it exists
      if (this.anims.exists(ANIMATION_KEYS.PLAYER.SHOOT)) {
        this.player.play(ANIMATION_KEYS.PLAYER.SHOOT);
        
        // Listen for animation completion to return to idle
        this.player.once('animationcomplete', () => {
          if (this.player && this.player.type === 'Sprite') {
            if (this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) {
              this.player.play(ANIMATION_KEYS.PLAYER.IDLE);
            }
            this.playerState = 'idle';
          }
        });
      } else {
        // Fallback: use static texture if animation doesn't exist
        this.player.setTexture(ASSET_KEYS.PLAYER_SHOOT);
        this.time.delayedCall(200, () => {
          if (this.player && this.player.type === 'Sprite') {
            this.player.setTexture(ASSET_KEYS.PLAYER_IDLE);
            this.playerState = 'idle';
          }
        });
      }
    }

    // Initialize projectile sprite array
    this.projectileSprites = [];
    
    // Store the X position where projectile is fired (so it stays straight even if player moves)
    this.projectileStartX = this.player.x;
    
    // Calculate projectile spawn parameters
    const startY = this.player.y; // Start at player center
    const endY = PHYSICS_CONFIG.CEILING_Y;
    const maxHeight = startY - endY;

    // Create the first energy projectile sprite at player position
    const firstProjectile = this.add.image(this.projectileStartX, startY, ASSET_KEYS.ENERGY_PROJECTILE);
    firstProjectile.setScale(1.0); // Adjust scale as needed
    firstProjectile.setDepth(-1); // Behind player sprite
    firstProjectile.setBlendMode(Phaser.BlendModes.ADD); // Additive glow effect
    firstProjectile.setAlpha(0.9);
    
    this.projectileSprites.push(firstProjectile);
    console.log('✨ First energy projectile sprite created');

    // Store initial state
    this.isProjectileActive = true;
    this.projectileAtMaxHeight = false;
    this.projectileTargetHeight = maxHeight;

    // Add safety timeout to ensure projectile is destroyed even if it doesn't reach max height
    // This prevents the projectile from staying forever if something goes wrong
    this.projectileSafetyTimer = this.time.delayedCall(PROJECTILE_CONFIG.MAX_LIFETIME, () => {
      if (this.isProjectileActive) {
        console.log('⚠️ Projectile safety timeout - destroying');
        this.destroyProjectile();
      }
    });

    // Start bubble trail effect
    if (this.bubbleTrailEmitter) {
      this.bubbleTrailEmitter.start();
      console.log('💧 Bubble trail started');
    }

    // Start water splash effect
    if (this.waterSplashEmitter) {
      this.waterSplashEmitter.start();
      console.log('💦 Water splash started');
    }

    // Emit event to UI for potential effects
    this.events.emit('projectile-fired');

    // Multi-shot: fire two additional angled projectile lines
    if (this.isMultiShotActive && this.player) {
      const angles = [-15, 15]; // degrees offset
      for (const angleDeg of angles) {
        const angleRad = (angleDeg * Math.PI) / 180;
        const startX = this.player.x;
        const startY = this.player.y;

        // Create a simple line graphic for the angled shot
        const line = this.add.graphics();
        line.lineStyle(4, 0x32CD32, 0.8); // Green for eco blast
        line.setDepth(-1);
        line.setBlendMode(Phaser.BlendModes.ADD);

        let lineLength = 0;
        const maxLength = GAME_CONFIG.HEIGHT;
        const growSpeed = PROJECTILE_CONFIG.GROWTH_SPEED;

        // Animate the angled line growing
        const growTimer = this.time.addEvent({
          delay: 16, // ~60fps
          loop: true,
          callback: () => {
            lineLength += growSpeed * 0.016;
            if (lineLength >= maxLength || this.isGameOver) {
              line.destroy();
              growTimer.remove();
              return;
            }

            const endX = startX + Math.sin(angleRad) * lineLength;
            const endY = startY - Math.cos(angleRad) * lineLength; // negative Y = upward

            // Check ceiling
            if (endY <= PHYSICS_CONFIG.CEILING_Y) {
              line.destroy();
              growTimer.remove();
              return;
            }

            line.clear();
            line.lineStyle(4, 0x32CD32, 0.8);
            line.lineBetween(startX, startY, endX, endY);

            // Check collision with balls along the line tip
            for (let i = this.balls.length - 1; i >= 0; i--) {
              const ball = this.balls[i];
              const ballRadius = ball.getData('radius');
              const dist = Phaser.Math.Distance.Between(endX, endY, ball.x, ball.y);
              if (dist < ballRadius + 10) {
                this.handleBallHit(ball, i);
                line.destroy();
                growTimer.remove();
                return;
              }
            }
          },
        });

        // Safety timeout
        this.time.delayedCall(PROJECTILE_CONFIG.MAX_LIFETIME * 2, () => {
          line.destroy();
          growTimer.remove();
        });
      }
    }
  }

  private updateProjectile(delta: number): void {
    if (!this.isProjectileActive || this.projectileSprites.length === 0) return;

    if (!this.player) return;

    // Calculate current total height of projectile stack
    const firstSprite = this.projectileSprites[0];
    const currentHeight = firstSprite.y - (this.projectileSprites[this.projectileSprites.length - 1]?.y || firstSprite.y);

    // Update particle emitters to create trail effect along the projectile stack
    if (this.bubbleTrailEmitter || this.waterSplashEmitter) {
      // Emit from a random point along the projectile stack
      const stackX = firstSprite.x;
      const stackTop = this.projectileSprites[this.projectileSprites.length - 1]?.y || firstSprite.y;
      const stackBottom = firstSprite.y;
      
      // Random position along the stack for "energy trail" effect
      const randomAlongStack = stackTop + (Math.random() * (stackBottom - stackTop));
      
      if (this.bubbleTrailEmitter) {
        this.bubbleTrailEmitter.setPosition(stackX, randomAlongStack);
      }
      
      if (this.waterSplashEmitter) {
        this.waterSplashEmitter.setPosition(stackX, randomAlongStack);
      }
    }

    // If not at max height yet, spawn new projectile sprites upward
    if (!this.projectileAtMaxHeight) {
      const growthSpeed = PROJECTILE_CONFIG.GROWTH_SPEED; // pixels per second
      const growthAmount = (growthSpeed * delta) / 1000;

      // Calculate how much height we need to add
      const heightToAdd = Math.min(growthAmount, this.projectileTargetHeight - currentHeight);
      
      if (heightToAdd > 0) {
        // Calculate how many new sprites we need to spawn
        const spriteHeight = PROJECTILE_CONFIG.SPRITE_SIZE + PROJECTILE_CONFIG.SPRITE_SPACING;
        const lastSprite = this.projectileSprites[this.projectileSprites.length - 1];
        const newSpriteY = lastSprite.y - spriteHeight;
        
        // Only spawn if we have room and the new sprite would be above the ceiling
        if (newSpriteY >= PHYSICS_CONFIG.CEILING_Y && currentHeight < this.projectileTargetHeight) {
          // Create new projectile sprite above the last one (use stored X position for straight line)
          const newProjectile = this.add.image(this.projectileStartX, newSpriteY, ASSET_KEYS.ENERGY_PROJECTILE);
          newProjectile.setScale(1.0);
          newProjectile.setDepth(-1);
          newProjectile.setBlendMode(Phaser.BlendModes.ADD);
          newProjectile.setAlpha(0.9);
          
          this.projectileSprites.push(newProjectile);
          console.log(`✨ Spawned projectile sprite #${this.projectileSprites.length} at y=${newSpriteY}`);
        }
      }

      // Check if reached max height
      const newCurrentHeight = firstSprite.y - (this.projectileSprites[this.projectileSprites.length - 1]?.y || firstSprite.y);
      if (newCurrentHeight >= this.projectileTargetHeight || this.projectileSprites[this.projectileSprites.length - 1]?.y <= PHYSICS_CONFIG.CEILING_Y) {
        this.projectileAtMaxHeight = true;
        console.log('🔱 Projectile stack reached max height - holding briefly');

        // Hold at max height briefly, then destroy
        this.time.delayedCall(PROJECTILE_CONFIG.HOLD_DURATION, () => {
          this.destroyProjectile();
        });
      }
    }
  }

  private destroyProjectile(): void {
    // Destroy all projectile sprites
    if (this.projectileSprites.length > 0) {
      this.projectileSprites.forEach(sprite => sprite.destroy());
      this.projectileSprites = [];
      console.log('✨ All projectile sprites destroyed');
    }
    
    // Cancel safety timer if it exists
    if (this.projectileSafetyTimer) {
      this.projectileSafetyTimer.remove();
      this.projectileSafetyTimer = undefined;
    }
    
    // Legacy cleanup (for compatibility)
    if (this.projectile) {
      const middleGlow = this.projectile.getData('middleGlow');
      if (middleGlow) {
        middleGlow.destroy();
      }
      this.projectile.destroy();
      this.projectile = undefined;
    }
    
    if (this.projectileGlow) {
      this.projectileGlow.destroy();
      this.projectileGlow = undefined;
    }
    
    this.isProjectileActive = false;
    this.projectileAtMaxHeight = false;

    // Fade OUT the shooting aura glow (smooth power-down)
    if (this.playerGlow) {
      // Stop any ongoing fade-in tween
      if (this.glowFadeIn) {
        this.glowFadeIn.stop();
        this.glowFadeIn = undefined;
      }

      // Slower fade-out to invisible
      this.glowFadeOut = this.tweens.add({
        targets: this.playerGlow,
        outerStrength: 0,  // Fade back to invisible
        duration: 300,     // Slower, smoother fade-out (0.3 seconds)
        ease: 'Power2'
      });
      console.log('✨ Shooting aura glow fading OUT');
    }
    
    // Fade out and destroy trident power point glow
    if (this.tridentGlow) {
      this.tweens.add({
        targets: this.tridentGlow,
        alpha: 0,
        scale: 2,
        duration: 300,
        ease: 'Power2',
        onComplete: () => {
          if (this.tridentGlow) {
            this.tridentGlow.destroy();
            this.tridentGlow = undefined;
          }
        }
      });
      console.log('⚡ Trident power point glow fading OUT');
    }
    
    // Stop bubble trail effect
    if (this.bubbleTrailEmitter) {
      this.bubbleTrailEmitter.stop();
      console.log('💧 Bubble trail stopped');
    }
    
    // Stop water splash effect
    if (this.waterSplashEmitter) {
      this.waterSplashEmitter.stop();
      console.log('💦 Water splash stopped');
    }
  }

  /**
   * Spawn balls from stage configuration
   */
  private spawnBallsFromStage(): void {
    if (!this.stageConfig || !this.stageConfig.balls) {
      console.warn('⚠️ No balls defined in stage configuration, falling back to test balls');
      this.spawnTestBalls();
      return;
    }

    console.log(`🎯 Spawning ${this.stageConfig.balls.length} balls from stage configuration`);

    this.stageConfig.balls.forEach((ballConfig) => {
      // Get radius based on ball size
      const radius = BALL_SIZES[ballConfig.size].radius;
      
      // Get bubble type and powerup type from config (optional)
      const bubbleType = (ballConfig.bubbleType || 'REGULAR') as BubbleTypeName;
      const powerupType = ballConfig.powerupType as PowerupTypeName | undefined;

      // Spawn the ball
      const ball = this.spawnBall(
        ballConfig.position.x,
        ballConfig.position.y,
        radius,
        ballConfig.velocity.x,
        ballConfig.velocity.y,
        bubbleType,
        powerupType
      );

      // Store ball ID for tracking
      ball.setData('id', ballConfig.id);
      
      const ballTypeInfo = powerupType ? `POWERUP (${powerupType})` : bubbleType;
      console.log(`  ✓ Spawned ${ballConfig.size} ${ballTypeInfo} ball '${ballConfig.id}' at (${ballConfig.position.x}, ${ballConfig.position.y})`);
    });
    
    // Spawn obstacles if defined
    this.spawnObstaclesFromStage();
  }

  /**
   * Spawn obstacles from stage configuration
   */
  private spawnObstaclesFromStage(): void {
    if (!this.stageConfig || !this.stageConfig.obstacles || this.stageConfig.obstacles.length === 0) {
      console.log('ℹ️ No obstacles defined in stage configuration');
      return;
    }

    console.log(`🧱 Spawning ${this.stageConfig.obstacles.length} obstacles from stage configuration`);

    // Pang-style obstacles: thick colored border, sand/beige fill, corner rivets
    const worldNum = this.currentStage <= 2 ? 1 : this.currentStage <= 4 ? 2 : 3;
    // Border color per world — vibrant, thick, like Pang's cyan frames
    const frameColor = worldNum === 1 ? 0x4dd0e1 : worldNum === 2 ? 0x5EC8D4 : 0x7799BB;
    const frameHighlight = worldNum === 1 ? 0x88EEFF : worldNum === 2 ? 0x8DE8F0 : 0xAABBDD;
    // Inner fill — warm sand gradient
    const fillTop = worldNum === 1 ? 0x2a7a9a : worldNum === 2 ? 0xD4B87A : 0x6A7080;
    const fillBot = worldNum === 1 ? 0x1a5068 : worldNum === 2 ? 0xB89050 : 0x4A5060;
    const borderW = 4;
    const rivetR = 6;

    this.stageConfig.obstacles.forEach((obstacleConfig) => {
      const obstacleType = obstacleConfig.type as ObstacleTypeName;
      const config = getObstacleConfig(obstacleType);
      const ox = obstacleConfig.position.x;
      const oy = obstacleConfig.position.y;
      const ow = obstacleConfig.dimensions.width;
      const oh = obstacleConfig.dimensions.height;

      // Sprite-rendered obstacles (e.g. beach_sign) skip the procedural Pang
      // frame and just draw the actual asset image at the obstacle bounds.
      // Collision still uses the bounds rect like every other obstacle.
      if (config.renderMode === 'sprite') {
        if (this.textures.exists(config.sprite)) {
          // Soft drop shadow under the sprite
          const shadow = this.add.graphics();
          shadow.setDepth(9);
          shadow.fillStyle(0x000000, 0.22);
          shadow.fillEllipse(ox + ow / 2, oy + oh + 8, ow * 0.7, 18);

          const img = this.add.image(ox + ow / 2, oy + oh / 2, config.sprite);
          img.setDisplaySize(ow, oh);
          img.setDepth(10);
          if (obstacleConfig.mirrored) img.setFlipX(true);

          // Tag with bounds for collision (same shape as Pang-frame branch).
          img.setData('id', obstacleConfig.id);
          img.setData('obstacleType', obstacleType);
          img.setData('obstacleConfig', config);
          img.setData('bounds', { x: ox, y: oy, width: ow, height: oh });
          this.obstacles.push(img);

          console.log(`  ✓ Spawned (sprite) ${config.name} '${obstacleConfig.id}' at (${ox}, ${oy}) size ${ow}x${oh}`);
          return; // skip the Pang-frame fallthrough
        } else {
          console.warn(`⚠️ Sprite-mode obstacle '${obstacleType}' missing texture '${config.sprite}', falling back to frame render`);
        }
      }

      const obstacle = this.add.graphics();
      obstacle.setDepth(10);

      // Drop shadow
      obstacle.fillStyle(0x000000, 0.25);
      obstacle.fillRect(ox + 3, oy + 4, ow, oh);

      // Outer frame — thick colored border (the Pang look)
      obstacle.fillStyle(frameColor, 1);
      obstacle.fillRect(ox, oy, ow, oh);

      // Inner fill — sand gradient, inset by border width
      const ix = ox + borderW, iy = oy + borderW;
      const iw = ow - borderW * 2, ih = oh - borderW * 2;
      if (iw > 0 && ih > 0) {
        obstacle.fillGradientStyle(fillTop, fillTop, fillBot, fillBot, 1, 1, 1, 1);
        obstacle.fillRect(ix, iy, iw, ih);

        // Inner highlight — top edge shine
        obstacle.lineStyle(1, 0xFFFFFF, 0.2);
        obstacle.lineBetween(ix + 2, iy + 1, ix + iw - 2, iy + 1);

        // Inner shadow — bottom edge
        obstacle.lineStyle(1, 0x000000, 0.15);
        obstacle.lineBetween(ix + 2, iy + ih - 1, ix + iw - 2, iy + ih - 1);
      }

      // Outer highlight on frame — top and left
      obstacle.lineStyle(1, frameHighlight, 0.5);
      obstacle.lineBetween(ox + 1, oy + 1, ox + ow - 1, oy + 1);
      obstacle.lineBetween(ox + 1, oy + 1, ox + 1, oy + oh - 1);

      // Corner rivets — small filled circles at each corner
      const rivetInset = borderW + rivetR + 2;
      if (ow > rivetInset * 2 && oh > rivetInset * 2) {
        const corners = [
          [ox + rivetInset, oy + rivetInset],
          [ox + ow - rivetInset, oy + rivetInset],
          [ox + rivetInset, oy + oh - rivetInset],
          [ox + ow - rivetInset, oy + oh - rivetInset],
        ];
        for (const [cx, cy] of corners) {
          obstacle.fillStyle(frameHighlight, 0.7);
          obstacle.fillCircle(cx, cy, rivetR);
          obstacle.lineStyle(1.5, frameColor, 0.9);
          obstacle.strokeCircle(cx, cy, rivetR);
          // Rivet shine dot
          obstacle.fillStyle(0xFFFFFF, 0.4);
          obstacle.fillCircle(cx - 1.5, cy - 1.5, 2);
        }
      }

      // Metallic shimmer sweep — masked to obstacle bounds
      const shimmerGfx = this.add.graphics().setDepth(11);
      shimmerGfx.setBlendMode(Phaser.BlendModes.ADD);
      // Mask: only show shimmer inside obstacle area
      const maskShape = this.make.graphics({ x: 0, y: 0 });
      maskShape.fillStyle(0xFFFFFF);
      maskShape.fillRect(ox + borderW, oy + borderW, ow - borderW * 2, oh - borderW * 2);
      shimmerGfx.enableFilters();
      shimmerGfx.filters!.internal.addMask(maskShape);

      // Animate shimmer position
      const shimmerData = { pos: -0.2 };
      const delay = 2000 + Math.random() * 4000;
      this.time.delayedCall(delay, () => {
        this.tweens.add({
          targets: shimmerData,
          pos: { from: -0.2, to: 1.2 },
          duration: 1400,
          ease: 'Sine.easeInOut',
          repeat: -1,
          repeatDelay: 5000 + Math.random() * 3000,
          onUpdate: () => {
            shimmerGfx.clear();
            const sweepX = ox + ow * shimmerData.pos;
            const sweepW = 40;
            // Soft gradient shimmer band
            shimmerGfx.fillStyle(0xFFFFFF, 0.06);
            shimmerGfx.fillRect(sweepX - sweepW, oy, sweepW * 2, oh);
            shimmerGfx.fillStyle(0xFFFFFF, 0.12);
            shimmerGfx.fillRect(sweepX - sweepW / 2, oy, sweepW, oh);
            shimmerGfx.fillStyle(0xFFFFFF, 0.18);
            shimmerGfx.fillRect(sweepX - sweepW / 4, oy, sweepW / 2, oh);
          },
        });
      });

      // Store obstacle data for collision
      obstacle.setData('id', obstacleConfig.id);
      obstacle.setData('obstacleType', obstacleType);
      obstacle.setData('obstacleConfig', config);
      obstacle.setData('bounds', { x: ox, y: oy, width: ow, height: oh });

      this.obstacles.push(obstacle);

      console.log(`  ✓ Spawned ${config.name} '${obstacleConfig.id}' at (${ox}, ${oy}) size ${ow}x${oh}`);
    });
  }

  private spawnTestBalls(): void {
    // Spawn 2 large balls for testing (fallback when no stage config)
    const { radius } = BALL_SIZES.LARGE;
    this.spawnBall(500, 300, radius, 150, 0);
    this.spawnBall(1400, 300, radius, -150, 0);
    console.log('⚠️ Using test balls (no stage configuration loaded)');
  }

  private spawnBall(
    x: number, 
    y: number, 
    radius: number, 
    velocityX: number, 
    velocityY: number,
    bubbleType: BubbleTypeName = 'REGULAR',
    powerupType?: PowerupTypeName
  ): Phaser.GameObjects.Arc {
    // Get bubble configuration
    const bubbleConfig = getBubbleConfig(bubbleType);
    
    // Create ball as sprite using bubble texture
    const ballSprite = this.add.sprite(x, y, bubbleConfig.sprite);
    
    // Scale sprite to match desired radius
    // Uses global texture radius from bubble config
    const scale = radius / BUBBLE_TEXTURE_RADIUS;
    ballSprite.setScale(scale);
    
    // Calculate actual visual radius after scaling
    // The sprite's actual collision radius should match its visual size
    const actualRadius = (ballSprite.width * scale) / 2;
    
    // ── DUAL-TONE VIBRANT TINT (v2) ───────────────────────────────────
    // Use the innerColor from bubbleConfig (vibrant) instead of the muted tint.
    // Fallback to legacy tint when innerColor is missing.
    const innerTint = bubbleConfig.innerColor ? parseInt(bubbleConfig.innerColor, 16) : getBubbleTint(bubbleType);
    ballSprite.setTint(innerTint);

    // Set alpha from configuration
    ballSprite.setAlpha(bubbleConfig.alpha);

    // Set blend mode from configuration
    const blendMode = bubbleConfig.blendMode === 'ADD' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL;
    ballSprite.setBlendMode(blendMode);

    // Heavy FX glow for powerup / pollution bubbles only — regular bubbles
    // stay crisp without an ambient halo.
    if (bubbleConfig.glowEnabled && bubbleConfig.glowColor_fx) {
      const glowColor = parseInt(bubbleConfig.glowColor_fx, 16);
      const glowStrength = bubbleConfig.glowStrength || 4;
      ballSprite.enableFilters();
      const glowFX = ballSprite.filters!.internal.addGlow(glowColor, glowStrength, 0, 1, false, 0.1, 10);

      // Add pulsing animation if enabled
      if (bubbleConfig.glowPulse && glowFX) {
        const pulseSpeed = bubbleConfig.glowPulseSpeed || 800;
        const pulseMin = bubbleConfig.glowPulseMin || 2;
        const pulseMax = bubbleConfig.glowPulseMax || (glowStrength * 1.5);

        // Animate the glow strength
        this.tweens.add({
          targets: glowFX,
          outerStrength: pulseMax,
          duration: pulseSpeed,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          onStart: () => {
            glowFX.outerStrength = pulseMin;
          }
        });
      }
    }

    // ── IRIDESCENT HUE-CYCLE — pushes dead tints to "alive" ─────────────
    // Slowly cycles the tint between innerColor and rimColor in HSV space.
    // Enabled for powerup bubbles too so they shimmer between cyan and gold.
    if (bubbleConfig.innerColor && bubbleConfig.rimColor) {
      const inner = parseInt(bubbleConfig.innerColor, 16);
      const rim = parseInt(bubbleConfig.rimColor, 16);
      const iri = { t: 0 };
      this.tweens.add({
        targets: iri,
        t: 1,
        duration: 1800 + Math.random() * 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        onUpdate: () => {
          if (!ballSprite.active) return;
          const r1 = (inner >> 16) & 0xff, g1 = (inner >> 8) & 0xff, b1 = inner & 0xff;
          const r2 = (rim >> 16) & 0xff, g2 = (rim >> 8) & 0xff, b2 = rim & 0xff;
          const r = Math.round(r1 + (r2 - r1) * iri.t * 0.35);
          const g = Math.round(g1 + (g2 - g1) * iri.t * 0.35);
          const b = Math.round(b1 + (b2 - b1) * iri.t * 0.35);
          ballSprite.setTint((r << 16) | (g << 8) | b);
        },
      });
    }
    
    // Store physics data
    ballSprite.setData('velocityX', velocityX);
    ballSprite.setData('velocityY', velocityY);
    ballSprite.setData('radius', actualRadius); // Use actual visual radius for collision
    
    // Store bubble type and config for explosions
    ballSprite.setData('bubbleType', bubbleType);
    ballSprite.setData('bubbleConfig', bubbleConfig);
    
    // Store powerup data if this is a powerup ball
    if (powerupType) {
      ballSprite.setData('isPowerupBall', true);
      ballSprite.setData('powerupType', powerupType);
      // Initialize bee-like movement parameters for powerup balls
      ballSprite.setData('beePhase', Math.random() * Math.PI * 2); // Random starting phase
      ballSprite.setData('beeAmplitude', 100 + Math.random() * 50); // Horizontal movement range
      ballSprite.setData('beeSpeed', 2 + Math.random()); // Speed of sine wave
      ballSprite.setData('baseX', x); // Center point for sine wave
      // Set higher depth so PowerUp balls render in front of obstacles
      ballSprite.setDepth(100);

      // ── HERO SPARKLE HALO — 4 orbiting golden/cyan sparkles ──
      // Powerup bubbles are rare hero items, so they get an orbiting
      // sparkle ring on top of the pulsing glow + gold chromatic rim.
      const sparkleColors = [0xFFE66D, 0xFFD700, 0x00E5FF, 0xFFF4C2];
      const sparkles: Phaser.GameObjects.Arc[] = [];
      for (let s = 0; s < sparkleColors.length; s++) {
        const spark = this.add.circle(x, y, Math.max(2, radius * 0.09), sparkleColors[s], 0.95);
        spark.setBlendMode(Phaser.BlendModes.ADD);
        spark.setDepth(ballSprite.depth + 3);
        try {
          spark.enableFilters();
          spark.filters!.internal.addGlow(sparkleColors[s], 3, 0, 1, false, 0.1, 8);
        } catch {}
        sparkles.push(spark);
      }
      ballSprite.setData('sparkles', sparkles);

      const orbitData = { a: Math.random() * Math.PI * 2 };
      this.tweens.add({
        targets: orbitData,
        a: orbitData.a + Math.PI * 2,
        duration: 2200,
        repeat: -1,
        ease: 'Linear',
        onUpdate: () => {
          if (!ballSprite.active) return;
          const orbitR = radius * 1.18;
          for (let i = 0; i < sparkles.length; i++) {
            const phase = orbitData.a + (i * Math.PI / 2);
            const wobble = 1 + Math.sin(orbitData.a * 3 + i) * 0.12;
            sparkles[i].setPosition(
              ballSprite.x + Math.cos(phase) * orbitR * wobble,
              ballSprite.y + Math.sin(phase) * orbitR * wobble * 0.9,
            );
            const twinkle = 0.8 + Math.sin(orbitData.a * 4 + i * 1.7) * 0.4;
            sparkles[i].setScale(twinkle);
          }
        },
      });

      // ── EMBOSSED ICON OVERLAY (gold relief medallion in the ball's face) ──
      // The carrier ball is a shared gold orb; the icon overlay tells the
      // player WHICH powerup is in this bubble. Sized to ~50% of the ball's
      // display diameter (fits inside the ball's center inset panel).
      const embossKey = `gold_emboss_${String(powerupType).toLowerCase().replace(/_/g, '')}`;
      if (this.textures.exists(embossKey)) {
        const emboss = this.add.image(x, y, embossKey);
        emboss.setOrigin(0.5);
        const ballDispW = ballSprite.width * scale;
        const iconMaxDim = Math.max(emboss.width, emboss.height);
        emboss.setScale((ballDispW * 0.50) / iconMaxDim);
        emboss.setDepth(ballSprite.depth + 4); // above ball + sparkles
        ballSprite.setData('powerupEmboss', emboss);
      } else {
        console.warn(`⚠️ Missing embossed icon texture '${embossKey}' for ${powerupType}`);
      }

      console.log(`⚡ Spawned PowerUp Ball with ${powerupType} at (${x}, ${y})`);
    } else {
      ballSprite.setData('isPowerupBall', false);
      // Normal balls render at default depth (0)
      ballSprite.setDepth(50);
    }

    // Store original scale for squash/stretch
    const ballScale = radius / BUBBLE_TEXTURE_RADIUS;
    ballSprite.setData('origScaleX', ballScale);
    ballSprite.setData('origScaleY', ballScale);

    // === GLOSSY HIGHLIGHT (3D reflection spot) ===
    const highlight = this.add.circle(
      x - radius * 0.25,
      y - radius * 0.25,
      radius * 0.2,
      0xFFFFFF, 0.45
    );
    highlight.setBlendMode(Phaser.BlendModes.ADD);
    highlight.setDepth(ballSprite.depth + 1);
    ballSprite.setData('highlight', highlight);

    // Secondary tiny pinpoint highlight (upper-left) for glass weight
    const microHighlight = this.add.circle(
      x - radius * 0.45,
      y - radius * 0.45,
      radius * 0.06,
      0xFFFFFF, 0.85
    );
    microHighlight.setBlendMode(Phaser.BlendModes.ADD);
    microHighlight.setDepth(ballSprite.depth + 2);
    ballSprite.setData('microHighlight', microHighlight);

    // === CHROMATIC RIM ARC (Overwatch ability-orb DNA) ===
    // Three offset stroked circles in R/G/B for radial chromatic aberration.
    // Enabled for powerup bubbles too — the gold rim reads as "hero item".
    if (bubbleConfig.rimColor) {
      const rim = parseInt(bubbleConfig.rimColor, 16);
      const r = ((rim >> 16) & 0xff) / 255;
      const g = ((rim >> 8) & 0xff) / 255;
      const b = (rim & 0xff) / 255;
      // Shift each channel ±2% of radius for chromatic split
      const rOff = radius * 0.03;
      const chromaR = this.add.circle(x + rOff, y, radius, 0x000000, 0).setStrokeStyle(2, (Math.round(r * 255) << 16) | 0x000000, 0.85);
      const chromaG = this.add.circle(x, y - rOff * 0.6, radius, 0x000000, 0).setStrokeStyle(2, (Math.round(g * 255) << 8), 0.85);
      const chromaB = this.add.circle(x - rOff, y, radius, 0x000000, 0).setStrokeStyle(2, Math.round(b * 255), 0.85);
      chromaR.setBlendMode(Phaser.BlendModes.ADD).setDepth(ballSprite.depth + 1);
      chromaG.setBlendMode(Phaser.BlendModes.ADD).setDepth(ballSprite.depth + 1);
      chromaB.setBlendMode(Phaser.BlendModes.ADD).setDepth(ballSprite.depth + 1);
      ballSprite.setData('chromaR', chromaR);
      ballSprite.setData('chromaG', chromaG);
      ballSprite.setData('chromaB', chromaB);

      // Slowly rotate the chromatic-aberration offset for shimmering motion
      this.tweens.add({
        targets: { a: 0 },
        a: Math.PI * 2,
        duration: 3600,
        repeat: -1,
        onUpdate: (tw) => {
          if (!ballSprite.active) return;
          const a = (tw.targets[0] as any).a;
          const off = radius * 0.035;
          chromaR.setPosition(ballSprite.x + Math.cos(a) * off, ballSprite.y + Math.sin(a) * off);
          chromaG.setPosition(ballSprite.x + Math.cos(a + 2.1) * off, ballSprite.y + Math.sin(a + 2.1) * off);
          chromaB.setPosition(ballSprite.x + Math.cos(a + 4.2) * off, ballSprite.y + Math.sin(a + 4.2) * off);
        },
      });
    }

    // ── EDUCATIONAL ITEM OVERLAY (e.g. plastic bag) ──────────────────────
    // If this stage has an educational.itemTheme AND the bubble isn't a
    // powerup/pollution special, roll against itemFrequency to decide if
    // this bubble carries the themed item. Child bubbles inherit the flag
    // separately in handleBallHit().
    this.maybeAttachEducationalItem(ballSprite, radius, !!powerupType, bubbleConfig);

    console.log(`🫧 Spawned ${bubbleConfig.name} at (${x}, ${y}), radius: ${radius}`);

    this.balls.push(ballSprite as any);
    this.bubbleSpawnsThisStage += 1;
    this.updateStormSkyProgress();
    return ballSprite as any;
  }

  /**
   * Recompute the storm-sky overlay opacity from current pop/spawn counters.
   * Progress = pops / max(1, spawns); each call tweens alpha smoothly so an
   * individual bubble pop produces a small, satisfying reveal rather than
   * a hard step. Cheap no-op when stormSky isn't active (non-ship stages).
   */
  private updateStormSkyProgress(): void {
    if (!this.stormSky) return;
    const denom = Math.max(1, this.bubbleSpawnsThisStage);
    const progress = Math.min(1, this.bubblePopsThisStage / denom);
    this.stormSky.setProgress(progress);
  }

  /**
   * Attach the themed item sprite (plastic bag, fishing net, etc.) to the
   * given bubble if the current stage's educational config calls for it.
   *
   * - Rolls once at spawn time against educational.itemTheme.frequency.
   * - Does NOT attach to powerup or pollution bubbles (those already carry
   *   strong visual affordances and adding a bag would muddle them).
   * - The overlay sprite is parented to the ball via data; the ball update
   *   loop keeps its position in sync, and destroyBallChildren() removes it.
   */
  private maybeAttachEducationalItem(
    ball: Phaser.GameObjects.Sprite,
    radius: number,
    isPowerup: boolean,
    bubbleConfig: any,
  ): void {
    const theme = this.stageConfig?.educational?.itemTheme;
    if (!theme || !theme.itemKey) {
      return;
    }
    if (isPowerup || (bubbleConfig as any)?.isPollution) return;

    // Pick the item key for this ball.
    //
    // Visual unification: every bubble size uses the SAME full-bag sprite —
    // the LARGE-size key from itemByBallSize — just scaled down for smaller
    // bubbles. Previously MEDIUM/SMALL got "scraps" variants; user wants the
    // same intact bag on all sizes so the read is consistent.
    // getBubbleSizeFromRadius returns lowercase ('large'), JSON keys are
    // uppercase ('LARGE'). Normalize to uppercase for the lookup.
    const sizeTier = getBubbleSizeFromRadius(radius).toUpperCase() as 'LARGE' | 'MEDIUM' | 'SMALL';
    const keyExists = (k: string) => this.textures.exists(k) || this.anims.exists(k);

    // Prefer the LARGE mapping for ALL sizes (visual consistency). Fall back
    // to the size-specific mapping only if LARGE isn't configured, then to
    // the flat itemKey pool as a last resort.
    let chosenKey: string | undefined;
    const largeKey = theme.itemByBallSize?.LARGE;
    if (largeKey && keyExists(largeKey)) {
      chosenKey = largeKey;
    } else {
      const sizeMappedKey = theme.itemByBallSize?.[sizeTier];
      if (sizeMappedKey && keyExists(sizeMappedKey)) {
        chosenKey = sizeMappedKey;
      } else {
        const rawKeys = Array.isArray(theme.itemKey) ? theme.itemKey : [theme.itemKey];
        const keys = rawKeys.filter(keyExists);
        if (keys.length === 0) return;
        chosenKey = keys[Math.floor(Math.random() * keys.length)];
      }
    }
    if (!chosenKey) return;

    const frequency = theme.frequency ?? 0.65;
    // If pendingForceHasItem was set on the scene (via split inheritance in
    // handleBallHit), honor that; otherwise roll dice against the frequency.
    // LARGE bubbles always carry a bag — they anchor the narrative ("pop every
    // bubble carrying a plastic bag"); skipping them on a 30% roll breaks the
    // lesson. MEDIUM/SMALL keep the frequency roll so chain-splits stay varied.
    const forced = this.pendingForceHasItem;
    if (forced === false) return;
    if (forced === undefined) {
      if (sizeTier !== 'LARGE' && Math.random() > frequency) return;
    }

    // Animated variant? If the chosen key matches a registered anim, we use
    // the anim's first frame as the initial texture and play the loop —
    // motion is baked into the frames so we skip the tumble/breathing CSS
    // tweens that static variants use.
    const isAnimated = this.anims.exists(chosenKey);
    let firstFrameKey = chosenKey;
    if (isAnimated) {
      const anim = this.anims.get(chosenKey);
      firstFrameKey = (anim.frames[0]?.textureKey as string) || chosenKey;
    }

    // Item sprite — sized to ~72% of bubble diameter so it reads clearly but
    // doesn't mask the bubble's color/effects entirely.
    const item = this.add.sprite(ball.x, ball.y, firstFrameKey);
    const itemTex = this.textures.get(firstFrameKey).getSourceImage() as HTMLImageElement;
    const targetSize = radius * 1.35; // fills most of the bubble
    const baseScale = Math.min(targetSize / itemTex.width, targetSize / itemTex.height);
    item.setScale(baseScale);
    // Translucent so the bag reads as thin plastic, not opaque white.
    // Animated variant gets slightly more opacity since the motion already
    // sells "lightweight" — static variants lean harder on transparency.
    item.setAlpha(isAnimated ? 0.55 : 0.48);
    item.setDepth(ball.depth + 3);
    // Tint toward aqua-blue so the bag picks up the underwater palette
    // instead of reading as stark white against the bubble colors.
    item.setTint(0xD0EEFF);

    if (isAnimated) {
      // Start at a random frame so multiple bags on screen don't tumble in
      // lock-step — each bag has its own phase.
      item.play({ key: chosenKey, startFrame: Math.floor(Math.random() * 12) });
    } else {
      item.setAngle(Math.random() * 360); // random starting rotation so static bags don't all align

      // ── TUMBLE MOTION (static variants only) ────────────────────────
      // Full slow rotation — the bag tumbles in the water's current.
      const spinDir = Math.random() < 0.5 ? -1 : 1;
      const spinDuration = 9000 + Math.random() * 5000; // 9–14s per revolution
      this.tweens.add({
        targets: item,
        angle: `+=${spinDir * 360}`,
        duration: spinDuration,
        repeat: -1,
        ease: 'Linear',
      });

      // Scale breathing — the bag pulses gently as water pressure shifts.
      this.tweens.add({
        targets: item,
        scaleX: baseScale * 1.08,
        scaleY: baseScale * 0.94,
        duration: 1700 + Math.random() * 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Subtle positional drift within the bubble — offsets from ball center
    // via data fields the ball-position update loop reads. Gives each bag a
    // slight independent micro-movement so it doesn't look frozen to the ball.
    item.setData('offsetX', 0);
    item.setData('offsetY', 0);
    const driftAmp = Math.min(6, radius * 0.08);
    this.tweens.add({
      targets: { v: 0 },
      v: Math.PI * 2,
      duration: 3500 + Math.random() * 2000,
      repeat: -1,
      ease: 'Linear',
      onUpdate: (tw) => {
        if (!item.scene) return;
        const t = (tw.targets[0] as any).v;
        item.setData('offsetX', Math.cos(t) * driftAmp);
        item.setData('offsetY', Math.sin(t * 1.3) * driftAmp);
      },
    });

    ball.setData('hasItem', true);
    ball.setData('itemSprite', item);
    ball.setData('itemKey', chosenKey);
  }

  /**
   * Register Phaser animations for any animated educational items the
   * current stage might use. Idempotent — each anim is created only once
   * per scene, and missing textures (e.g. if a stage doesn't load the full
   * frame set) are silently skipped. Called from initializeScene() so bag
   * sprites are ready to play the loop as soon as they're instantiated.
   */
  private ensureEducationalItemAnimations(): void {
    // 12-frame Grok img2vid loops, paired with `${name}_drift_1..12` texture
    // keys loaded in BootScene. Each per-world popped item shares the same
    // 12@7fps cadence so they all read with the same calm bobbing rhythm.
    const driftAnims: Array<{ key: string; prefix: string }> = [
      { key: 'bag_drift',   prefix: 'bag_drift'   }, // World 1 — plastic bags
      { key: 'butt_drift',  prefix: 'butt_drift'  }, // World 2 — cigarette butts
      { key: 'storm_drift', prefix: 'storm_drift' }, // World 3 — storm clouds
    ];
    for (const { key, prefix } of driftAnims) {
      if (this.anims.exists(key)) continue;
      const frames: Phaser.Types.Animations.AnimationFrame[] = [];
      for (let i = 1; i <= 12; i++) {
        const k = `${prefix}_${i}`;
        if (this.textures.exists(k)) frames.push({ key: k });
      }
      if (frames.length >= 2) {
        this.anims.create({
          key,
          frames,
          frameRate: 7,
          repeat: -1,
          yoyo: false,
        });
      }
    }
  }

  /**
   * One-shot magic-dust burst played when a bag-carrying bubble pops.
   * Uses a throwaway ParticleEmitter so it self-destructs after the sparkle
   * lifetime, independent of the bubble's own pop particles.
   *
   * Also emits a second, heavier particle layer of torn bag pieces when
   * the stage's itemTheme.debrisVfx is configured — those fall with gravity,
   * tumble, and fade, reinforcing the "bag was just destroyed" read.
   */
  private emitEducationalDissolveVfx(x: number, y: number, radius: number): void {
    const theme = this.stageConfig?.educational?.itemTheme;
    if (!theme) return;

    // ── Layer 1: magic dust sparkle burst (additive, bright) ──────────
    const vfxKey = theme.dissolveVfx;
    if (vfxKey && this.textures.exists(vfxKey)) {
      const count = Math.max(10, Math.round(radius / 3));
      const emitter = this.add.particles(x, y, vfxKey, {
        speed: { min: 80, max: 260 },
        lifespan: { min: 520, max: 860 },
        scale: { start: 0.45, end: 0.02 },
        alpha: { start: 1, end: 0 },
        rotate: { min: 0, max: 360 },
        angle: { min: 0, max: 360 },
        blendMode: Phaser.BlendModes.ADD,
        quantity: count,
        emitting: false,
      });
      emitter.setDepth(200);
      emitter.explode(count, x, y);
      this.time.delayedCall(1000, () => {
        try { emitter.destroy(); } catch {}
      });
    }

    // ── Layer 2: torn bag pieces, falling with gravity (physical debris) ─
    // Subtle: only 3–5 pieces, smaller radius spread, longer lifetime so
    // they visibly tumble downward before fading. Normal blend so pieces
    // read as physical scraps, not glowing VFX.
    const debrisKey = theme.debrisVfx;
    if (debrisKey && this.textures.exists(debrisKey)) {
      const debrisCount = Phaser.Math.Between(3, 5);
      const debris = this.add.particles(x, y, debrisKey, {
        // Outward burst, narrower than the sparkle burst
        speed: { min: 60, max: 180 },
        // Tumble as they fall
        rotate: { start: 0, end: 360 },
        angle: { min: 230, max: 310 }, // bias upward/outward so they arc
        gravityY: 520,                  // fall under gravity
        lifespan: { min: 1400, max: 2200 },
        scale: {
          start: Math.max(0.35, radius / 200),
          end: Math.max(0.22, radius / 260),
        },
        alpha: { start: 0.95, end: 0 },
        quantity: debrisCount,
        emitting: false,
      });
      debris.setDepth(199);
      debris.explode(debrisCount, x, y);
      this.time.delayedCall(2400, () => {
        try { debris.destroy(); } catch {}
      });
    }
  }

  /**
   * Clean up all visual children attached to a bubble (highlight, micro-highlight,
   * chromatic rim arcs). Used on pop and on level teardown.
   */
  private destroyBallChildren(ball: Phaser.GameObjects.Arc | Phaser.GameObjects.Sprite): void {
    const keys = ['highlight', 'microHighlight', 'chromaR', 'chromaG', 'chromaB'];
    for (const k of keys) {
      const child = ball.getData(k);
      if (child && child.destroy) {
        try { child.destroy(); } catch {}
        ball.setData(k, null);
      }
    }
    // Orbiting sparkle halo (powerup bubbles only — stored as an array)
    const sparkles = ball.getData('sparkles') as Phaser.GameObjects.Arc[] | undefined;
    if (Array.isArray(sparkles)) {
      for (const s of sparkles) {
        if (s && (s as any).destroy) { try { s.destroy(); } catch {} }
      }
      ball.setData('sparkles', null);
    }
    // Educational item overlay (plastic bag / net / etc.)
    const itemSprite = ball.getData('itemSprite');
    if (itemSprite && itemSprite.destroy) {
      try { itemSprite.destroy(); } catch {}
      ball.setData('itemSprite', null);
    }
    // Embossed powerup icon overlay (gold relief medallion in powerup balls)
    const emboss = ball.getData('powerupEmboss');
    if (emboss && emboss.destroy) {
      try { emboss.destroy(); } catch {}
      ball.setData('powerupEmboss', null);
    }
  }

  private updateBalls(delta: number): void {
    const deltaSeconds = delta / 1000;
    this.balls.forEach((ball) => {
      const isPowerupBall = ball.getData('isPowerupBall');
      
      // PowerUp balls have special bee-like movement
      if (isPowerupBall) {
        const radius = ball.getData('radius');
        const baseX = ball.getData('baseX');
        const amplitude = ball.getData('beeAmplitude');
        const speed = ball.getData('beeSpeed');
        let phase = ball.getData('beePhase');
        
        // Update phase for sine wave movement
        phase += speed * deltaSeconds;
        ball.setData('beePhase', phase);
        
        // Calculate position using sine wave for horizontal (bee-like)
        ball.x = baseX + Math.sin(phase) * amplitude;
        
        // Slow vertical float with slight variation
        const verticalSpeed = 30 + Math.sin(phase * 0.5) * 15; // Varies between 15-45 pixels/sec
        ball.y += verticalSpeed * deltaSeconds * (this.isBallsFrozen ? 0 : 1);
        
        // Keep in upper half of screen (float back up if goes too low)
        const screenMidY = GAME_CONFIG.HEIGHT / 2;
        if (ball.y > screenMidY) {
          ball.y -= 50 * deltaSeconds; // Float back up
        }
        
        // Keep away from ceiling
        if (ball.y - radius < PHYSICS_CONFIG.CEILING_Y + 50) {
          ball.y = PHYSICS_CONFIG.CEILING_Y + 50 + radius;
        }
        
        // Bounce off walls
        if (ball.x - radius < PHYSICS_CONFIG.WALL_LEFT || ball.x + radius > PHYSICS_CONFIG.WALL_RIGHT) {
          // Reverse baseX direction
          const currentOffset = ball.x - baseX;
          ball.setData('baseX', ball.x - currentOffset);
        }
      } else {
        // Normal ball physics
        let velocityX = ball.getData('velocityX');
        let velocityY = ball.getData('velocityY');
        const radius = ball.getData('radius');

        // Skip physics if balls are frozen
        if (this.isBallsFrozen) {
          return;
        }

        // Apply gravity
        velocityY += PHYSICS_CONFIG.GRAVITY * deltaSeconds;

        // Update position
        ball.x += velocityX * deltaSeconds;
        ball.y += velocityY * deltaSeconds;

        // Check obstacle collisions
        this.obstacles.forEach((obstacle) => {
          const bounds = obstacle.getData('bounds');
          const config = obstacle.getData('obstacleConfig');
          
          if (!config.ballBounce) return; // Skip if obstacle doesn't bounce balls
          
          // Check if ball intersects with obstacle
          const ballLeft = ball.x - radius;
          const ballRight = ball.x + radius;
          const ballTop = ball.y - radius;
          const ballBottom = ball.y + radius;
          
          if (ballRight > bounds.x && ballLeft < bounds.x + bounds.width &&
              ballBottom > bounds.y && ballTop < bounds.y + bounds.height) {
            
            // Determine which side of the obstacle was hit
            const overlapLeft = ballRight - bounds.x;
            const overlapRight = (bounds.x + bounds.width) - ballLeft;
            const overlapTop = ballBottom - bounds.y;
            const overlapBottom = (bounds.y + bounds.height) - ballTop;
            
            const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);
            
            if (minOverlap === overlapLeft) {
              // Hit from left
              ball.x = bounds.x - radius;
              velocityX = -Math.abs(velocityX);
            } else if (minOverlap === overlapRight) {
              // Hit from right
              ball.x = bounds.x + bounds.width + radius;
              velocityX = Math.abs(velocityX);
            } else if (minOverlap === overlapTop) {
              // Hit from top
              ball.y = bounds.y - radius;
              velocityY = -Math.abs(velocityY) * PHYSICS_CONFIG.BOUNCE_DAMPENING;
            } else if (minOverlap === overlapBottom) {
              // Hit from bottom
              ball.y = bounds.y + bounds.height + radius;
              velocityY = Math.abs(velocityY);
            }
          }
        });

        // Bounce off walls
        if (ball.x - radius < PHYSICS_CONFIG.WALL_LEFT) {
          ball.x = PHYSICS_CONFIG.WALL_LEFT + radius;
          velocityX = Math.abs(velocityX);
        } else if (ball.x + radius > PHYSICS_CONFIG.WALL_RIGHT) {
          ball.x = PHYSICS_CONFIG.WALL_RIGHT - radius;
          velocityX = -Math.abs(velocityX);
        }
        
        // ── Anti-camp movement: every ball has a PERSONALITY so they don't all
        // do the same thing. Some hunt the player, some hold the left flank,
        // some hold the right, some wander the full width. Mixed together there
        // is no safe spot to stand and the screen stays lively. The behaviour is
        // picked once, round-robin, the first time we see each ball (split
        // children get their own, so variety grows as bubbles break apart). All
        // steering is capped so nothing speeds past the normal bubble pace.
        let behavior = ball.getData('behavior') as string | undefined;
        if (!behavior) {
          const order = ['wander', 'leftFlank', 'hunter', 'rightFlank'];
          behavior = order[this.ballBehaviorCounter % order.length];
          this.ballBehaviorCounter++;
          ball.setData('behavior', behavior);
          ball.setData('wanderPhase', Math.random() * Math.PI * 2);
        }

        const pullAccel = 240; // px/s^2 steering force
        const pullCap = 320;   // steering never pushes horizontal speed past this
        let targetX: number | null = null;
        if (behavior === 'hunter') {
          targetX = this.player ? this.player.x : null;          // come at the player
        } else if (behavior === 'leftFlank') {
          targetX = PHYSICS_CONFIG.WALL_LEFT + 330;              // patrol the left side
        } else if (behavior === 'rightFlank') {
          targetX = PHYSICS_CONFIG.WALL_RIGHT - 330;             // patrol the right side
        } else {
          // wander: a slow full-width sweep that ignores the player
          const t = this.time.now / 1000;
          const phase = (ball.getData('wanderPhase') as number) || 0;
          const mid = (PHYSICS_CONFIG.WALL_LEFT + PHYSICS_CONFIG.WALL_RIGHT) / 2;
          targetX = mid + Math.sin(t * 0.7 + phase) * 720;
        }
        if (targetX !== null) {
          const dir = (targetX - ball.x) >= 0 ? 1 : -1;
          if (Math.sign(velocityX) !== dir || Math.abs(velocityX) < pullCap) {
            velocityX += dir * pullAccel * deltaSeconds;
          }
        }

        // Bounce off ground with minimum height enforcement
        if (ball.y + radius > PHYSICS_CONFIG.GROUND_Y) {
          ball.y = PHYSICS_CONFIG.GROUND_Y - radius;
          velocityY = -Math.abs(velocityY) * PHYSICS_CONFIG.BOUNCE_DAMPENING;

          // === SQUASH on ground hit (subtle, snappy) ===
          const origScaleX = ball.getData('origScaleX') || ball.scaleX;
          const origScaleY = ball.getData('origScaleY') || ball.scaleY;
          ball.setScale(origScaleX * 1.12, origScaleY * 0.88); // Gentle squash
          this.tweens.add({
            targets: ball,
            scaleX: origScaleX,
            scaleY: origScaleY,
            duration: 150,
            ease: 'Sine.easeOut',
          });

          // === GROUND RIPPLE RING (water splash) ===
          const ripple = this.add.circle(ball.x, PHYSICS_CONFIG.GROUND_Y, 10, 0xFFFFFF, 0);
          ripple.setStrokeStyle(2, 0xAADDFF, 0.4);
          ripple.setDepth(3);
          this.tweens.add({
            targets: ripple,
            scale: radius / 10 * 2,
            alpha: 0,
            duration: 400,
            ease: 'Power2',
            onComplete: () => ripple.destroy(),
          });

          // Bounce dust puff
          if (this.particleEmitter) {
            this.particleEmitter.setParticleTint(0xCCBB99);
            this.particleEmitter.emitParticleAt(ball.x, PHYSICS_CONFIG.GROUND_Y, 4);
          }

          // Ensure minimum bounce height (Pang compatibility)
          const minVelocityY = -Math.sqrt(2 * PHYSICS_CONFIG.GRAVITY * (PHYSICS_CONFIG.GROUND_Y - PHYSICS_CONFIG.MIN_BOUNCE_HEIGHT - radius));
          
          // If bounce is too weak, boost it to minimum
          if (velocityY > minVelocityY) {
            velocityY = minVelocityY;
          }
        }

        // Bounce off ceiling
        if (ball.y - radius < PHYSICS_CONFIG.CEILING_Y) {
          ball.y = PHYSICS_CONFIG.CEILING_Y + radius;
          velocityY = Math.abs(velocityY);
        }
        
        // Check if ball is stuck (very low velocity) - give it a push
        const MIN_VELOCITY = 80;
        const totalVelocity = Math.sqrt(velocityX * velocityX + velocityY * velocityY);
        if (totalVelocity < MIN_VELOCITY) {
          console.log(`⚠️ Ball stuck with low velocity (${totalVelocity.toFixed(0)}), giving it a push`);
          
          // Give a random horizontal kick if too slow; each ball's behaviour
          // steering will redirect it over the next frames.
          if (Math.abs(velocityX) < 50) {
            velocityX = (Math.random() > 0.5 ? 1 : -1) * (150 + Math.random() * 100);
          }
          
          // Give upward velocity if too slow vertically
          if (Math.abs(velocityY) < 50) {
            velocityY = -(200 + Math.random() * 100);
          }
        }

        // Update velocities
        ball.setData('velocityX', velocityX);
        ball.setData('velocityY', velocityY);

        // === GENTLE VELOCITY-BASED STRETCH (very subtle, max 5%) ===
        const origSX = ball.getData('origScaleX') || ball.scaleX;
        const origSY = ball.getData('origScaleY') || ball.scaleY;
        // Only stretch when moving fast, and very subtly
        const speed = Math.sqrt(velocityX * velocityX + velocityY * velocityY);
        if (speed > 400) {
          const stretchAmt = Math.min((speed - 400) / 3000, 0.05); // Max 5%
          const absVY = Math.abs(velocityY);
          const absVX = Math.abs(velocityX);
          if (absVY > absVX * 1.5) {
            ball.setScale(origSX * (1 - stretchAmt * 0.4), origSY * (1 + stretchAmt));
          } else if (absVX > absVY * 1.5) {
            ball.setScale(origSX * (1 + stretchAmt), origSY * (1 - stretchAmt * 0.4));
          } else {
            // Lerp back to circle when not clearly directional
            ball.setScale(
              ball.scaleX + (origSX - ball.scaleX) * 0.15,
              ball.scaleY + (origSY - ball.scaleY) * 0.15
            );
          }
        } else {
          // Smoothly return to perfect circle when slow
          ball.setScale(
            ball.scaleX + (origSX - ball.scaleX) * 0.1,
            ball.scaleY + (origSY - ball.scaleY) * 0.1
          );
        }

      }

      // === SYNC OVERLAYS TO BALL POSITION (every ball, every frame) ─────
      // Highlight, micro-highlight, embossed powerup-icon, and educational-
      // item overlays MUST track the ball's current position regardless of
      // whether the ball is on the bee-sine-wave (powerup) path or normal
      // physics. Previously these lived inside the `else` (physics) branch,
      // which left powerup-ball overlays frozen at spawn while the ball
      // flew off — the emboss appeared to "float randomly" detached from
      // the ball. Hoisting them here glues every overlay to its ball.
      const radiusForOverlays = ball.getData('radius') as number;

      const highlight = ball.getData('highlight') as Phaser.GameObjects.Arc | undefined;
      if (highlight && !highlight.scene) {
        ball.setData('highlight', null);
      } else if (highlight) {
        highlight.setPosition(ball.x - radiusForOverlays * 0.25, ball.y - radiusForOverlays * 0.25);
      }

      const micro = ball.getData('microHighlight') as Phaser.GameObjects.Arc | undefined;
      if (micro && !micro.scene) {
        ball.setData('microHighlight', null);
      } else if (micro) {
        micro.setPosition(ball.x - radiusForOverlays * 0.45, ball.y - radiusForOverlays * 0.45);
      }

      const emboss = ball.getData('powerupEmboss') as Phaser.GameObjects.Image | undefined;
      if (emboss && !emboss.scene) {
        ball.setData('powerupEmboss', null);
      } else if (emboss) {
        emboss.setPosition(ball.x, ball.y);
      }

      const itemSprite = ball.getData('itemSprite') as Phaser.GameObjects.Sprite | undefined;
      if (itemSprite && !itemSprite.scene) {
        ball.setData('itemSprite', null);
      } else if (itemSprite) {
        const ox = (itemSprite.getData('offsetX') as number) || 0;
        const oy = (itemSprite.getData('offsetY') as number) || 0;
        itemSprite.setPosition(ball.x + ox, ball.y + oy);
      }
    });
  }

  private checkCollisions(): void {
    // Check projectile-obstacle collisions
    if (this.isProjectileActive && this.projectileSprites.length > 0) {
      for (const obstacle of this.obstacles) {
        const config = obstacle.getData('obstacleConfig');
        
        if (!config.blockProjectiles) continue; // Skip if obstacle doesn't block projectiles
        
        const bounds = obstacle.getData('bounds');
        
        // Check if any projectile sprite hits the obstacle
        for (const sprite of this.projectileSprites) {
          const spriteHalfWidth = (PROJECTILE_CONFIG.SPRITE_SIZE * sprite.scaleX) / 2;
          const spriteHalfHeight = (PROJECTILE_CONFIG.SPRITE_SIZE * sprite.scaleY) / 2;
          const spriteLeft = sprite.x - spriteHalfWidth;
          const spriteRight = sprite.x + spriteHalfWidth;
          const spriteTop = sprite.y - spriteHalfHeight;
          const spriteBottom = sprite.y + spriteHalfHeight;
          
          // Check intersection with obstacle
          if (spriteRight > bounds.x && spriteLeft < bounds.x + bounds.width &&
              spriteBottom > bounds.y && spriteTop < bounds.y + bounds.height) {
            console.log('🛑 Projectile blocked by obstacle!');
            this.destroyProjectile();
            break;
          }
        }
        
        if (this.projectileSprites.length === 0) break; // Projectile was destroyed
      }
    }
    
    // Check projectile-ball collisions (sprite stack intersection)
    if (this.isProjectileActive && this.projectileSprites.length > 0) {
      // Iterate backwards to safely remove elements while iterating
      for (let i = this.balls.length - 1; i >= 0; i--) {
        const ball = this.balls[i];
        const ballRadius = ball.getData('radius');
        
        // Check if projectile still exists (handleBallHit destroys it)
        if (this.projectileSprites.length === 0) {
          break; // Projectile was destroyed, exit loop
        }
        
        // Check collision with any projectile sprite in the stack
        let hitDetected = false;
        for (const sprite of this.projectileSprites) {
          // Check horizontal distance from sprite center
          const distanceFromSprite = Math.abs(ball.x - sprite.x);
          const spriteHalfWidth = (PROJECTILE_CONFIG.SPRITE_SIZE * sprite.scaleX) / 2;
          
          // Check if ball's center is within sprite width + ball radius
          if (distanceFromSprite < spriteHalfWidth + ballRadius) {
            // Ball is horizontally aligned with sprite, check vertical bounds
            const spriteHalfHeight = (PROJECTILE_CONFIG.SPRITE_SIZE * sprite.scaleY) / 2;
            const spriteTop = sprite.y - spriteHalfHeight;
            const spriteBottom = sprite.y + spriteHalfHeight;
            
            // Check if ball overlaps with sprite vertically
            const collisionBuffer = ballRadius * 0.3; // Buffer to catch collisions
            if (ball.y + ballRadius + collisionBuffer > spriteTop && ball.y - ballRadius - collisionBuffer < spriteBottom) {
              // Hit!
              console.log('💥 Ball hit by energy projectile!');
              hitDetected = true;
              break;
            }
          }
        }
        
        // Time Freeze can stop a bubble below the player's centre, where the
        // upward beam never reaches (the beam is anchored at the firing point
        // and only grows up). While frozen, extend the beam's reach straight
        // down to the floor for bubbles aligned with it, so they can still pop.
        if (!hitDetected && this.isBallsFrozen && this.projectileSprites.length > 0) {
          const bottom = this.projectileSprites[0];
          const beamHalfWidth = (PROJECTILE_CONFIG.SPRITE_SIZE * bottom.scaleX) / 2;
          if (Math.abs(ball.x - bottom.x) < beamHalfWidth + ballRadius &&
              ball.y >= bottom.y &&
              ball.y - ballRadius <= PHYSICS_CONFIG.GROUND_Y) {
            hitDetected = true;
          }
        }

        if (hitDetected) {
          this.handleBallHit(ball, i);
          // handleBallHit() destroys the projectile, so break out of loop
          break;
        }
      }
    }

    // Check player-ball collisions - SKIP IN DESIGN MODE, if invincible, or if balls are frozen
    if (this.player && !this.isGameOver && !designModeState.isDesignMode() && !this.isInvincible && !this.isBallsFrozen) {
      for (const ball of this.balls) {
        const playerRadius = (PLAYER_CONFIG.WIDTH * PLAYER_CONFIG.SCALE * 0.5) / 2;
        const ballRadius = ball.getData('radius');
        const distance = Phaser.Math.Distance.Between(
          this.player.x,
          this.player.y,
          ball.x,
          ball.y
        );

        if (distance < playerRadius + ballRadius) {
          const impact = ImpactSystem.get(this);
          // Check shield first
          if (this.hasShield) {
            this.popShield();
            this.startInvincibility();
            return;
          }

          // ── ENERGY-BASED DAMAGE: drain first, lose life only when depleted ──
          const lifeLost = this.handlePlayerHit(ENERGY_CONFIG.DAMAGE_PER_HIT);

          // Common feedback regardless of life loss
          AudioChoreographer.get(this).playVoice('ow');
          this.playReactiveAnim(ANIMATION_KEYS.PLAYER.FLINCH, 500);

          if (!lifeLost) {
            // Took damage, still have energy — show normal hit flair
            const hitQuip = this.getEventQuip('hit');
            this.showCharacterQuip(hitQuip.text, hitQuip.color);
            // If down to 1 life, shift to angry idle
            if (this.lives === 1) {
              this.time.delayedCall(550, () => {
                this.playReactiveAnim(ANIMATION_KEYS.PLAYER.ANGRY, 2000);
              });
            }
          }

          console.log(`💔 Hit! Lives: ${this.lives} | lifeLost: ${lifeLost}`);

          // Even if no life lost, still do the small kick (handlePlayerHit does bigger one on death)
          if (!lifeLost) {
            impact.fire(this.player!.x, this.player!.y, 'MEDIUM', 0xFF3355);
          }

          return;
        }
      }
    }

    // Update shield position (hex pattern + orbiting sparks)
    this.updateShieldPosition();
  }

  private handleBallHit(ball: Phaser.GameObjects.Arc, index: number): void {
    // Ignore if game is already over
    if (this.isGameOver) {
      console.log('⚠️ handleBallHit() called but game is over - ignoring');
      return;
    }
    
    // Ignore if ball is already being split (prevents harpoon overkill)
    if (this.ballsBeingSplit.has(ball)) {
      return;
    }

    // Storm-sky progress: each pop nudges the dark clouds toward clear.
    this.bubblePopsThisStage += 1;
    this.updateStormSkyProgress();

    const radius = ball.getData('radius');
    const velocityX = ball.getData('velocityX');
    const ballX = ball.x;
    const ballY = ball.y;
    const bubbleType = ball.getData('bubbleType') || 'REGULAR';
    const isPowerupBall = ball.getData('isPowerupBall');
    const powerupType = ball.getData('powerupType');
    const hasItem = !!ball.getData('hasItem');

    // Store ball hit position for floating score text
    this.lastBallHitX = ballX;
    this.lastBallHitY = ballY;

    // INSTANTLY destroy harpoon on hit
    this.destroyProjectile();

    // === AAA: POLLUTION BUBBLE — spawn toxic ground pool when popped ===
    // Only spawn pool for the FIRST pop (large/medium), not child splits
    const bubbleConfigData = getBubbleConfig(bubbleType);
    if ((bubbleConfigData as any).isPollution && radius >= BALL_SIZES.SMALL.radius) {
      // Delay pool spawn slightly so it doesn't interfere with hit-freeze
      this.time.delayedCall(200, () => {
        if (!this.isGameOver) this.spawnPollutionPool(ballX);
      });
    }

    // If this is a powerup ball, drop the powerup and destroy the ball
    if (isPowerupBall && powerupType) {
      this.dropPowerup(ballX, ballY, powerupType);
      // Award points for hitting powerup ball
      this.events.emit('score-changed', 500);
      
      // Remove from balls array
      this.balls.splice(index, 1);
      
      // Pop animation for powerup ball
      const explosionColors = getExplosionColors(bubbleType);
      this.emitBallPopParticles(ballX, ballY, radius, explosionColors);
      this.tweens.add({
        targets: ball,
        alpha: 0,
        scale: 0,
        duration: 200,
        ease: 'Back.easeIn',
        onComplete: () => {
          this.destroyBallChildren(ball);
          ball.destroy();
          this.ballsBeingSplit.delete(ball);
          // Check if this was the last ball (win condition)
          this.scheduleWinCheck();
        }
      });
      return; // Don't split powerup balls
    }

    // Mark ball as being split
    this.ballsBeingSplit.add(ball);

    // Remove the ball from active balls array
    this.balls.splice(index, 1);

    // Camera shake now handled by ImpactSystem.fire() in emitBallPopParticles

    // ANIMATION PHASE 1: Grow ball bigger (anticipation frame)
    // Calculate grow scale based on current ball scale (proportional to size)
    const currentScale = ball.scaleX; // Get current scale
    const growScale = currentScale * BUBBLE_GROW_MULTIPLIER;
    
    this.tweens.add({
      targets: ball,
      scale: growScale,
      duration: 100, // 0.1 seconds to grow
      ease: 'Back.easeOut',
      onComplete: () => {
        // ANIMATION PHASE 2: Pop and split
        // Shrink proportionally using configured multiplier
        const popScale = currentScale * BUBBLE_POP_MULTIPLIER;
        
        this.tweens.add({
          targets: ball,
          alpha: 0,
          scale: popScale,
          duration: 100, // 0.1 seconds to pop
          ease: 'Back.easeIn',
          onStart: () => {
            // Emit particles at the start of pop animation with bubble colors
            const explosionColors = getExplosionColors(bubbleType);
            this.emitBallPopParticles(ballX, ballY, radius, explosionColors);

            // Educational dissolve: on a bag bubble, emit an extra magic-dust
            // burst and increment the "bags cleared" counter. The dust is a
            // particle emitter so it drifts + fades naturally.
            if (hasItem) {
              this.emitEducationalDissolveVfx(ballX, ballY, radius);
              this.bagsCleared += 1;
              const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
              if (uiScene?.updateBagsCleared) {
                uiScene.updateBagsCleared(this.bagsCleared);
              }
            }

            // ── TRIPLE-AAA: Pitch-arpeggio pop + sub-bass thud layer ──
            const bubbleSize = getBubbleSizeFromRadius(radius);
            const soundConfig = getBubbleSound(bubbleSize);
            const audioManager = AudioManager.getInstance();
            audioManager.playBubblePop(this, soundConfig.key, ballX, bubbleSize, { volume: soundConfig.volume });
          },
          onComplete: () => {
            // Destroy all child overlays + ball
            this.destroyBallChildren(ball);
            ball.destroy();
            this.ballsBeingSplit.delete(ball);

            // ANIMATION PHASE 3: Spawn new balls (delayed)
            // Split into smaller balls if not the smallest
            // Use 0.6 ratio for clear visual size difference
            const splitRatio = 0.6;
            const newRadius = radius * splitRatio;
            
            // Only split if new size would be significantly larger than SMALL
            if (newRadius > BALL_SIZES.SMALL.radius * 1.2) {
              // Speed multiplier decreases for smaller balls (3rd level and below are slower)
              // Large -> Medium: 1.8x speed
              // Medium -> Small: 1.0x speed (same speed as parent)
              // Small -> Tiny: 0.7x speed (much slower)
              const speedMultiplier = newRadius > BALL_SIZES.MEDIUM.radius ? 1.8 : 
                                     newRadius > BALL_SIZES.SMALL.radius ? 1.0 : 0.7;
              const newSpeed = Math.abs(velocityX) * speedMultiplier;
              
              // Umbrella explosion: balls burst upward and outward
              // Smaller balls have much less upward velocity
              const upwardVelocity = newRadius > BALL_SIZES.MEDIUM.radius ? -400 : 
                                    newRadius > BALL_SIZES.SMALL.radius ? -280 : -200;
              
              // Spawn balls ABOVE the old ball position (gain height)
              const heightGain = radius * 0.8; // Spawn higher (80% vs 60%)
              const spawnY = ballY - heightGain;

              // Spawn two smaller balls closer together
              const spawnOffset = newRadius * 1.1; // 1.5x radius apart (closer than before)

              // Propagate the educational bag flag to children via a
              // data-only hint read by maybeAttachEducationalItem(): true
              // forces an item on both kids, false forces NO item on either.
              // undefined lets the normal frequency roll decide.
              const inheritHasItem = hasItem;

              // Spawn with larger initial size, then shrink to normal
              // Inherit bubble type from parent ball
              this.pendingForceHasItem = inheritHasItem ? true : undefined;
              const leftBall = this.spawnBall(ballX - spawnOffset, spawnY, newRadius, -newSpeed, upwardVelocity, bubbleType);
              this.pendingForceHasItem = inheritHasItem ? true : undefined;
              const rightBall = this.spawnBall(ballX + spawnOffset, spawnY, newRadius, newSpeed, upwardVelocity, bubbleType);
              this.pendingForceHasItem = undefined;
              
              // Calculate the correct normal scale for this ball size
              const normalScale = newRadius / BUBBLE_TEXTURE_RADIUS;
              
              // Start new balls at larger size (using configured grow multiplier)
              leftBall.setScale(normalScale * BUBBLE_GROW_MULTIPLIER);
              rightBall.setScale(normalScale * BUBBLE_GROW_MULTIPLIER);
              
              // Animate them shrinking to their correct normal size
              this.tweens.add({
                targets: [leftBall, rightBall],
                scale: normalScale, // Use correct scale for this ball size, not 1.0
                duration: 150,
                ease: 'Back.easeOut',
                onComplete: () => {
                  // Schedule win check AFTER spawn animation completes (only if game not over)
                  if (!this.isGameOver) {
                    this.scheduleWinCheck();
                  }
                }
              });
            } else {
              // No split, smallest ball - check win immediately
              this.scheduleWinCheck();
            }

            // Emit score event
            this.events.emit('ball-destroyed', { radius, score2x: this.isScore2xActive });

            // AAA: Character quip on last ball
            if (this.balls.length === 1 && this.ballsBeingSplit.size <= 1) {
              const quip = this.getEventQuip('last_ball');
              this.showCharacterQuip(quip.text, quip.color);
            }

            // (HUD bubble counter syncs every frame in update() now, so no
            // explicit updateBallCount() call needed here.)
          }
        });
      }
    });
  }

  /**
   * Pop flavor text — small floating word ("Pop!" / "Boom!" / "Saved!" /
   * "Nice!" / "Direct hit!") that appears just above the popped bubble.
   * Word is chosen randomly from the client's gaming-words brief; the
   * pool excludes "Direct hit!" until the player has built a small streak
   * (so it reads as an earned compliment, not noise on every pop).
   */
  private spawnPopFlavorText(x: number, y: number, multiplier: number): void {
    const FLAVOR_WORDS = ['Pop!', 'Boom!', 'Saved!', 'Nice!'];
    const FLAVOR_BIG = 'Direct hit!';
    // ~55% of pops show a flavor word so the channel doesn't oversaturate
    if (Math.random() > 0.55) return;
    const word = (multiplier >= 4 && Math.random() < 0.5)
      ? FLAVOR_BIG
      : FLAVOR_WORDS[Math.floor(Math.random() * FLAVOR_WORDS.length)];
    const txt = this.add.text(x, y - 90, word, {
      fontFamily: 'CF Asty Std, Arial Black, sans-serif',
      fontSize: '38px',
      color: '#FFF8C2',
      stroke: '#1A2840',
      strokeThickness: 4,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 3, color: '#000000', blur: 6, fill: true },
    }).setOrigin(0.5).setDepth(11_500).setAlpha(0).setScale(0.6);
    this.tweens.add({
      targets: txt,
      alpha: 1,
      scale: 1,
      duration: 110,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: txt,
      y: y - 160,
      alpha: 0,
      duration: 520,
      delay: 160,
      ease: 'Quad.easeOut',
      onComplete: () => txt.destroy(),
    });
  }

  /**
   * Show floating score text on bubble when destroyed
   * Based on Figma design: Jolly Lodger font, 94.4px, orange gradient fill, white stroke
   */
  private showFloatingScoreText(data: { points: number; multiplier: number | null; basePoints: number }): void {
    const { points, multiplier } = data;

    // Use last ball hit position
    const x = this.lastBallHitX;
    const y = this.lastBallHitY;

    // ── Client gaming-words pass: small flavor word above the score popup
    //    on every pop. Random pick from the brief; fades quickly so it
    //    reinforces the hit without cluttering the screen.
    this.spawnPopFlavorText(x, y, multiplier ?? 1);

    // ── TRIPLE-AAA: Floating +N score popup (Diablo/Overwatch style) ──
    try {
      ScorePopup.spawn(this, x, y, points, {
        combo: multiplier ?? 1,
        critical: (multiplier ?? 1) >= 4 || points >= 400,
      });
    } catch {}

    // AAA: Color-code by value tier
    const getPointColor = (pts: number): string => {
      if (pts >= 1600) return '#FF2222'; // Red for massive
      if (pts >= 800) return '#FF6600';  // Orange for big
      if (pts >= 400) return '#FFD700';  // Gold for medium
      return '#FFB84A';                   // Yellow for normal
    };

    // AAA: Size scales with value
    const getFontSize = (pts: number): string => {
      if (pts >= 1600) return '120px';
      if (pts >= 800) return '105px';
      if (pts >= 400) return '94px';
      return '80px';
    };

    // Create multiplier text (x2, x3, x4, x8) - only if multiplier > 1
    if (multiplier && multiplier > 1) {
      const multColor = multiplier >= 8 ? '#FF2222' : multiplier >= 4 ? '#FF6600' : '#FFD700';
      const multiplierText = this.add.text(
        x,
        y - 30,
        `x${multiplier}`,
        {
          fontFamily: 'CF Asty Std',
          fontSize: multiplier >= 8 ? '120px' : '94.4px',
          color: multColor,
          stroke: '#FFFFFF',
          strokeThickness: 6,
          letterSpacing: 2,
          shadow: { offsetX: 0, offsetY: 0, color: multColor, blur: multiplier >= 4 ? 15 : 0, fill: false },
        }
      );
      multiplierText.setOrigin(0.5);
      multiplierText.setDepth(1000);
      multiplierText.setAlpha(0);
      multiplierText.setScale(0.3);

      // AAA: Dramatic pop-in with rotation
      this.tweens.add({
        targets: multiplierText,
        alpha: 1,
        y: y - 90,
        scale: 1.2,
        angle: { from: -15, to: 0 },
        duration: 250,
        ease: 'Back.easeOut',
      });

      this.tweens.add({
        targets: multiplierText,
        alpha: 0,
        y: y - 150,
        scale: 0.8,
        duration: 350,
        delay: 450,
        ease: 'Power2',
        onComplete: () => multiplierText.destroy(),
      });

      // AAA: Multiplier burst ring
      if (multiplier >= 4) {
        const burstRing = this.add.circle(x, y - 60, 20, 0x000000, 0);
        burstRing.setStrokeStyle(2, parseInt(multColor.replace('#', '0x')), 0.5);
        burstRing.setBlendMode(Phaser.BlendModes.ADD);
        burstRing.setDepth(999);
        this.tweens.add({
          targets: burstRing,
          scale: 4,
          alpha: 0,
          duration: 400,
          ease: 'Power2',
          onComplete: () => burstRing.destroy(),
        });
      }
    }

    // Create points text (+200, +400, etc.)
    const pointColor = getPointColor(points);
    const fontSize = getFontSize(points);
    const pointsText = this.add.text(
      x,
      y + 10,
      `+${points}`,
      {
        fontFamily: 'CF Asty Std',
        fontSize: fontSize,
        color: pointColor,
        stroke: '#FFFFFF',
        strokeThickness: 6,
        letterSpacing: 2,
        shadow: points >= 800 ? { offsetX: 0, offsetY: 0, color: pointColor, blur: 12, fill: false } : undefined,
      }
    );
    pointsText.setOrigin(0.5);
    pointsText.setDepth(1000);
    pointsText.setAlpha(0);
    pointsText.setScale(0.5);

    // AAA: Bouncy entrance with slight randomization
    const offsetX = (Math.random() - 0.5) * 30; // Slight random spread
    this.tweens.add({
      targets: pointsText,
      alpha: 1,
      y: y - 20,
      x: x + offsetX,
      scale: points >= 800 ? 1.15 : 1.0,
      duration: 250,
      ease: 'Back.easeOut',
    });

    this.tweens.add({
      targets: pointsText,
      alpha: 0,
      y: y - 80,
      scale: 0.7,
      duration: 350,
      delay: 500,
      ease: 'Power2',
      onComplete: () => pointsText.destroy(),
    });
  }

  private scheduleWinCheck(): void {
    // Don't schedule if game is already over
    if (this.isGameOver) {
      console.log('⚠️ scheduleWinCheck() called but game is over - ignoring');
      return;
    }
    
    // Cancel any existing win check timer
    if (this.winCheckTimer) {
      this.winCheckTimer.remove();
      console.log('🔄 Cancelled previous win check timer');
    }

    // Schedule a new win check after a short delay (50ms to ensure all animations settle)
    this.winCheckTimer = this.time.delayedCall(50, () => {
      console.log(`🎯 Win check: balls=${this.balls.length}, splitting=${this.ballsBeingSplit.size}, gameOver=${this.isGameOver}`);
      if (this.balls.length === 0 && this.ballsBeingSplit.size === 0 && !this.isGameOver) {
        console.log('✅ Win condition met!');
        this.stageComplete();
      } else {
        console.log('⏭️  Win condition not met, continuing game');
      }
      this.winCheckTimer = undefined;
    });
  }

  private stageComplete(): void {
    // Prevent multiple calls
    if (this.isGameOver) {
      console.log('⚠️ stageComplete() called but already game over - ignoring');
      return;
    }

    // === AAA: ENDLESS MODE - spawn next wave ===
    if (this.isEndlessMode) {
      this.endlessWave++;
      this.endlessBallsPerWave = Math.min(2 + this.endlessWave, 8); // Cap at 8 balls

      // Show wave notification
      const { WIDTH, HEIGHT } = GAME_CONFIG;
      const waveText = this.add.text(WIDTH / 2, HEIGHT / 2, `WAVE ${this.endlessWave + 1}`, {
        fontFamily: 'CF Asty Std',
        fontSize: '100px',
        color: '#FFD700',
        stroke: '#000000',
        strokeThickness: 8,
        shadow: { offsetX: 0, offsetY: 0, color: '#FFD700', blur: 20, fill: false },
      });
      waveText.setOrigin(0.5);
      waveText.setDepth(10000);
      waveText.setAlpha(0);
      waveText.setScale(0.5);

      this.tweens.add({
        targets: waveText,
        alpha: 1, scale: 1.0,
        duration: 300,
        ease: 'Back.easeOut',
      });
      this.time.delayedCall(1200, () => {
        this.tweens.add({
          targets: waveText,
          alpha: 0, scale: 1.3,
          duration: 300,
          onComplete: () => waveText.destroy(),
        });
      });

      // Spawn new wave after brief pause
      this.time.delayedCall(1500, () => {
        const bubbleTypes: BubbleTypeName[] = ['REGULAR', 'CYAN', 'RED', 'GREEN', 'PURPLE', 'ORANGE', 'YELLOW'];
        for (let i = 0; i < this.endlessBallsPerWave; i++) {
          const x = 200 + Math.random() * (WIDTH - 400);
          const y = 200 + Math.random() * 200;
          const vx = (Math.random() - 0.5) * 300;
          const vy = 0;
          const type = bubbleTypes[Math.floor(Math.random() * bubbleTypes.length)];
          // Balls get slightly smaller in later waves
          const radiusMult = Math.max(0.6, 1.0 - this.endlessWave * 0.03);
          this.spawnBall(x, y, BALL_SIZES.LARGE.radius * radiusMult, vx, vy, type);
        }
        // Every 3 waves, spawn a powerup ball
        if (this.endlessWave % 3 === 2) {
          const powerups: PowerupTypeName[] = ['TIME_FREEZE', 'OCEAN_SHIELD', 'ECO_BLAST', 'RAPID_FIRE', 'EXTRA_LIFE'];
          const puType = powerups[Math.floor(Math.random() * powerups.length)];
          this.spawnBall(WIDTH / 2, 250, BALL_SIZES.MEDIUM.radius, 80, 0, 'POWERUP', puType);
        }
      });

      console.log(`🌊 ENDLESS MODE: Wave ${this.endlessWave + 1} - ${this.endlessBallsPerWave} balls`);
      return; // Don't trigger stage complete
    }

    console.log('🎉 Stage complete!');
    this.isGameOver = true;

    // Achievement triggers
    const achievements = AchievementManager.getInstance();
    achievements.setScene(this);
    if (this.lives === LIVES_CONFIG.MAX_LIVES) achievements.unlock('perfect_clear');
    if (this.currentStage === 2) achievements.unlock('world1_complete');
    if (this.currentStage === 4) achievements.unlock('world2_complete');
    if (this.currentStage === 6) achievements.unlock('world3_complete');

    // Reset time scale (in case slow-mo was active)
    this.time.timeScale = 1;
    this.cameras.main.zoomTo(1, 200);

    // Stop all animations immediately
    this.tweens.killAll();

    // Clear win check timer
    if (this.winCheckTimer) {
      this.winCheckTimer.remove();
      this.winCheckTimer = undefined;
    }

    // Clear balls being split
    this.ballsBeingSplit.clear();

    // Celebration particles!
    this.celebrateStageComplete();

    // In demo/auto-play mode
    if (this.isDemoMode) {
      if (this.isAutoPlay) {
        // Auto-play: continue to next stage after brief delay.
        // v4 NOTE: the previous setTimeout-chain (`stop UI → 50ms →
        // stop GAME → 50ms → start GAME`) caused the requestAnimationFrame
        // chain to drop on stage 2+ and the canvas went black. Driving the
        // transition off the scene's own clock + a single `start()` call
        // (which internally fires SHUTDOWN on the prior run before reusing
        // the instance) keeps the loop intact.
        const nextStage = this.currentStage + 1;
        const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
        const score = uiScene?.getScore() || 0;
        const sceneRef = this.scene;
        this.time.delayedCall(1500, () => {
          // Defer one tick out of the timer callback. v4's scene.start()
          // tears down the current scene mid-rAF if invoked synchronously
          // from a TimerEvent, which sometimes drops the rAF chain and
          // freezes the game loop on the next stage.
          requestAnimationFrame(() => {
            try {
              if (nextStage > 6) {
                console.log('🤖 AUTO-PLAY: All stages cleared! Starting boss...');
                sceneRef.stop(SCENE_KEYS.UI);
                sceneRef.start(SCENE_KEYS.BOSS, { score, autoPlay: true });
              } else {
                console.log(`🤖 AUTO-PLAY: Stage cleared → Stage ${nextStage}`);
                sceneRef.start(SCENE_KEYS.GAME, { stage: nextStage, score, autoPlay: true });
              }
            } catch (e) { console.error('AUTO-PLAY advance failed:', e); }
          });
        });
      } else {
        // Demo stage clear: bounce back to the menu for a 20s breather, then
        // MenuScene's shortened idle watch fires the next attract demo. This
        // gives the splash screen visible time between cycles, where the old
        // flow chained one demo straight into the next.
        this.cleanupDemoMode();
        const sceneRef = this.scene;
        this.time.delayedCall(2000, () => {
          requestAnimationFrame(() => {
            try {
              sceneRef.start(SCENE_KEYS.MENU, { fromDemo: true });
            } catch (e) {
              sceneRef.start(SCENE_KEYS.MENU);
            }
          });
        });
      }
      return;
    }

    // Get score and time from UI scene — captured now, before the delay below,
    // so the time bonus reflects the moment the stage was actually cleared.
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    const score = uiScene?.getScore() || 0;
    const timeRemaining = uiScene?.getTimeRemaining() || 0;

    // Hold the Stage Clear popup back by 0.5s. The brief beat lets the
    // celebration breathe and, crucially, keeps the player from accidentally
    // firing a CTA with leftover gameplay button-mashing the instant the stage
    // ends — the popup and its key handlers don't even exist yet during the
    // wait. GameScene.update() already early-returns while isGameOver, so the
    // scene sits frozen here; its clock keeps ticking, so this still fires.
    this.time.delayedCall(500, () => {
      // Pause game and UI scenes
      this.scene.pause();
      this.scene.pause(SCENE_KEYS.UI);

      // Launch Stage Clear scene with stage info + educational outro payload.
      // The outro block is shipped verbatim; StageClearScene templates {bags} +
      // {creature} tokens at render time.
      this.scene.launch(SCENE_KEYS.STAGE_CLEAR, {
        stage: this.currentStage,
        score: score,
        startingScore: this.startingScore,
        timeRemaining: timeRemaining,
        bagsCleared: this.bagsCleared,
        educationalOutro: this.stageConfig?.educational?.outro,
        creatureName: this.stageConfig?.educational?.ambientCreature?.name,
        background: this.stageConfig?.background,
      });
    });
  }

  /**
   * AAA: Play a one-shot reactive animation (flinch/victory/angry) then return to idle.
   * Silently no-ops if the animation or player sprite is missing.
   */
  private playReactiveAnim(animKey: string, holdMs = 600): void {
    if (!this.player) return;
    const sprite = this.player as Phaser.GameObjects.Sprite;
    if (typeof sprite.play !== 'function') return;
    if (!this.anims.exists(animKey)) return;

    try {
      sprite.play(animKey, true);
    } catch {
      return;
    }

    // Return to idle after hold
    this.time.delayedCall(holdMs, () => {
      if (this.isGameOver || !this.player) return;
      const s = this.player as Phaser.GameObjects.Sprite;
      if (typeof s.play === 'function' && this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) {
        try { s.play(ANIMATION_KEYS.PLAYER.IDLE, true); } catch {}
      }
      this.playerState = 'idle';
    });
  }

  private playerDeathCameraEffect(): void {
    const camera = this.cameras.main;
    const px = this.player?.x ?? GAME_CONFIG.WIDTH / 2;
    const py = this.player?.y ?? GAME_CONFIG.HEIGHT / 2;

    // Unified COSMIC death impact — red, dramatic
    const impact = ImpactSystem.get(this);
    impact.fire(px, py, 'HUGE', 0xFF2222);
    impact.screenFlash(0xFF0000, 400, true);

    // Death kill-cam: zoom in on player, then out
    impact.finalBlow(px, py, 1100);

    // Radial desaturation via post-fx (dramatic drain)
    try {
      if (camera.filters) {
        const wipe = camera.filters.internal.addVignette(px / camera.width, py / camera.height, 0.9, 0.6);
        this.tweens.add({
          targets: wipe,
          radius: 0.3,
          strength: 0.9,
          duration: 600,
          ease: 'Cubic.easeOut',
          onComplete: () => {
            this.time.delayedCall(400, () => {
              try { camera.filters?.internal.remove(wipe); } catch {}
            });
          },
        });
      }
    } catch {}

    console.log('💥 Death camera effect triggered!');
  }

  public gameOver(reason: string = 'Time over'): void {
    console.log(`💀 Game Over - ${reason}!`);
    this.isGameOver = true;

    // Stop all tweens to prevent callbacks from executing
    this.tweens.killAll();

    // Cancel any pending win check
    if (this.winCheckTimer) {
      this.winCheckTimer.remove();
      this.winCheckTimer = undefined;
    }

    // Get score from UI
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    const finalScore = uiScene?.getScore() || 0;

    // In auto-play mode: retry the same stage. The scene.start() call is
    // deferred out of the TimerEvent via rAF so the active tick finishes
    // before Phaser tears the scene down (otherwise the rAF chain drops).
    if (this.isAutoPlay) {
      console.log(`🤖 AUTO-PLAY: Game over on stage ${this.currentStage}, retrying in 1.5s...`);
      const stageToRetry = this.currentStage;
      const scoreToCarry = this.startingScore;
      const sceneRef = this.scene;
      this.time.delayedCall(1500, () => {
        requestAnimationFrame(() => {
          try {
            sceneRef.start(SCENE_KEYS.GAME, { stage: stageToRetry, score: scoreToCarry, autoPlay: true });
          } catch (e) {
            console.error('AUTO-PLAY restart failed:', e);
          }
        });
      });
      return;
    }

    // Stop UI scene
    this.scene.stop(SCENE_KEYS.UI);

    // In demo mode (not auto-play): bounce back to the menu so the attract
    // cycle pauses on the splash for 20s before the next demo stage kicks
    // off. MenuScene's create() already stops any lingering GAME/UI scenes,
    // so we just need to start it with the fromDemo flag.
    if (this.isDemoMode) {
      this.cleanupDemoMode();
      const game = this.game;
      setTimeout(() => {
        try {
          game.scene.stop(SCENE_KEYS.UI);
          setTimeout(() => {
            game.scene.stop(SCENE_KEYS.GAME);
            setTimeout(() => {
              game.scene.start(SCENE_KEYS.MENU, { fromDemo: true });
            }, 50);
          }, 50);
        } catch (e) {
          game.scene.start(SCENE_KEYS.MENU);
        }
      }, 1500);
      return;
    }

    // Hold the dying scene on screen for 3 seconds and fade it to black
    // before the game-over card appears. The gap (a) lets the player register
    // the loss, (b) gives them time to release the shoot button and the
    // stick so leftover joystick input does not land on a CTA the instant
    // the card pops up. The card itself fades in from black over 400ms via
    // GameOverScene.fadeIn, so the transition reads as one continuous beat.
    this.cameras.main.fadeOut(2500, 0, 0, 0);
    const stageReached = this.currentStage;
    this.time.delayedCall(3000, () => {
      this.scene.launch(SCENE_KEYS.GAME_OVER, { score: finalScore, reason, stage: stageReached });
      this.scene.pause();
    });
  }

  /**
   * Start invincibility period after being hit
   */
  private startInvincibility(): void {
    this.isInvincible = true;

    // Flash the player sprite
    if (this.player) {
      const flashTween = this.tweens.add({
        targets: this.player,
        alpha: { from: 0.3, to: 1 },
        duration: LIVES_CONFIG.FLASH_INTERVAL,
        yoyo: true,
        repeat: Math.floor(LIVES_CONFIG.INVINCIBILITY_DURATION / (LIVES_CONFIG.FLASH_INTERVAL * 2)),
      });

      // End invincibility after duration
      this.time.delayedCall(LIVES_CONFIG.INVINCIBILITY_DURATION, () => {
        this.isInvincible = false;
        if (this.player) {
          this.player.setAlpha(1);
        }
        flashTween.stop();
      });
    }

    // Reset player position to center
    if (this.player) {
      this.player.x = PLAYER_CONFIG.START_X;
    }
  }

  /**
   * Setup demo mode (AI player, labels, input listener)
   */
  private setupDemoMode(): void {
    this.aiPlayer = new AIPlayer();

    // Watermark label
    const labelText = this.isAutoPlay ? 'AUTO-PLAY' : 'DEMO';
    this.demoLabel = this.add.text(GAME_CONFIG.WIDTH - 30, GAME_CONFIG.HEIGHT - 30, labelText, {
      fontSize: '48px',
      fontFamily: 'CF Asty Std',
      color: this.isAutoPlay ? '#00FF88' : '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 4,
    });
    this.demoLabel.setOrigin(1, 1);
    this.demoLabel.setAlpha(0.6);
    this.demoLabel.setDepth(10000);

    // Auto-play: show stage info instead of "PRESS ANY KEY"
    if (this.isAutoPlay) {
      this.demoPrompt = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT - 60,
        `Stage ${this.currentStage} / 6`, {
        fontSize: '32px', fontFamily: 'CF Asty Std', color: '#00FF88',
        stroke: '#000000', strokeThickness: 4,
      });
      this.demoPrompt.setOrigin(0.5);
      this.demoPrompt.setDepth(10000);
      this.demoPrompt.setAlpha(0.5);
      // No exit on input for auto-play
    } else {
      // === STANDARD DEMO / ATTRACT MODE ===

      // "PRESS ANY KEY" flashing prompt
      this.demoPrompt = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT - 50, 'PRESS ANY KEY TO PLAY', {
        fontSize: '40px', fontFamily: 'CF Asty Std', color: '#FFD700',
        stroke: '#000000', strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 0, color: '#FFD700', blur: 15, fill: false },
      });
      this.demoPrompt.setOrigin(0.5);
      this.demoPrompt.setDepth(10001);
      this.tweens.add({
        targets: this.demoPrompt,
        alpha: { from: 1, to: 0.15 },
        duration: 700, yoyo: true, repeat: -1,
      });

      // Listen for ANY user input to exit demo
      const exitDemo = () => {
        if (this.isDemoMode && !this.isAutoPlay) {
          this.cleanupDemoMode();
          this.scene.stop(SCENE_KEYS.UI);
          this.scene.start(SCENE_KEYS.MENU);
        }
      };
      this.input.keyboard?.on('keydown', exitDemo);
      this.input.on('pointerdown', exitDemo);
      // Gamepad: any face button / D-pad / shoulder press also exits the
      // attract loop, so the prompt honours "PRESS ANY KEY" on a controller.
      this.input.gamepad?.on('down', exitDemo);

      // === AAA ARCADE LEADERBOARD OVERLAY ===
      this.buildArcadeLeaderboard();
    }
  }

  /**
   * AAA: Build the arcade-style scrolling leaderboard overlay for demo/attract mode
   */
  private async buildArcadeLeaderboard(): Promise<void> {
    const { WIDTH } = GAME_CONFIG;
    const panelX = WIDTH - 380;
    const panelW = 360;
    // Sit the panel below the top-right HUD cluster (BUBBLES pill + TIME bar +
    // the BAGS CLEARED pill, which bottoms out around y≈219) so the attract-mode
    // leaderboard never overlaps the live HUD readouts. panelH is derived from
    // the row count further down so the panel ends just under the last entry.
    const panelY = 240;

    // Fetch leaderboard data
    let entries: Array<{ player_name: string; best_score: number; highest_stage: number }> = [];
    try {
      entries = await getGlobalLeaderboard(10);
    } catch (e) {
      console.warn('Leaderboard fetch failed, using placeholder data');
    }

    // Fallback if no data
    if (entries.length === 0) {
      entries = [
        { player_name: 'POSEIDON', best_score: 25000, highest_stage: 6 },
        { player_name: 'ATHENA', best_score: 20000, highest_stage: 5 },
        { player_name: 'HERMES', best_score: 15000, highest_stage: 4 },
        { player_name: 'APOLLO', best_score: 12000, highest_stage: 3 },
        { player_name: 'ARES', best_score: 8000, highest_stage: 2 },
      ];
    }

    // Size the panel to its actual contents so there's no dead space between the
    // last row and the "NOW PLAYING" footer. Rows are 42px apart starting at +95;
    // the footer sits ~26px under the last row, with ~20px of bottom padding.
    const rowCount = Math.max(1, entries.length);
    const lastRowBottom = 95 + (rowCount - 1) * 42 + 28;
    const panelH = lastRowBottom + 46;

    // --- PANEL BACKGROUND ---
    const panelBg = this.add.rectangle(panelX + panelW / 2, panelY + panelH / 2, panelW, panelH, 0x000000, 0.65);
    panelBg.setDepth(10000);
    panelBg.setStrokeStyle(2, 0x00CCFF, 0.4);
    this.demoLeaderboardElements.push(panelBg);

    // Animated border glow
    this.tweens.add({
      targets: panelBg,
      strokeAlpha: { from: 0.3, to: 0.7 },
      duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // --- TITLE ---
    const title = this.add.text(panelX + panelW / 2, panelY + 25, 'HIGH SCORES', {
      fontSize: '36px', fontFamily: 'CF Asty Std', color: '#00EEFF',
      stroke: '#003344', strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 0, color: '#00CCFF', blur: 12, fill: false },
      letterSpacing: 4,
    });
    title.setOrigin(0.5).setDepth(10001);
    this.demoLeaderboardElements.push(title);

    // Title shimmer
    this.tweens.add({
      targets: title,
      alpha: { from: 0.8, to: 1 },
      scale: { from: 1.0, to: 1.03 },
      duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // --- HEADER --- name flush left, SCORE flush right to match the rows.
    const headerStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '16px', fontFamily: 'Arial', color: '#4488AA',
      stroke: '#001122', strokeThickness: 1,
    };
    const headerLeft = this.add.text(panelX + 15, panelY + 60, '#   NAME', headerStyle).setOrigin(0, 0);
    headerLeft.setDepth(10001);
    this.demoLeaderboardElements.push(headerLeft);
    const headerRight = this.add.text(panelX + panelW - 15, panelY + 60, 'SCORE', headerStyle).setOrigin(1, 0);
    headerRight.setDepth(10001);
    this.demoLeaderboardElements.push(headerRight);

    // Separator line
    const sep = this.add.rectangle(panelX + panelW / 2, panelY + 78, panelW - 30, 1, 0x00CCFF, 0.3);
    sep.setDepth(10001);
    this.demoLeaderboardElements.push(sep);

    // --- CYCLING ENTRIES ---
    const startCycle = () => {
      // Clear old entry texts
      this.demoLeaderboardElements
        .filter((el: any) => el.getData?.('isEntry'))
        .forEach((el: any) => el.destroy());
      this.demoLeaderboardElements = this.demoLeaderboardElements
        .filter((el: any) => !el.getData?.('isEntry'));

      const medals = ['🥇', '🥈', '🥉'];
      const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

      entries.forEach((entry, i) => {
        const ey = panelY + 95 + i * 42;
        const rank = i + 1;
        const medal = i < 3 ? medals[i] : `${rank}.`;
        const name = (entry.player_name || 'ANON').substring(0, 12).toUpperCase();
        const score = entry.best_score.toLocaleString();
        const color = i < 3 ? rankColors[i] : '#AADDEE';
        const rowStyle: Phaser.Types.GameObjects.Text.TextStyle = {
          fontSize: '20px', fontFamily: 'CF Asty Std', color: color,
          stroke: '#001122', strokeThickness: 2,
          shadow: i < 3 ? { offsetX: 0, offsetY: 0, color: color, blur: 6, fill: false } : undefined,
        };

        // Each row is a container: rank + name flush to the left column,
        // score flush to the right edge. Separate text objects (instead of
        // padEnd on one string) keep the columns aligned with the
        // proportional CF Asty font, and the container lets the whole row
        // slide in as one unit.
        const row = this.add.container(panelX, ey);
        const medalText = this.add.text(12, 0, medal, rowStyle).setOrigin(0, 0);
        const nameText = this.add.text(52, 0, name, rowStyle).setOrigin(0, 0);
        const scoreText = this.add.text(panelW - 15, 0, score, rowStyle).setOrigin(1, 0);
        row.add([medalText, nameText, scoreText]);
        row.setDepth(10001);
        row.setAlpha(0);
        row.setData('isEntry', true);
        this.demoLeaderboardElements.push(row);

        // Staggered slide-in from the right edge of the panel.
        this.tweens.add({
          targets: row,
          alpha: 1,
          x: { from: panelX + panelW + 20, to: panelX },
          duration: 400,
          delay: i * 180,
          ease: 'Power2',
        });
      });

      // After all entries shown + 6s hold, fade out and restart cycle
      const totalAnimTime = entries.length * 180 + 400;
      this.time.delayedCall(totalAnimTime + 6000, () => {
        // Fade out all entries
        const entryEls = this.demoLeaderboardElements.filter((el: any) => el.getData?.('isEntry'));
        entryEls.forEach((el: any, i: number) => {
          this.tweens.add({
            targets: el,
            alpha: 0,
            x: (el as any).x - 30,
            duration: 300,
            delay: i * 80,
          });
        });
        // Restart cycle after fade out
        this.time.delayedCall(entryEls.length * 80 + 500, () => {
          if (this.isDemoMode && !this.isAutoPlay) startCycle();
        });
      });
    };

    // Start the cycle after a 1s delay
    this.time.delayedCall(1000, startCycle);

    // --- "NOW PLAYING" stage indicator ---
    const stageName = this.stageConfig?.stageName || `Stage ${this.currentStage}`;
    const nowPlaying = this.add.text(panelX + panelW / 2, panelY + panelH - 20,
      `NOW PLAYING: ${stageName}`, {
      fontSize: '18px', fontFamily: 'CF Asty Std', color: '#66AACC',
      stroke: '#001122', strokeThickness: 2,
    });
    nowPlaying.setOrigin(0.5).setDepth(10001).setAlpha(0.6);
    this.demoLeaderboardElements.push(nowPlaying);
  }

  /**
   * Cleanup demo mode resources
   */
  private cleanupDemoMode(): void {
    DemoController.getInstance().endDemo();
    this.aiPlayer = undefined;
    if (this.demoLabel) { this.demoLabel.destroy(); this.demoLabel = undefined; }
    if (this.demoPrompt) { this.demoPrompt.destroy(); this.demoPrompt = undefined; }
    // Destroy leaderboard overlay
    this.demoLeaderboardElements.forEach(el => { try { el.destroy(); } catch {} });
    this.demoLeaderboardElements = [];
  }

  /**
   * Celebration particle burst when stage is cleared
   */
  private celebrateStageComplete(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const colors = [0xFFD700, 0xFF6600, 0x00CED1, 0xFF2222, 0x32CD32, 0xAA66FF, 0xFF69B4, 0x00FF88];

    // === AAA: CONFETTI BURST from multiple positions (staggered) ===
    const positions = [
      { x: WIDTH * 0.15, y: HEIGHT * 0.4, delay: 0 },
      { x: WIDTH * 0.35, y: HEIGHT * 0.25, delay: 100 },
      { x: WIDTH * 0.5, y: HEIGHT * 0.15, delay: 200 },
      { x: WIDTH * 0.65, y: HEIGHT * 0.25, delay: 300 },
      { x: WIDTH * 0.85, y: HEIGHT * 0.4, delay: 400 },
    ];

    for (const pos of positions) {
      this.time.delayedCall(pos.delay, () => {
        if (this.particleEmitter) {
          const color = colors[Math.floor(Math.random() * colors.length)];
          this.particleEmitter.setParticleTint(color);
          this.particleEmitter.emitParticleAt(pos.x, pos.y, 25);
        }
        // Confetti rectangles flying outward
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI * 2 / 8) * i + Math.random() * 0.5;
          const color = colors[Math.floor(Math.random() * colors.length)];
          const confetti = this.add.rectangle(pos.x, pos.y, 8 + Math.random() * 6, 4 + Math.random() * 3, color, 1);
          confetti.setDepth(1500);
          confetti.setAngle(Math.random() * 360);
          this.tweens.add({
            targets: confetti,
            x: pos.x + Math.cos(angle) * (200 + Math.random() * 200),
            y: pos.y + Math.sin(angle) * (150 + Math.random() * 150) + 200, // Gravity arc
            angle: confetti.angle + (Math.random() - 0.5) * 720,
            alpha: 0,
            duration: 1200 + Math.random() * 600,
            ease: 'Power1',
            onComplete: () => confetti.destroy(),
          });
        }
      });
    }

    // === AAA: "STAGE CLEAR!" mega text ===
    const clearText = this.add.text(WIDTH / 2, HEIGHT / 2, 'STAGE CLEAR!', {
      fontFamily: 'CF Asty Std',
      fontSize: '140px',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 10,
      shadow: { offsetX: 0, offsetY: 0, color: '#FFD700', blur: 30, fill: false },
    });
    clearText.setOrigin(0.5);
    clearText.setDepth(1500);
    clearText.setAlpha(0);
    clearText.setScale(0.2);

    this.tweens.add({
      targets: clearText,
      alpha: 1,
      scale: 1.0,
      duration: 400,
      ease: 'Back.easeOut',
      easeParams: [2],
    });

    this.time.delayedCall(1200, () => {
      this.tweens.add({
        targets: clearText,
        alpha: 0,
        scale: 1.3,
        duration: 400,
        ease: 'Power2',
        onComplete: () => clearText.destroy(),
      });
    });

    // Unified COSMIC stage-clear impact (gold) + victory voice + music swell + victory pose
    const impact = ImpactSystem.get(this);
    impact.fire(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, 'COSMIC', 0xFFD700);
    const choreo = AudioChoreographer.get(this);
    choreo.playVoice('victory');
    choreo.musicSwell(1.2, 500);
    this.playReactiveAnim(ANIMATION_KEYS.PLAYER.VICTORY, 2000);
  }

  /**
   * Create an elliptical ground shadow beneath the player
   */
  private createPlayerGroundShadow(): void {
    if (!this.player) return;

    this.playerShadow = this.add.ellipse(
      this.player.x,
      PHYSICS_CONFIG.GROUND_Y - 5,
      80,
      32,
      0x000000,
      0.22,
    );
    this.playerShadow.setDepth(this.player.depth - 2);
  }

  /**
   * Setup touch controls for mobile devices
   */
  private setupTouchControls(): void {
    // Only show on touch-capable devices
    this.isTouchDevice = this.sys.game.device.input.touch;
    if (!this.isTouchDevice) return;

    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // Left zone (left 1/3 of screen, bottom half)
    const leftZone = this.add.rectangle(0, HEIGHT / 2, WIDTH / 3, HEIGHT / 2, 0x000000, 0);
    leftZone.setOrigin(0, 0);
    leftZone.setInteractive();
    leftZone.setDepth(9999);
    leftZone.on('pointerdown', () => { this.touchLeft = true; });
    leftZone.on('pointerup', () => { this.touchLeft = false; });
    leftZone.on('pointerout', () => { this.touchLeft = false; });

    // Right zone (right 1/3 of screen, bottom half)
    const rightZone = this.add.rectangle(WIDTH * 2 / 3, HEIGHT / 2, WIDTH / 3, HEIGHT / 2, 0x000000, 0);
    rightZone.setOrigin(0, 0);
    rightZone.setInteractive();
    rightZone.setDepth(9999);
    rightZone.on('pointerdown', () => { this.touchRight = true; });
    rightZone.on('pointerup', () => { this.touchRight = false; });
    rightZone.on('pointerout', () => { this.touchRight = false; });

    // Shoot zone (middle 1/3, top half)
    const shootZone = this.add.rectangle(WIDTH / 3, 0, WIDTH / 3, HEIGHT / 2, 0x000000, 0);
    shootZone.setOrigin(0, 0);
    shootZone.setInteractive();
    shootZone.setDepth(9999);
    shootZone.on('pointerdown', () => { this.touchShoot = true; });

    // Visual hints (subtle arrows)
    const arrowStyle = { fontSize: '48px', color: '#FFFFFF' };
    const leftHint = this.add.text(WIDTH / 6, HEIGHT - 40, '<', arrowStyle);
    leftHint.setOrigin(0.5);
    leftHint.setAlpha(0.15);
    leftHint.setDepth(9998);

    const rightHint = this.add.text(WIDTH * 5 / 6, HEIGHT - 40, '>', arrowStyle);
    rightHint.setOrigin(0.5);
    rightHint.setAlpha(0.15);
    rightHint.setDepth(9998);
  }

  private setupDebugGraphics(): void {
    // Create debug graphics layer (checkbox moved to menu)
    this.debugGraphics = this.add.graphics();
    this.debugGraphics.setDepth(1000); // Draw on top of everything
  }

  private updateDebugInfo(): void {
    if (!debugState.getDebugMode() || !this.debugGraphics) return;

    // Clear previous debug graphics
    this.debugGraphics.clear();
    this.clearDebugTexts();

    // Draw ball debug info
    this.balls.forEach((ball, index) => {
      const radius = ball.getData('radius');
      const velocityX = ball.getData('velocityX');
      const velocityY = ball.getData('velocityY');

      // Draw hitbox circle
      this.debugGraphics!.strokeCircle(ball.x, ball.y, radius);
      this.debugGraphics!.strokeCircle(ball.x, ball.y, 2); // Center point

      // Draw velocity vector
      const vectorScale = 0.1;
      const endX = ball.x + velocityX * vectorScale;
      const endY = ball.y + velocityY * vectorScale;
      
      this.debugGraphics!.lineStyle(2, 0xFFFF00); // Yellow for velocity
      this.debugGraphics!.lineBetween(ball.x, ball.y, endX, endY);
      
      // Draw arrow head
      const angle = Math.atan2(velocityY, velocityX);
      const arrowLength = 8;
      const arrowAngle = Math.PI / 6;
      
      this.debugGraphics!.lineBetween(
        endX, endY,
        endX - arrowLength * Math.cos(angle - arrowAngle),
        endY - arrowLength * Math.sin(angle - arrowAngle)
      );
      this.debugGraphics!.lineBetween(
        endX, endY,
        endX - arrowLength * Math.cos(angle + arrowAngle),
        endY - arrowLength * Math.sin(angle + arrowAngle)
      );

      // Create debug text
      const text = this.add.text(
        ball.x + radius + DEBUG_CONFIG.TEXT_OFFSET,
        ball.y - radius,
        `Ball ${index + 1}\nVel: ${velocityX.toFixed(0)}, ${velocityY.toFixed(0)}\nPos: ${ball.x.toFixed(0)}, ${ball.y.toFixed(0)}`,
        {
          fontSize: `${DEBUG_CONFIG.TEXT_SIZE}px`,
          fontFamily: 'Arial',
          color: DEBUG_CONFIG.TEXT_COLOR,
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: { x: 4, y: 2 }
        }
      );
      text.setOrigin(0, 1);
      text.setDepth(1001);
      this.debugTexts.push(text);
    });

    // Draw player debug info
    if (this.player) {
      const playerRadius = (PLAYER_CONFIG.WIDTH * PLAYER_CONFIG.SCALE * 0.5) / 2;
      
      // Draw player hitbox
      this.debugGraphics!.strokeCircle(this.player.x, this.player.y, playerRadius);
      this.debugGraphics!.strokeCircle(this.player.x, this.player.y, 2);

      // Player debug text
      const playerText = this.add.text(
        this.player.x + playerRadius + DEBUG_CONFIG.TEXT_OFFSET,
        this.player.y - playerRadius,
        `Player\nPos: ${this.player.x.toFixed(0)}, ${this.player.y.toFixed(0)}`,
        {
          fontSize: `${DEBUG_CONFIG.TEXT_SIZE}px`,
          fontFamily: 'Arial',
          color: DEBUG_CONFIG.TEXT_COLOR,
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: { x: 4, y: 2 }
        }
      );
      playerText.setOrigin(0, 1);
      playerText.setDepth(1001);
      this.debugTexts.push(playerText);
    }

    // Draw projectile debug info
    if (this.projectile && this.isProjectileActive) {
      // Draw projectile hitbox
      this.debugGraphics!.strokeRect(
        this.projectile.x - 8,
        this.projectile.y - 32,
        16, 64
      );
      this.debugGraphics!.fillCircle(this.projectile.x, this.projectile.y, 2);

      // Projectile debug text
      const projectileText = this.add.text(
        this.projectile.x + 10,
        this.projectile.y - 32,
        `Projectile\nPos: ${this.projectile.x.toFixed(0)}, ${this.projectile.y.toFixed(0)}`,
        {
          fontSize: `${DEBUG_CONFIG.TEXT_SIZE}px`,
          fontFamily: 'Arial',
          color: DEBUG_CONFIG.TEXT_COLOR,
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: { x: 4, y: 2 }
        }
      );
      projectileText.setOrigin(0, 1);
      projectileText.setDepth(1001);
      this.debugTexts.push(projectileText);
    }

    // Draw powerup debug info
    this.droppedPowerups.forEach((powerup, index) => {
      const powerupType = powerup.getData('powerupType');
      const config = getPowerupConfig(powerupType);
      
      // Draw collection radius circle
      this.debugGraphics!.lineStyle(2, 0x00FF00); // Green for powerup
      this.debugGraphics!.strokeCircle(powerup.x, powerup.y, config.collectRadius);
      this.debugGraphics!.fillCircle(powerup.x, powerup.y, 3); // Center point
      
      // Calculate distance to player if player exists
      let distanceText = '';
      if (this.player) {
        const distance = Phaser.Math.Distance.Between(
          this.player.x,
          this.player.y,
          powerup.x,
          powerup.y
        );
        distanceText = `\nDist: ${distance.toFixed(0)} (need <${config.collectRadius})`;
      }
      
      // Powerup debug text
      const powerupText = this.add.text(
        powerup.x + config.collectRadius + DEBUG_CONFIG.TEXT_OFFSET,
        powerup.y,
        `Powerup ${index + 1}\nType: ${powerupType}\nPos: ${powerup.x.toFixed(0)}, ${powerup.y.toFixed(0)}${distanceText}`,
        {
          fontSize: `${DEBUG_CONFIG.TEXT_SIZE}px`,
          fontFamily: 'Arial',
          color: '#00FF00',
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: { x: 4, y: 2 }
        }
      );
      powerupText.setOrigin(0, 0.5);
      powerupText.setDepth(1001);
      this.debugTexts.push(powerupText);
    });
  }

  private clearDebugTexts(): void {
    this.debugTexts.forEach(text => text.destroy());
    this.debugTexts = [];
  }

  private handleShutdown(): void {
    this.isSceneReady = false;
    // If we shut down while still paused (e.g. quitting to menu from the pause
    // menu), balance the watchdog modal count the resume path would have popped.
    if (this.isPaused) {
      KioskWatchdog.instance.popModal();
      this.isPaused = false;
    }
    // Do NOT increment createRunToken here — shutdown fires BEFORE Phaser's
    // next create() for this scene runs, so incrementing would invalidate the
    // fresh create's runToken against its own load (empty stage 2 bug).
    // create() alone manages the token; the stale-load guard already catches
    // any in-flight load from a previous run.
    this.events.off('show-score-text', this.showFloatingScoreText, this);
    this.events.off('timer-urgency', this.onTimerUrgency);
    // Tear down storm-sky overlay if this stage had one (World 3 only).
    this.stormSky?.destroy();
    this.stormSky = undefined;
    // Explicitly tear down the background videos before Phaser's display-list
    // destroy nukes them. destroyBackground() calls stop() on each underlying
    // HTMLVideoElement, which the browser releases more deterministically
    // than waiting for the implicit Video.destroy() path. Important under
    // demo-mode cycling, where two new videos are spun up on every stage
    // restart and the next createVideoBackground would otherwise sometimes
    // get a play() that never actually starts.
    this.backgroundManager?.destroyBackground();
    this.backgroundManager = undefined;
  }
}
